import React, { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  Snackbar, CircularProgress, Avatar, Divider, TablePagination,
  Card, CardContent, Grid, List, ListItemButton, ListItemIcon,
  ListItemText, AppBar, Toolbar, Tooltip, Checkbox, Badge,
  Tabs, Tab, Drawer, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Business, Add, Visibility, Menu as MenuIcon,
  Logout, Assignment, CheckCircle, Edit, PictureAsPdf,
  Group as GroupIcon, Star, Description, Print, Download,
  Delete, School, People, CloudUpload,
  HelpOutline, Close, TrendingUp, Checkroom, DrawOutlined, MyLocation,
  Place, GpsFixed, GpsNotFixed, CalendarMonth, Search, ExpandMore,
  AutoAwesome, AccountBalance, EventAvailable,
} from '@mui/icons-material';
import axios from 'axios';
import {
  API_ENDPOINTS, WORK_ORDER_SIGN_URL, WORK_ORDER_PDF_URL, MENTOR_REPORTS,
  PARTICIPANT_TRAINING_REPORTS, PARTICIPANT_TRAINING_REPORT_PDF_URL,
  TRAINING_REPORT_PDF_URL,
  TSHIRT_ENTRY_SIGN_URL, TSHIRT_RECEIPT_PDF_URL,
  WORK_ORDER_SUBMISSION_TIMESHEET_URL, WORK_ORDER_SUBMISSION_INVOICE_URL,
  WORK_ORDER_ATTACHMENT_DOWNLOAD_URL, WORK_ORDER_CONFIRM_PAYMENT_URL,
  REPORT_CONFIRM_PAYMENT_URL, GROUP_REPORT_CONFIRM_PAYMENT_URL,
  TBIP_PDF_URL, TBIP_EXCEL_URL, FIAP_PDF_URL,
} from '../config';
import { BRAND } from '../theme';
import { subscribePush } from '../index';
import VisitReportForm from './VisitReportForm';
import MSMEMap from './MSMEMap';
import CalendarPlanner from './CalendarPlanner';
import MSMEVisitSchedule from './MSMEVisitSchedule';
import EnterpriseImprovementPlanDialog from './EnterpriseImprovementPlanDialog';
import BookTBIPMeetingDialog from './BookTBIPMeetingDialog';
import FinancialImprovementPlanDialog from './FinancialImprovementPlanDialog';
import BookFIAPMeetingDialog from './BookFIAPMeetingDialog';


import { getErrorMessage } from '../utils/error';

const DRAWER_WIDTH = 220;
const ROWS_PER_PAGE = 15;

const VISIT_TYPE_LABELS = {
  initial: 'Initial Assessment',
  followup: 'Follow-up Visit',
  final: 'Final Assessment',
  training: 'Training Support',
  mentoring: 'Mentoring Session',
};

const STATUS_COLORS = {
  draft: 'default',
  submitted: 'primary',
  reviewed: 'success',
};

const MSME_STATUS_CONFIG = {
  active:             { label: 'Active',             color: 'success', bgcolor: '#E8F5E9', textColor: '#2E7D32', border: '#A5D6A7' },
  temporarily_closed: { label: 'Temporarily Closed', color: 'warning', bgcolor: '#FFF3E0', textColor: '#E65100', border: '#FFE082' },
  out_of_business:    { label: 'Out of Business',    color: 'error',   bgcolor: '#FFEBEE', textColor: '#C62828', border: '#EF9A9A' },
  unavailable:        { label: 'Unavailable',        color: 'default', bgcolor: '#F3E5F5', textColor: '#6A1B9A', border: '#CE93D8' },
};

const getMsmeStatusInfo = (status) => MSME_STATUS_CONFIG[status] || {
  label: (status || 'Active').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  color: 'default',
  bgcolor: '#F5F5F5',
  textColor: '#616161',
  border: '#E0E0E0',
};

const NORTHERN_UGANDA_DISTRICTS = [
  'Gulu', 'Lira', 'Arua', 'Kitgum', 'Dokolo', 'Oyam', 'Apac', 'Nebbi',
  'Koboko', 'Nwoya', 'Pader', 'Yumbe', 'Adjumani', 'Moyo', 'Moroto',
  'Agago', 'Amolatar', 'Amuru', 'Alebtong', 'Kole', 'Otuke', 'Maracha',
  'Madi-Okollo', 'Terego', 'Zombo', 'Pakwach', 'Lamwo', 'Obongi',
  'Abim', 'Kotido', 'Kaabong', 'Karenga', 'Nakapiripirit', 'Nabilatuk', 'Amudat',
];

const EMPTY_GROUP_REPORT = {
  group: '',
  session_number: '',
  visit_date: new Date().toISOString().slice(0, 10),
  msmes_supported: [],
  attendees: [],
  session_overview: '',
  challenges_identified: '',
  interventions_delivered: '',
  outcomes_achieved: '',
  next_steps: '',
  additional_notes: '',
  status: 'draft',
};

/* ── Group report draft persistence helpers ─────────────────────────────── */
function grpDraftKey(editingGroupReport, groupId) {
  if (editingGroupReport) return `grp_report_draft_edit_${editingGroupReport.id}`;
  return `grp_report_draft_new_${groupId || 'unknown'}`;
}
function readGrpDraft(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}
function writeGrpDraft(key, form) {
  try { localStorage.setItem(key, JSON.stringify({ form, savedAt: Date.now() })); }
  catch { /* storage full */ }
}
function clearGrpDraft(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

const EMPTY_CONTRIBUTION = {
  group_report: '',
  msmes_observed: [],
  notes: '',
  challenges_observed: '',
  interventions_made: '',
  follow_up_needed: '',
};

const STANDARD_CONDITIONS = [
  'The BGE must use only the standardised PRUDEV II tools and templates and submit all reports in the provided formats.',
  'The BGE is fully accountable for the accuracy, completeness, and timely submission of all attendance sheets and field reports.',
  'Under no circumstances may the BGE retain, store, or record MSME CRM login credentials. All account access details must be handed directly and exclusively to the MSME owner.',
  'All MSME data and business information must be handled strictly confidentially.',
  'GOPA Pro retains ownership of all outputs produced under this Work Order.',
  'A 6% withholding tax will be applied in accordance with Ugandan tax laws.',
  'BGE must use their official BGE code in all documentation.',
  'Timely submission of outputs is required to avoid payment delays.',
  'GOPA Pro reserves the right to terminate this Work Order in cases of non-performance, data mishandling, or breach of any condition above.',
];


export default function BGEDashboard({ token, currentUser, onLogout }) {
  const headers = { Authorization: `Bearer ${token}` };
  const bgeName = currentUser?.bge_profile?.name || currentUser?.username || 'BGE';

  const [section, setSection] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('section');
    return ['msmes', 'groups', 'reports', 'workorders', 'training', 'tshirts'].includes(requested) ? requested : 'msmes';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const [msmes, setMsmes] = useState([]);
  const [reports, setReports] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupReports, setGroupReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // group-report dialog
  const [groupReportDialog, setGroupReportDialog] = useState(false);
  const [editingGroupReport, setEditingGroupReport] = useState(null);
  const [groupReportForm, setGroupReportForm] = useState(EMPTY_GROUP_REPORT);
  const [groupReportSaving, setGroupReportSaving] = useState(false);
  const [groupReportErrors, setGroupReportErrors] = useState('');
  const [grpDraftBanner, setGrpDraftBanner]       = useState(null); // { savedAt }
  const grpDraftTimerRef    = useRef(null);
  const currentGrpDraftKey  = useRef('');

  // Member contribution dialog (non-leader BGEs feeding info into a group report)
  const [contributionDialog, setContributionDialog] = useState(false);
  const [contributionForm, setContributionForm] = useState(EMPTY_CONTRIBUTION);
  const [contributionSaving, setContributionSaving] = useState(false);

  // Payment receipt confirmation dialog for BGEs
  const [bgePaymentConfirmDialog, setBgePaymentConfirmDialog] = useState(false);
  const [bgePaymentConfirmTarget, setBgePaymentConfirmTarget] = useState(null);
  const [bgePaymentConfirmType, setBgePaymentConfirmType] = useState('work_order'); // 'work_order' | 'msme_report' | 'group_report'
  const [bgePaymentConfirmForm, setBgePaymentConfirmForm] = useState({
    reference: '',
    notes: '',
    payment_method: 'Mobile Money',
  });
  const [bgePaymentConfirming, setBgePaymentConfirming] = useState(false);
  const [contributionErrors, setContributionErrors] = useState('');
  const [contributionEditingId, setContributionEditingId] = useState(null);

  // Contributions loaded inside the team-lead group report dialog
  const [reportContributions, setReportContributions] = useState([]);

  // Per-person MSME attendance for the group report dialog
  const [grpAttendees, setGrpAttendees] = useState([]);   // [{_key, id?, msme, attendee_name, ...}]
  const _grpAttKey = useRef(0);
  const newGrpRow = () => ({
    _key: ++_grpAttKey.current,
    id: null, msme: '', attendee_name: '', attendee_phone: '',
    gender: '', age_group: '', refugee_status: 'H', consent_photo: true, consent_contact: true,
  });

  const [helpDialog, setHelpDialog] = useState(false);
  const [helpSection, setHelpSection] = useState(0);

  // Training tab — assigned sessions
  const [leadSessions, setLeadSessions] = useState([]);
  const [mentorSessions, setMentorSessions] = useState([]);
  const [participantSessions, setParticipantSessions] = useState([]);

  // Training report dialog (lead sessions)
  const [trDialog, setTrDialog] = useState(false);
  const [trSession, setTrSession] = useState(null);
  const [trData, setTrData] = useState(null);
  const [trSaving, setTrSaving] = useState(false);
  const [trForm, setTrForm] = useState({});
  const EMPTY_TR = {
    training_title: '', training_dates: '', venue: '', district: '',
    facilitation_team: '', training_purpose: '', session_objectives: '',
    activities_delivered: '', key_lessons: '', growth_support_areas: '',
    key_findings: '', bge_contributions: '', bds_actions: '',
    recommendations: '', next_steps: '', conclusion: '', status: 'draft',
  };

  // Peer-to-Peer session creation dialog
  const [p2pSessionDialog, setP2pSessionDialog] = useState(false);
  const [p2pSessionSaving, setP2pSessionSaving] = useState(false);
  const [allExperts, setAllExperts] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [p2pSearchMsme, setP2pSearchMsme] = useState('');
  const [p2pForm, setP2pForm] = useState({
    title: 'Peer-to-Peer Learning Session',
    date: new Date().toISOString().slice(0, 10),
    end_date: '',
    location: '',
    district: '',
    topic: '',
    lead_bge_id: '',
    mentor_bge_ids: [],
    msme_ids: [],
    description: '',
  });

  // Attendance dialog (lead — editable)
  const [attDialog, setAttDialog] = useState(false);
  const [attSession, setAttSession] = useState(null);
  const [attRows, setAttRows] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const _attKey = useRef(0);
  const newAttRow = () => ({
    _key: ++_attKey.current,
    id: null, msme: '', attendee_name: '', attendee_phone: '',
    gender: '', age_group: '', refugee_status: 'H', consent_photo: true, consent_contact: true,
  });

  // Full MSME list for attendance picker (bypasses personal assignment scope)
  const [trainingMsmes, setTrainingMsmes] = useState([]);
  const fetchTrainingMsmes = useCallback(async () => {
    if (trainingMsmes.length > 0) return;
    try {
      const res = await axios.get(`${API_ENDPOINTS.MSMES}?training=1`, { headers: { Authorization: `Bearer ${token}` } });
      setTrainingMsmes(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { /* non-critical */ }
  }, [token, trainingMsmes.length]);

  // Mentor report dialog
  const [mrDialog, setMrDialog] = useState(false);
  const [mrSession, setMrSession] = useState(null);
  const [mrData, setMrData] = useState(null);
  const [mrSaving, setMrSaving] = useState(false);
  const [mrForm, setMrForm] = useState({});
  const EMPTY_MR = {
    training_title: '', training_dates: '', venue: '',
    mentoring_activities: '', msmes_mentored: '',
    key_observations: '', challenges: '', recommendations: '', next_steps: '',
    status: 'draft',
  };

  // Participant training report
  const [ptReports, setPtReports] = useState([]);
  const [ptDialog, setPtDialog] = useState(false);
  const [ptEdit, setPtEdit] = useState(null);
  const [ptSaving, setPtSaving] = useState(false);
  const [trainingTab, setTrainingTab] = useState(0);
  const EMPTY_PT = {
    training_title: '', training_dates: '', venue: '', district: '', facilitator_name: '',
    topics_covered: '', key_learnings: '', practical_application: '',
    msmes_as_test_subjects: '', challenges: '', action_plan: '',
    attendees: [], status: 'draft',
  };
  const [ptForm, setPtForm] = useState({ ...EMPTY_PT });
  const [ptAttendeeRow, setPtAttendeeRow] = useState({ name: '', bge_code: '', phone: '', gender: '', organisation: '' });

  // Work orders
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrderPreview, setWorkOrderPreview] = useState(null);
  const [woReview, setWoReview] = useState(null);       // WO being reviewed in dialog
  const [woSigning, setWoSigning] = useState(null);     // id of WO being signed
  const [woPdfBlob, setWoPdfBlob] = useState(null);     // blob URL for PDF preview

  // Timesheet & invoice submissions
  const [submissions, setSubmissions] = useState([]);
  const [timesheetFile, setTimesheetFile] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [submissionUploading, setSubmissionUploading] = useState(false);
  const timesheetInputRef = useRef(null);
  const invoiceInputRef = useRef(null);
  // inline upload per work order
  const [activeUploadWoId, setActiveUploadWoId] = useState(null);
  // inline replace state
  const [replacingSubId, setReplacingSubId] = useState(null);
  const [replaceTimesheetFile, setReplaceTimesheetFile] = useState(null);
  const [replaceInvoiceFile, setReplaceInvoiceFile] = useState(null);
  const [replaceUploading, setReplaceUploading] = useState(false);
  const replaceTimesheetRef = useRef(null);
  const replaceInvoiceRef = useRef(null);

  // Supporting document attachments
  const [attachments, setAttachments] = useState([]);
  const [activeAttachWoId, setActiveAttachWoId] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [attachCaption, setAttachCaption] = useState('');
  const [attachUploading, setAttachUploading] = useState(false);
  const attachInputRef = useRef(null);

  // Payments against work orders
  const [payments, setPayments] = useState([]);
  const [activePaymentWoId, setActivePaymentWoId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_date: '', amount: '', balance: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Signature upload
  const [sigFile, setSigFile] = useState(null);
  const [sigUploading, setSigUploading] = useState(false);
  const [sigUrl, setSigUrl] = useState(currentUser?.bge_profile?.signature_url || null);
  const sigInputRef = useRef(null);

  const [myBgeProfile, setMyBgeProfile] = useState(currentUser?.bge_profile || null);
  const [bgeLocationDialog, setBgeLocationDialog] = useState(false);
  const [bgeLocationForm, setBgeLocationForm] = useState({
    location: currentUser?.bge_profile?.location || '',
    latitude: currentUser?.bge_profile?.latitude || '',
    longitude: currentUser?.bge_profile?.longitude || '',
    loading: false,
    saving: false,
  });

  const myBgeId = currentUser?.bge_profile?.id;
  const myBgeCode = currentUser?.bge_profile?.bge_code || '';

  // pagination
  const [reportPage, setReportPage] = useState(0);

  // VisitReportForm (new full-screen form)
  const [visitReportOpen, setVisitReportOpen]   = useState(false);
  const [visitReportMsme, setVisitReportMsme]   = useState(null);
  const [visitReportType, setVisitReportType]   = useState('data_update');
  const [visitReportEdit, setVisitReportEdit]   = useState(null);

  // MSME detail dialog
  const [msmeDetailDialog, setMsmeDetailDialog] = useState(false);
  const [selectedMsme, setSelectedMsme] = useState(null);
  const [msmeReports, setMsmeReports] = useState([]);
  const [msmeDetailTab, setMsmeDetailTab] = useState(0);
  const [msmeDetailSnapshots, setMsmeDetailSnapshots] = useState([]);

  // Enterprise Improvement Plans (TBIP)
  const [tbipPlans, setTbipPlans] = useState([]);
  const [tbipDialog, setTbipDialog] = useState(false);
  const [selectedTbip, setSelectedTbip] = useState(null);
  const [selectedTbipMsme, setSelectedTbipMsme] = useState(null);
  const [tbipFilterStatus, setTbipFilterStatus] = useState('all');
  const [tbipFilterPriority, setTbipFilterPriority] = useState('all');
  const [tbipSearch, setTbipSearch] = useState('');
  const [tbipPage, setTbipPage] = useState(0);
  const [newTbipMsmePicker, setNewTbipMsmePicker] = useState(false);
  const [tbipBookMeetingOpen, setTbipBookMeetingOpen] = useState(false);
  const [tbipBookMeetingMsme, setTbipBookMeetingMsme] = useState(null);
  const [tbipBookMeetingPlan, setTbipBookMeetingPlan] = useState(null);

  // Financial Improvement Action Plans (FIAP / Access to Finance)
  const [fiapPlans, setFiapPlans] = useState([]);
  const [fiapDialog, setFiapDialog] = useState(false);
  const [selectedFiap, setSelectedFiap] = useState(null);
  const [selectedFiapMsme, setSelectedFiapMsme] = useState(null);
  const [fiapFilterCategory, setFiapFilterCategory] = useState('all');
  const [fiapFilterPriority, setFiapFilterPriority] = useState('all');
  const [fiapFilterStatus, setFiapFilterStatus] = useState('all');
  const [fiapSearch, setFiapSearch] = useState('');
  const [fiapPage, setFiapPage] = useState(0);
  const [newFiapMsmePicker, setNewFiapMsmePicker] = useState(false);
  const [fiapBookMeetingOpen, setFiapBookMeetingOpen] = useState(false);
  const [fiapBookMeetingPlan, setFiapBookMeetingPlan] = useState(null);
  const [fiapBookMeetingMsme, setFiapBookMeetingMsme] = useState(null);



  // Full MSME list for training attendance (bypasses personal assignment scope)

  // Growth update form
  const [growthDialog, setGrowthDialog] = useState(false);
  const [growthMsme, setGrowthMsme] = useState(null);
  const [growthSnapshots, setGrowthSnapshots] = useState([]);
  const [growthSaving, setGrowthSaving] = useState(false);
  const [growthError, setGrowthError] = useState('');

  // ── Quick Visit Update (GPS + revenue + employees) ───────────────────────
  const [quickUpdateDialog, setQuickUpdateDialog] = useState(false);
  const [quickUpdateMsme, setQuickUpdateMsme]     = useState(null);
  const [quickUpdateSaving, setQuickUpdateSaving] = useState(false);
  const [quickUpdateForm, setQuickUpdateForm]     = useState({
    last_month_revenue: '', employees_ft_male: '', employees_ft_female: '',
    employees_pt_male: '', employees_pt_female: '',
    latitude: '', longitude: '', gpsStatus: 'idle', // 'idle'|'loading'|'ok'|'denied'
  });

  const openQuickUpdate = (msme) => {
    setQuickUpdateMsme(msme);
    setQuickUpdateForm({
      last_month_revenue: '', employees_ft_male: '', employees_ft_female: '',
      employees_pt_male: '', employees_pt_female: '',
      latitude: '', longitude: '', gpsStatus: 'loading',
    });
    setQuickUpdateDialog(true);
    // Auto-capture device GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setQuickUpdateForm(f => ({
          ...f, gpsStatus: 'ok',
          latitude:  pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        })),
        () => setQuickUpdateForm(f => ({ ...f, gpsStatus: 'denied' })),
        { timeout: 10000 },
      );
    } else {
      setQuickUpdateForm(f => ({ ...f, gpsStatus: 'denied' }));
    }
  };

  const saveQuickUpdate = async () => {
    if (!quickUpdateMsme) return;
    setQuickUpdateSaving(true);
    const h = { Authorization: `Bearer ${token}` };
    const bgeId = currentUser?.bge_profile?.id;
    try {
      const snapshotPayload = {
        msme:                quickUpdateMsme.id,
        snapshot_date:       new Date().toISOString().slice(0, 10),
        source:              'bge_visit',
        collected_by:        bgeId || null,
        last_month_revenue:  quickUpdateForm.last_month_revenue  || null,
        employees_ft_male:   quickUpdateForm.employees_ft_male   !== '' ? Number(quickUpdateForm.employees_ft_male)   : null,
        employees_ft_female: quickUpdateForm.employees_ft_female !== '' ? Number(quickUpdateForm.employees_ft_female) : null,
        employees_pt_male:   quickUpdateForm.employees_pt_male   !== '' ? Number(quickUpdateForm.employees_pt_male)   : null,
        employees_pt_female: quickUpdateForm.employees_pt_female !== '' ? Number(quickUpdateForm.employees_pt_female) : null,
        ...(quickUpdateForm.gpsStatus === 'ok' ? {
          latitude:  parseFloat(quickUpdateForm.latitude),
          longitude: parseFloat(quickUpdateForm.longitude),
        } : {}),
      };
      await axios.post(API_ENDPOINTS.GROWTH_SNAPSHOTS, snapshotPayload, { headers: h });

      // Also update the MSME's own GPS pin if we captured a location
      if (quickUpdateForm.gpsStatus === 'ok') {
        const newLat = parseFloat(quickUpdateForm.latitude);
        const newLng = parseFloat(quickUpdateForm.longitude);
        await axios.patch(`${API_ENDPOINTS.MSMES}${quickUpdateMsme.id}/`, {
          latitude:  newLat,
          longitude: newLng,
        }, { headers: h });
        setMsmes(prev => prev.map(m => m.id === quickUpdateMsme.id ? { ...m, latitude: newLat, longitude: newLng } : m));
      }

      notify('Visit data saved ✓');
      setQuickUpdateDialog(false);
      fetchMsmes();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to save visit data', 'error');
    } finally {
      setQuickUpdateSaving(false);
    }
  };

  // ── Edit MSME Information (Status, Location, Alternative Contact) ────────
  const [editMsmeDialog, setEditMsmeDialog] = useState(false);
  const [editMsmeSaving, setEditMsmeSaving] = useState(false);
  const [editMsmeError, setEditMsmeError] = useState('');
  const [editMsmeForm, setEditMsmeForm] = useState({
    id: '',
    business_name: '',
    status: 'active',
    district: '',
    city: '',
    address: '',
    latitude: '',
    longitude: '',
    _gpsLoading: false,
    owner_name: '',
    phone: '',
    email: '',
    alt_contact_name: '',
    alt_phone: '',
    alt_email: '',
    alt_contact_role: '',
  });

  const openEditMsme = (msme) => {
    setEditMsmeForm({
      id: msme.id,
      business_name: msme.business_name || '',
      status: msme.status || 'active',
      district: msme.district || msme.state || msme.diag_district || '',
      city: msme.city || '',
      address: msme.address || '',
      latitude: msme.latitude != null && msme.latitude !== '' ? String(msme.latitude) : '',
      longitude: msme.longitude != null && msme.longitude !== '' ? String(msme.longitude) : '',
      _gpsLoading: false,
      owner_name: msme.owner_name || '',
      phone: msme.phone || '',
      email: msme.email || msme.business_email || '',
      alt_contact_name: msme.alt_contact_name || '',
      alt_phone: msme.alt_phone || '',
      alt_email: msme.alt_email || '',
      alt_contact_role: msme.alt_contact_role || '',
    });
    setEditMsmeSaving(false);
    setEditMsmeError('');
    setEditMsmeDialog(true);
  };

  const saveEditMsme = async () => {
    if (!editMsmeForm.id) return;
    setEditMsmeSaving(true);
    setEditMsmeError('');
    try {
      const payload = {
        status: editMsmeForm.status,
        district: (editMsmeForm.district || '').trim(),
        state: (editMsmeForm.district || '').trim(),
        city: (editMsmeForm.city || '').trim(),
        address: (editMsmeForm.address || '').trim(),
        latitude: editMsmeForm.latitude !== '' && editMsmeForm.latitude !== null ? parseFloat(editMsmeForm.latitude) : null,
        longitude: editMsmeForm.longitude !== '' && editMsmeForm.longitude !== null ? parseFloat(editMsmeForm.longitude) : null,
        owner_name: (editMsmeForm.owner_name || '').trim(),
        phone: (editMsmeForm.phone || '').trim(),
        email: (editMsmeForm.email || '').trim(),
        alt_contact_name: (editMsmeForm.alt_contact_name || '').trim(),
        alt_phone: (editMsmeForm.alt_phone || '').trim(),
        alt_email: (editMsmeForm.alt_email || '').trim(),
        alt_contact_role: (editMsmeForm.alt_contact_role || '').trim(),
      };
      const res = await axios.patch(`${API_ENDPOINTS.MSMES}${editMsmeForm.id}/`, payload, { headers });
      const updated = res.data;
      notify(`Updated ${updated.business_name || 'MSME'} successfully`, 'success');
      setMsmes(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
      setSelectedMsme(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      setEditMsmeDialog(false);
      fetchMsmes();
    } catch (err) {
      const d = err.response?.data;
      const msg = d?.detail || d?.error || (typeof d === 'object' ? Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(', ') : 'Failed to update MSME');
      setEditMsmeError(msg);
      notify(msg, 'error');
    } finally {
      setEditMsmeSaving(false);
    }
  };

  // T-shirt receipts (BGE signs their own entry)
  const [tshirtEntries, setTshirtEntries] = useState([]);
  const [tshirtEntriesLoading, setTshirtEntriesLoading] = useState(false);
  const [tshirtSigningId, setTshirtSigningId] = useState(null); // entry id being signed
  const [tshirtEdits, setTshirtEdits] = useState({}); // { [entryId]: { size, quantity } }

  const DIGITAL_TOOLS_OPTIONS = [
    'Zoho Books',
    'Biashara App',
    'Brevo (Email Marketing)',
    'Message Carrier (SMS)',
    'WhatsApp Business',
    'QuickBooks',
    'Facebook / Instagram Business',
    'Google My Business',
    'MTN / Airtel MoMo Business',
    'Point of Sale (POS) System',
    'Canva (Design & Marketing)',
    'TikTok for Business',
    'Shopify / WooCommerce',
    'Other',
  ];

  const TRAINING_CHANGE_OPTIONS = [
    'Improved record keeping / bookkeeping',
    'Adopted digital accounting tools',
    'Registered business (URSB / TIN)',
    'Opened a business bank account',
    'Joined a SACCO',
    'Improved customer service',
    'Expanded product or service range',
    'Developed a business plan',
    'Improved marketing and promotion',
    'Hired additional staff',
    'Improved pricing strategy',
    'Reduced costs / improved efficiency',
    'Accessed new markets or customers',
    'Improved supply chain management',
    'Improved financial management',
    'Other',
  ];

  const EMPTY_GROWTH = {
    snapshot_date: new Date().toISOString().slice(0, 10),
    source: 'bge_visit',
    annual_turnover: '', total_assets: '',
    employees_ft_male: '', employees_ft_female: '',
    employees_pt_male: '', employees_pt_female: '',
    has_tin: '', tin_number: '',
    has_ursb: '', ursb_reg_number: '',
    has_business_bank: '', bank_name: '',
    has_sacco: '',
    has_mobile_money: '',
    has_momo_pay: '', momo_pay_code: '',
    employees_ft_refugee: '', employees_pt_refugee: '',
    last_month_revenue: '',
    digital_tools: [],
    digital_tools_other: '',
    training_made_changes: '',
    training_changes: [],
    training_changes_other: '',
    notes: '',
    // narrative (only used when source === 'annual' or 'quarterly')
    narrative_business_overview: '',
    narrative_key_achievement: '',
    narrative_challenges: '',
    narrative_support_provided: '',
    narrative_recommendations: '',
    narrative_next_steps: '',
  };
  const [growthForm, setGrowthForm] = useState(EMPTY_GROWTH);

  const openGrowthForm = async (msme) => {
    setGrowthMsme(msme);
    setGrowthForm(EMPTY_GROWTH);
    setGrowthError('');
    setGrowthDialog(true);
    // Load existing snapshots for history display
    try {
      const res = await axios.get(`${API_ENDPOINTS.GROWTH_SNAPSHOTS}?msme=${msme.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setGrowthSnapshots(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (e) { setGrowthSnapshots([]); }
  };

  const saveGrowthSnapshot = async () => {
    if (!growthMsme) return;
    setGrowthSaving(true); setGrowthError('');
    const bgeId = currentUser?.bge_profile?.id;
    const payload = {
      msme: growthMsme.id,
      snapshot_date: growthForm.snapshot_date,
      source: growthForm.source,
      collected_by: bgeId || null,
      annual_turnover:     growthForm.annual_turnover   || null,
      last_month_revenue:  growthForm.last_month_revenue || null,
      total_assets:        growthForm.total_assets      || null,
      employees_ft_male:   growthForm.employees_ft_male   !== '' ? Number(growthForm.employees_ft_male)   : null,
      employees_ft_female: growthForm.employees_ft_female !== '' ? Number(growthForm.employees_ft_female) : null,
      employees_pt_male:   growthForm.employees_pt_male   !== '' ? Number(growthForm.employees_pt_male)   : null,
      employees_pt_female: growthForm.employees_pt_female !== '' ? Number(growthForm.employees_pt_female) : null,
      has_tin:           growthForm.has_tin           === '' ? null : growthForm.has_tin === 'true',
      tin_number:        growthForm.has_tin === 'true' ? (growthForm.tin_number || '') : '',
      has_ursb:          growthForm.has_ursb          === '' ? null : growthForm.has_ursb === 'true',
      ursb_reg_number:   growthForm.has_ursb === 'true' ? (growthForm.ursb_reg_number || '') : '',
      has_business_bank: growthForm.has_business_bank === '' ? null : growthForm.has_business_bank === 'true',
      bank_name:         growthForm.has_business_bank === 'true' ? (growthForm.bank_name || '') : '',
      has_sacco:         growthForm.has_sacco         === '' ? null : growthForm.has_sacco === 'true',
      has_mobile_money:  growthForm.has_mobile_money  === '' ? null : growthForm.has_mobile_money === 'true',
      has_momo_pay:      growthForm.has_momo_pay      === '' ? null : growthForm.has_momo_pay === 'true',
      momo_pay_code:     growthForm.has_momo_pay === 'true' ? (growthForm.momo_pay_code || '') : '',
      employees_ft_refugee: growthForm.employees_ft_refugee !== '' ? Number(growthForm.employees_ft_refugee) : null,
      employees_pt_refugee: growthForm.employees_pt_refugee !== '' ? Number(growthForm.employees_pt_refugee) : null,
      digital_tools:          growthForm.digital_tools,
      digital_tools_other:    growthForm.digital_tools.includes('Other') ? (growthForm.digital_tools_other || '') : '',
      training_made_changes:  growthForm.training_made_changes === '' ? null : growthForm.training_made_changes === 'true',
      training_changes:       growthForm.training_made_changes === 'true' ? growthForm.training_changes : [],
      training_changes_other: (growthForm.training_made_changes === 'true' && growthForm.training_changes.includes('Other'))
                                ? (growthForm.training_changes_other || '') : '',
      notes: growthForm.notes,
    };
    const isReviewType = growthForm.source === 'annual' || growthForm.source === 'quarterly';
    const hasNarrative = isReviewType && [
      growthForm.narrative_business_overview, growthForm.narrative_key_achievement,
      growthForm.narrative_challenges, growthForm.narrative_support_provided,
      growthForm.narrative_recommendations, growthForm.narrative_next_steps,
    ].some(v => v.trim());

    try {
      await axios.post(API_ENDPOINTS.GROWTH_SNAPSHOTS, payload,
        { headers: { Authorization: `Bearer ${token}` } });

      if (hasNarrative) {
        const visitType = growthForm.source === 'annual' ? 'annual_review' : 'quarterly_review';
        const reportPayload = {
          msme:                   growthMsme.id,
          bge:                    bgeId || null,
          visit_type:             visitType,
          visit_date:             growthForm.snapshot_date,
          status:                 'draft',
          business_overview:      growthForm.narrative_business_overview,
          key_achievement:        growthForm.narrative_key_achievement,
          challenges_identified:  growthForm.narrative_challenges,
          support_provided:       growthForm.narrative_support_provided,
          recommendations:        growthForm.narrative_recommendations,
          next_steps:             growthForm.narrative_next_steps,
        };
        await axios.post(API_ENDPOINTS.REPORTS, reportPayload,
          { headers: { Authorization: `Bearer ${token}` } });
        notify('Growth update and narrative report saved as draft.');
      } else {
        notify('Growth update saved.');
      }
      setGrowthDialog(false);
    } catch (e) {
      // Extract a human-readable message from DRF validation errors
      const errData = e.response?.data;
      const errMsg =
        typeof errData === 'string' ? errData :
        Array.isArray(errData) ? errData[0] :
        errData?.detail || errData?.non_field_errors?.[0] ||
        (typeof errData === 'object' ? Object.values(errData).flat()[0] : null) ||
        'Save failed.';
      setGrowthError(String(errMsg));
    } finally { setGrowthSaving(false); }
  };

  // in-app report viewer
  const [viewReport, setViewReport] = useState(null);

  const notify = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const fetchMsmes = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const res = await axios.get(`${API_ENDPOINTS.MSMES}?all=1`, { headers: h });
      setMsmes(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      notify('Failed to load MSMEs', 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchReports = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.REPORTS, { headers: h });
      setReports(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      notify('Failed to load reports', 'error');
    }
  }, [token]);

  const fetchGroups = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.BGE_GROUPS, { headers: h });
      const all = Array.isArray(res.data) ? res.data : res.data.results || [];
      // Backend returns every group the BGE-user can see — for a non-admin
      // BGE user, the list endpoint isn't tenant-scoped (it's open), so
      // narrow to just the groups this BGE belongs to client-side.
      setGroups(all.filter(g => (g.members_detail || []).some(m => m.id === myBgeId)));
    } catch {
      // silent — group view is optional
    }
  }, [token, myBgeId]);

  const fetchGroupReports = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.GROUP_REPORTS, { headers: h });
      setGroupReports(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      // silent — endpoint may be unreachable on stale deploys
    }
  }, [token]);

  const fetchWorkOrders = useCallback(async (retryCount = 0) => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.WORK_ORDERS, { headers: h, timeout: 60000 });
      setWorkOrders(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (err) {
      const isNetworkOrColdStart = !err.response || err.message === 'Network Error' || err.code === 'ECONNABORTED' || err.response?.status >= 500;
      if (retryCount < 2 && isNetworkOrColdStart) {
        setTimeout(() => fetchWorkOrders(retryCount + 1), (retryCount + 1) * 3500);
      }
    }
  }, [token]);

  const fetchTbipPlans = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.ENTERPRISE_IMPROVEMENT_PLANS, { headers: h });
      setTbipPlans(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      // non-critical
    }
  }, [token]);

  const fetchFiapPlans = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.FINANCIAL_IMPROVEMENT_PLANS, { headers: h });
      setFiapPlans(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      // non-critical
    }
  }, [token]);


  const fetchSubmissions = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.WORK_ORDER_SUBMISSIONS, { headers: h });
      setSubmissions(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      // silent
    }
  }, [token]);

  const fetchPayments = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.WORK_ORDER_PAYMENTS, { headers: h });
      setPayments(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      // silent
    }
  }, [token]);

  const fetchAttachments = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(API_ENDPOINTS.WORK_ORDER_ATTACHMENTS, { headers: h });
      setAttachments(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (err) {
      console.warn('Failed to fetch work order attachments:', err);
    }
  }, [token]);


  const fetchSessions = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [sRes, faRes, trRes, mrRes] = await Promise.all([
        axios.get(API_ENDPOINTS.TRAINING_SESSIONS, { headers: h }),
        axios.get(API_ENDPOINTS.FACILITATION_ASSIGNMENTS, { headers: h }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.TRAINING_REPORTS, { headers: h }).catch(() => ({ data: [] })),
        axios.get(MENTOR_REPORTS, { headers: h }).catch(() => ({ data: [] })),
      ]);
      const sessData = Array.isArray(sRes.data) ? sRes.data : sRes.data.results || [];
      const allAssignments = Array.isArray(faRes.data) ? faRes.data : faRes.data.results || [];
      const trReports = Array.isArray(trRes.data) ? trRes.data : trRes.data.results || [];
      const mrReports = Array.isArray(mrRes.data) ? mrRes.data : mrRes.data.results || [];

      const trBySession = {};
      trReports.forEach(r => { trBySession[r.session] = r; });
      const mrBySession = {};
      mrReports.forEach(r => { mrBySession[r.session] = r; });

      const enriched = sessData.map(s => ({
        ...s,
        _tr: trBySession[s.id] || null,
        _mr: mrBySession[s.id] || null,
      }));

      const leadSessionIds = new Set([
        ...allAssignments.filter(a => a.role !== 'mentor' && a.session).map(a => a.session),
        ...sessData.filter(s => (s.team || []).some(m => m.bge_id === myBgeId && m.role === 'lead')).map(s => s.id),
      ]);
      const mentorIds = new Set([
        ...allAssignments.filter(a => a.role === 'mentor' && a.session).map(a => a.session),
        ...sessData.filter(s => (s.team || []).some(m => m.bge_id === myBgeId && m.role === 'mentor')).map(s => s.id),
      ]);
      const leadTopics = new Set(allAssignments.filter(a => a.role !== 'mentor' && a.topic).map(a => a.topic));
      setLeadSessions(enriched.filter(s => leadSessionIds.has(s.id) || (s.topic && leadTopics.has(s.topic))));
      setMentorSessions(enriched.filter(s => mentorIds.has(s.id)));
      setParticipantSessions(enriched.filter(s =>
        (s.bge_participants || []).includes(myBgeId)
      ));
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, myBgeId]);

  const fetchPtReports = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(PARTICIPANT_TRAINING_REPORTS, { headers: h });
      setPtReports(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch { /* silent */ }
  }, [token]);

  const fetchTshirtEntries = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setTshirtEntriesLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.TSHIRT_ENTRIES, { headers: h });
      setTshirtEntries(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setTshirtEntries([]);
    } finally {
      setTshirtEntriesLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (section === 'tshirts') fetchTshirtEntries();
  }, [section, fetchTshirtEntries]);

  const handleSignTshirtEntry = async (entryId) => {
    const h = { Authorization: `Bearer ${token}` };
    setTshirtSigningId(entryId);
    // Include any size/qty edits the BGE made before signing
    const edits = tshirtEdits[entryId] || {};
    const body = {};
    if (edits.size)     body.size     = edits.size;
    if (edits.quantity) body.quantity = edits.quantity;
    try {
      await axios.post(TSHIRT_ENTRY_SIGN_URL(entryId), body, { headers: h });
      await fetchTshirtEntries();
      // Clear edits for this entry since it's now signed
      setTshirtEdits(prev => { const next = { ...prev }; delete next[entryId]; return next; });
      setSnack({ open: true, msg: 'T-shirt receipt signed successfully!', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, msg: err.response?.data?.detail || 'Signing failed. Please try again.', severity: 'error' });
    } finally {
      setTshirtSigningId(null);
    }
  };

  const handleDownloadTshirtPdf = async (receiptId, receiptTitle) => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(TSHIRT_RECEIPT_PDF_URL(receiptId), { headers: h, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tshirt-receipt-${(receiptTitle || receiptId).toString().replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnack({ open: true, msg: 'Failed to download PDF.', severity: 'error' });
    }
  };

  const openAttendance = async (session) => {
    fetchTrainingMsmes();
    setAttSession(session);
    setAttLoading(true);
    setAttDialog(true);
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API_ENDPOINTS.ATTENDANCE}?session=${session.id}`, { headers: h });
      const records = Array.isArray(res.data) ? res.data : res.data.results || [];
      setAttRows(records.length ? records.map(r => ({ ...r, _key: r.id })) : [newAttRow()]);
    } catch {
      setAttRows([newAttRow()]);
    } finally {
      setAttLoading(false);
    }
  };

  const saveAttendance = async () => {
    setAttLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    try {
      const filled = attRows.filter(a => a.attendee_name || a.msme);
      await Promise.all(filled.map(a => {
        const payload = {
          session: attSession.id,
          msme: a.msme || null,
          attendee_name: a.attendee_name,
          attendee_phone: a.attendee_phone,
          gender: a.gender,
          age_group: a.age_group,
          refugee_status: a.refugee_status || 'H',
          consent_photo: a.consent_photo,
          consent_contact: a.consent_contact,
          present: true,
        };
        if (a.id) return axios.patch(`${API_ENDPOINTS.ATTENDANCE}${a.id}/`, payload, { headers: h });
        return axios.post(API_ENDPOINTS.ATTENDANCE, payload, { headers: h });
      }));
      notify('Attendance saved');
      setAttDialog(false);
      fetchSessions();
    } catch { notify('Failed to save attendance', 'error'); }
    finally { setAttLoading(false); }
  };

  const openTrReport = async (session) => {
    setTrSession(session);
    const existing = session._tr;
    const stats = session.attendance_stats || {};
    if (existing) {
      let fullTr = existing;
      try {
        const res = await axios.get(`${API_ENDPOINTS.TRAINING_REPORTS}${existing.id}/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullTr = res.data;
      } catch { /* fallback to existing */ }
      const { id, session: _s, bge: _b, created_at, updated_at, submitted_at,
              session_title, session_date, session_location, bge_name, total_participants, ...rest } = fullTr;
      setTrData(fullTr);
      setTrForm({ ...EMPTY_TR, ...rest });
    } else {
      setTrData(null);
      setTrForm({
        ...EMPTY_TR,
        training_title: session.title || '',
        training_dates: session.date || '',
        venue: session.location || '',
        district: session.location || '',
        facilitation_team: (session.team || []).map(m => `${m.bge_name} (${m.role === 'lead' ? 'Lead' : 'Mentor'})`).filter(Boolean).join(', '),
        // Auto-fill demographics from attendance sheet
        ...(stats.total_present ? {
          participants_male_youth:   stats.youth_male   || 0,
          participants_female_youth: stats.youth_female || 0,
          participants_adult_male:   stats.adult_male   || 0,
          participants_adult_female: stats.adult_female || 0,
        } : {}),
      });
    }
    setTrDialog(true);
  };

  const openTrPdf = (reportId, mode = 'view') => {
    if (!reportId) return;
    const url = `${TRAINING_REPORT_PDF_URL(reportId)}${mode === 'download' ? '?dl=1' : ''}`;
    window.open(url, '_blank');
  };

  const openNewP2pSession = async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      if (allExperts.length === 0) {
        const expRes = await axios.get(API_ENDPOINTS.EXPERTS, { headers: h });
        setAllExperts(Array.isArray(expRes.data) ? expRes.data : expRes.data.results || []);
      }
      if (allTopics.length === 0) {
        const topRes = await axios.get(API_ENDPOINTS.TRAINING_TOPICS, { headers: h });
        setAllTopics(Array.isArray(topRes.data) ? topRes.data : topRes.data.results || []);
      }
    } catch { /* non-critical */ }
    fetchTrainingMsmes();

    setP2pForm({
      title: 'Peer-to-Peer Learning Session',
      date: new Date().toISOString().slice(0, 10),
      end_date: '',
      location: myBgeProfile?.district || 'Gulu / Lira',
      district: myBgeProfile?.district || '',
      topic: '',
      lead_bge_id: myBgeId || '',
      mentor_bge_ids: [],
      msme_ids: [],
      description: '',
    });
    setP2pSearchMsme('');
    setP2pSessionDialog(true);
  };

  const saveP2pSession = async () => {
    if (!p2pForm.title.trim()) { notify('Please enter a session title', 'error'); return; }
    if (!p2pForm.date) { notify('Please select a session date', 'error'); return; }
    if (!p2pForm.lead_bge_id) { notify('Please select a Lead BGE', 'error'); return; }

    setP2pSessionSaving(true);
    const h = { Authorization: `Bearer ${token}` };
    const payload = {
      title: p2pForm.title.trim(),
      date: p2pForm.date,
      end_date: p2pForm.end_date || null,
      location: p2pForm.location.trim(),
      topic: p2pForm.topic || null,
      description: p2pForm.description.trim(),
      businesses: p2pForm.msme_ids,
      team: [
        { bge_id: p2pForm.lead_bge_id, role: 'lead' },
        ...p2pForm.mentor_bge_ids.map(id => ({ bge_id: id, role: 'mentor' })),
      ],
    };
    try {
      await axios.post(API_ENDPOINTS.TRAINING_SESSIONS, payload, { headers: h });
      notify('Peer-to-Peer session created successfully!');
      setP2pSessionDialog(false);
      fetchSessions();
    } catch (err) {
      notify(getErrorMessage(err, 'Failed to create session'), 'error');
    } finally {
      setP2pSessionSaving(false);
    }
  };

  const saveTrReport = async (submitNow = false) => {
    if (!trSession) return;
    setTrSaving(true);
    const h = { Authorization: `Bearer ${token}` };
    const payload = { ...trForm, session: trSession.id, status: submitNow ? 'submitted' : (trForm.status || 'draft') };
    try {
      if (trData?.id) {
        await axios.patch(`${API_ENDPOINTS.TRAINING_REPORTS}${trData.id}/`, payload, { headers: h });
      } else {
        await axios.post(API_ENDPOINTS.TRAINING_REPORTS, payload, { headers: h });
      }
      notify(submitNow ? 'Report submitted!' : 'Draft saved');
      setTrDialog(false);
      fetchSessions();
    } catch (err) { notify(err.response?.data?.detail || 'Failed to save', 'error'); }
    finally { setTrSaving(false); }
  };

  const synthesizeMentorReports = () => {
    if (!trData?.mentor_reports?.length) {
      notify('No mentor reports found for this session yet.', 'info');
      return;
    }
    const reports = trData.mentor_reports;
    const mentorObs = reports.map(r =>
      `• [${r.bge_name || 'Mentor'}]: ${r.key_observations || 'No observations recorded'}`
    ).join('\n');
    const mentorChallenges = reports.map(r =>
      `• [${r.bge_name || 'Mentor'}]: ${r.challenges || 'No challenges noted'}`
    ).join('\n');
    const mentorContrib = reports.map(r =>
      `• [${r.bge_name || 'Mentor'}]: Mentored: ${r.msmes_mentored || 'Assigned MSMEs'}. Activities: ${r.mentoring_activities || 'Syndicate breakout facilitation'}`
    ).join('\n');

    setTrForm(prev => ({
      ...prev,
      key_findings: prev.key_findings
        ? `${prev.key_findings}\n\n[Syndicate Mentor Findings & Challenges]:\n${mentorChallenges}`
        : `[Syndicate Mentor Findings & Challenges]:\n${mentorChallenges}`,
      growth_support_areas: prev.growth_support_areas
        ? `${prev.growth_support_areas}\n\n[Syndicate Diagnostic Observations]:\n${mentorObs}`
        : `[Syndicate Diagnostic Observations]:\n${mentorObs}`,
      bge_contributions: prev.bge_contributions
        ? `${prev.bge_contributions}\n\n[Mentor Syndicate Facilitation]:\n${mentorContrib}`
        : `[Mentor Syndicate Facilitation]:\n${mentorContrib}`,
    }));
    notify('Mentor syndicate observations synthesized into Questions 5, 6, and 7!');
  };

  const openMrReport = (session) => {
    setMrSession(session);
    const existing = session._mr;
    if (existing) {
      setMrData(existing);
      setMrForm({ ...EMPTY_MR, ...existing });
    } else {
      setMrData(null);
      setMrForm({
        ...EMPTY_MR,
        training_title: session.title || '',
        training_dates: session.date || '',
        venue: session.location || '',
      });
    }
    setMrDialog(true);
  };

  const saveMrReport = async (submitNow = false) => {
    if (!mrSession) return;
    setMrSaving(true);
    const h = { Authorization: `Bearer ${token}` };
    const existing = mrSession._mr;
    const payload = { ...mrForm, session: mrSession.id, status: submitNow ? 'submitted' : (mrForm.status || 'draft') };
    try {
      if (existing?.id) {
        await axios.patch(`${MENTOR_REPORTS}${existing.id}/`, payload, { headers: h });
      } else {
        await axios.post(MENTOR_REPORTS, payload, { headers: h });
      }
      notify(submitNow ? 'Mentor report submitted!' : 'Draft saved');
      setMrDialog(false);
      fetchSessions();
    } catch (err) { notify(err.response?.data?.detail || 'Failed to save', 'error'); }
    finally { setMrSaving(false); }
  };

  const openPtDialog = (report = null, sessionDefaults = {}) => {
    setPtEdit(report);
    // If opened from a session card (sessionDefaults has attendees), always use the
    // session's current bge_participants list — the admin controls who was registered.
    // If no sessionDefaults, use what was saved on the report (or empty).
    const base = report ? { ...EMPTY_PT, ...report } : { ...EMPTY_PT };
    const merged = sessionDefaults.attendees
      ? { ...base, ...sessionDefaults, attendees: sessionDefaults.attendees }
      : { ...base, ...sessionDefaults };
    setPtForm(merged);
    setPtAttendeeRow({ name: '', bge_code: '', phone: '', gender: '', organisation: '' });
    setPtDialog(true);
  };

  const savePtReport = async (submitNow = false) => {
    setPtSaving(true);
    const h = { Authorization: `Bearer ${token}` };
    const payload = { ...ptForm, status: submitNow ? 'submitted' : (ptForm.status || 'draft') };
    try {
      if (ptEdit?.id) {
        await axios.patch(`${PARTICIPANT_TRAINING_REPORTS}${ptEdit.id}/`, payload, { headers: h });
      } else {
        await axios.post(PARTICIPANT_TRAINING_REPORTS, payload, { headers: h });
      }
      notify(submitNow ? 'Report submitted!' : 'Draft saved');
      setPtDialog(false);
      fetchPtReports();
    } catch (err) { notify(err.response?.data?.detail || 'Failed to save', 'error'); }
    finally { setPtSaving(false); }
  };

  const openPtPdf = async (id, mode = 'view') => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${PARTICIPANT_TRAINING_REPORT_PDF_URL(id)}${mode === 'download' ? '?dl=1' : ''}`,
        { headers: h, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url; a.download = `ParticipantReport_${id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else { window.open(url, '_blank'); }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch { notify('Failed to render PDF', 'error'); }
  };

  const fetchMyProfile = useCallback(async () => {
    if (!myBgeId) return;
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API_ENDPOINTS.EXPERTS}${myBgeId}/`, { headers: h });
      if (res.data) {
        setMyBgeProfile(res.data);
        if (res.data.signature_url) setSigUrl(res.data.signature_url);
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (storedUser && storedUser.bge_profile) {
            storedUser.bge_profile = { ...storedUser.bge_profile, ...res.data };
            localStorage.setItem('user', JSON.stringify(storedUser));
          }
        } catch {}
      }
    } catch {}
  }, [myBgeId, token]);

  const pushAttempted = useRef(false);

  useEffect(() => {
    fetchMyProfile();
    fetchMsmes();
    fetchReports();
    fetchGroups();
    fetchGroupReports();
    fetchWorkOrders();
    fetchSubmissions();
    fetchPayments();
    fetchAttachments();
    fetchSessions();
    fetchPtReports();
    fetchTbipPlans();
    fetchFiapPlans();
    // Request push notification permission once per session
    if (!pushAttempted.current) {
      pushAttempted.current = true;
      subscribePush(`Bearer ${token}`);
    }
  }, [fetchMyProfile, fetchMsmes, fetchReports, fetchGroups, fetchGroupReports, fetchWorkOrders, fetchSubmissions, fetchPayments, fetchAttachments, fetchSessions, fetchPtReports, fetchTbipPlans, fetchFiapPlans, token]);

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_title: `BGE - ${section}`, page_path: `/bge/${section}` });
    }
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNewReport = (msmeId = '', type = 'data_update') => {
    const msme = msmes.find(x => x.id === msmeId || x.id === Number(msmeId)) || null;
    setVisitReportMsme(msme);
    setVisitReportType(type || 'data_update');
    setVisitReportEdit(null);
    setVisitReportOpen(true);
  };

  const openEditReport = (report) => {
    const msme = msmes.find(x => x.id === report.msme || x.id === Number(report.msme)) || null;
    setVisitReportMsme(msme);
    setVisitReportEdit(report);
    setVisitReportOpen(true);
  };

  const deleteReport = async (report) => {
    if (!window.confirm(`Delete this draft report for "${report.msme_name || 'this MSME'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_ENDPOINTS.REPORTS}${report.id}/`, { headers: { Authorization: `Bearer ${token}` } });
      notify('Draft report deleted', 'success');
      setReports(prev => prev.filter(r => r.id !== report.id));
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to delete report', 'error');
    }
  };

  // ── group reports ─────────────────────────────────────────────────────────

  /* Draft-save helpers (debounced 800 ms) */
  const scheduleGrpDraftSave = useCallback((f) => {
    clearTimeout(grpDraftTimerRef.current);
    grpDraftTimerRef.current = setTimeout(() => {
      if (currentGrpDraftKey.current) writeGrpDraft(currentGrpDraftKey.current, f);
    }, 800);
  }, []);

  /* Thin wrapper — updates form AND schedules a draft save */
  const setGrf = useCallback((updater) => {
    setGroupReportForm(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      scheduleGrpDraftSave(next);
      return next;
    });
  }, [scheduleGrpDraftSave]);

  const restoreGrpDraft = () => {
    const saved = readGrpDraft(currentGrpDraftKey.current);
    if (!saved) return;
    setGroupReportForm(saved.form);
    setGrpDraftBanner(null);
  };

  const discardGrpDraft = () => {
    clearGrpDraft(currentGrpDraftKey.current);
    setGrpDraftBanner(null);
  };

  const openNewGroupReport = (groupId = '') => {
    setEditingGroupReport(null);
    const key = grpDraftKey(null, groupId);
    currentGrpDraftKey.current = key;
    const saved = readGrpDraft(key);
    if (saved) {
      setGrpDraftBanner({ savedAt: saved.savedAt });
      setGroupReportForm(saved.form);
    } else {
      setGroupReportForm({ ...EMPTY_GROUP_REPORT, group: groupId });
      setGrpDraftBanner(null);
    }
    setGroupReportErrors('');
    setGrpAttendees([newGrpRow()]);
    setGroupReportDialog(true);
  };

  const openEditGroupReport = async (rep) => {
    setEditingGroupReport(rep);
    const key = grpDraftKey(rep, rep.group);
    currentGrpDraftKey.current = key;
    const serverForm = {
      group: rep.group,
      session_number: rep.session_number || '',
      visit_date: rep.visit_date,
      msmes_supported: rep.msmes_supported || [],
      attendees: rep.attendees || [],
      session_overview: rep.session_overview || '',
      challenges_identified: rep.challenges_identified || '',
      interventions_delivered: rep.interventions_delivered || '',
      outcomes_achieved: rep.outcomes_achieved || '',
      next_steps: rep.next_steps || '',
      additional_notes: rep.additional_notes || '',
      status: rep.status,
    };
    const saved = readGrpDraft(key);
    const serverTs = rep.updated_at ? new Date(rep.updated_at).getTime() : 0;
    if (saved && saved.savedAt > serverTs) {
      setGrpDraftBanner({ savedAt: saved.savedAt });
      // Show server data first; user decides to restore or discard
      setGroupReportForm(serverForm);
    } else {
      setGroupReportForm(serverForm);
      setGrpDraftBanner(null);
    }
    setGroupReportErrors('');
    setReportContributions([]);
    setGrpAttendees([]);
    const h = { Authorization: `Bearer ${token}` };
    // Load member contributions and existing attendance in parallel
    const [contribRes, attRes] = await Promise.allSettled([
      axios.get(`${API_ENDPOINTS.GROUP_REPORT_CONTRIBUTIONS}?group_report=${rep.id}`, { headers: h }),
      axios.get(`${API_ENDPOINTS.GROUP_REPORT_ATTENDANCE}?group_report=${rep.id}`, { headers: h }),
    ]);
    if (contribRes.status === 'fulfilled') {
      const list = Array.isArray(contribRes.value.data) ? contribRes.value.data : (contribRes.value.data.results || []);
      setReportContributions(list.filter(c => Number(c.bge) !== Number(myBgeId)));
    }
    if (attRes.status === 'fulfilled') {
      const list = Array.isArray(attRes.value.data) ? attRes.value.data : (attRes.value.data.results || []);
      setGrpAttendees(list.length ? list.map(r => ({ ...r, _key: r.id })) : [newGrpRow()]);
    } else {
      setGrpAttendees([newGrpRow()]);
    }
    setGroupReportDialog(true);
  };

  const saveGroupReport = async (statusOverride) => {
    if (!groupReportForm.group) { setGroupReportErrors('Please select a group.'); return; }
    if (!groupReportForm.visit_date) { setGroupReportErrors('Please set a visit date.'); return; }
    setGroupReportSaving(true);
    setGroupReportErrors('');
    const payload = {
      ...groupReportForm,
      session_number: groupReportForm.session_number === '' ? null : Number(groupReportForm.session_number),
      ...(statusOverride ? { status: statusOverride } : {}),
    };
    const h = { Authorization: `Bearer ${token}` };
    try {
      let reportId;
      if (editingGroupReport) {
        await axios.patch(`${API_ENDPOINTS.GROUP_REPORTS}${editingGroupReport.id}/`, payload, { headers: h });
        reportId = editingGroupReport.id;
        notify(statusOverride === 'submitted' ? 'Group report submitted' : 'Group report draft saved');
      } else {
        const r = await axios.post(API_ENDPOINTS.GROUP_REPORTS, payload, { headers: h });
        reportId = r.data.id;
        notify(statusOverride === 'submitted' ? 'Group report submitted' : 'Group report draft saved');
      }
      // Save MSME attendance rows (present ones only)
      if (reportId) {
        const present = grpAttendees.filter(a => a.attendee_name || a.msme);
        await Promise.allSettled(present.map(a => {
          const ap = {
            group_report: reportId,
            msme: a.msme || null,
            attendee_name: a.attendee_name,
            attendee_phone: a.attendee_phone,
            gender: a.gender,
            age_group: a.age_group,
            refugee_status: a.refugee_status || 'H',
            consent_photo: a.consent_photo,
            consent_contact: a.consent_contact,
          };
          if (a.id) return axios.patch(`${API_ENDPOINTS.GROUP_REPORT_ATTENDANCE}${a.id}/`, ap, { headers: h });
          return axios.post(API_ENDPOINTS.GROUP_REPORT_ATTENDANCE, ap, { headers: h });
        }));
      }
      // Clear local draft after successful server save
      clearGrpDraft(currentGrpDraftKey.current);
      setGroupReportDialog(false);
      fetchGroupReports();
    } catch (err) {
      setGroupReportErrors(getErrorMessage(err, 'Failed to save group report.'));
    } finally {
      setGroupReportSaving(false);
    }
  };

  const isTeamLeadOf = (group) => group?.team_lead === myBgeId;


  // ── Member contributions ──────────────────────────────────────────────────
  const openContribution = async (groupReport) => {
    setContributionErrors('');
    // If I already have a contribution for this report, load it; otherwise blank
    try {
      const r = await axios.get(
        `${API_ENDPOINTS.GROUP_REPORT_CONTRIBUTIONS}?group_report=${groupReport.id}`,
        { headers }
      );
      const list = Array.isArray(r.data) ? r.data : (r.data.results || []);
      const mine = list.find(c => Number(c.bge) === Number(myBgeId));
      if (mine) {
        setContributionEditingId(mine.id);
        setContributionForm({
          group_report:        groupReport.id,
          msmes_observed:      mine.msmes_observed || [],
          notes:               mine.notes || '',
          challenges_observed: mine.challenges_observed || '',
          interventions_made:  mine.interventions_made || '',
          follow_up_needed:    mine.follow_up_needed || '',
        });
      } else {
        setContributionEditingId(null);
        setContributionForm({ ...EMPTY_CONTRIBUTION, group_report: groupReport.id });
      }
    } catch {
      setContributionEditingId(null);
      setContributionForm({ ...EMPTY_CONTRIBUTION, group_report: groupReport.id });
    }
    setContributionDialog(true);
  };

  const saveContribution = async () => {
    if (!contributionForm.group_report) { setContributionErrors('Missing group_report id'); return; }
    setContributionSaving(true);
    setContributionErrors('');
    try {
      if (contributionEditingId) {
        await axios.patch(
          `${API_ENDPOINTS.GROUP_REPORT_CONTRIBUTIONS}${contributionEditingId}/`,
          contributionForm, { headers }
        );
        notify('Contribution updated');
      } else {
        // POST acts as upsert on the backend (same (report, bge) pair → updates)
        await axios.post(API_ENDPOINTS.GROUP_REPORT_CONTRIBUTIONS, contributionForm, { headers });
        notify('Contribution saved');
      }
      setContributionDialog(false);
      fetchGroupReports();
    } catch (err) {
      setContributionErrors(getErrorMessage(err, 'Failed to save contribution.'));
    } finally {
      setContributionSaving(false);
    }
  };

  // ── PDF / print helpers ───────────────────────────────────────────────────
  // Open the server-rendered PDF for an MSME visit report. `mode='download'`
  // forces a file download (?dl=1); `mode='view'` opens it inline so the
  // browser's PDF viewer handles its own print/save UI.
  const openMsmeReportPdf = async (reportId, mode = 'view') => {
    try {
      const res = await axios.get(
        `${API_ENDPOINTS.REPORTS}${reportId}/pdf/${mode === 'download' ? '?dl=1' : ''}`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `MSMEReport_${reportId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank');
      }
      // Defer revoke so the new tab can read the URL
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      notify('Failed to render PDF', 'error');
    }
  };

  const openGroupReportPdf = async (reportId, mode = 'view') => {
    try {
      const res = await axios.get(
        `${API_ENDPOINTS.GROUP_REPORTS}${reportId}/pdf/${mode === 'download' ? '?dl=1' : ''}`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `GroupReport_${reportId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      notify('Failed to render PDF', 'error');
    }
  };

  const openMsmeDetail = async (msme) => {
    setSelectedMsme(msme);
    setMsmeDetailDialog(true);
    setMsmeDetailTab(0);
    setMsmeDetailSnapshots([]);
    try {
      const [rptRes, snapRes] = await Promise.allSettled([
        axios.get(`${API_ENDPOINTS.REPORTS}?msme=${msme.id}`, { headers }),
        axios.get(`${API_ENDPOINTS.GROWTH_SNAPSHOTS}?msme=${msme.id}`, { headers }),
      ]);
      setMsmeReports(rptRes.status === 'fulfilled'
        ? (Array.isArray(rptRes.value.data) ? rptRes.value.data : rptRes.value.data.results || [])
        : []);
      setMsmeDetailSnapshots(snapRes.status === 'fulfilled'
        ? (Array.isArray(snapRes.value.data) ? snapRes.value.data : snapRes.value.data.results || [])
        : []);
    } catch {
      setMsmeReports([]);
    }
  };

  // ── signature upload ──────────────────────────────────────────────────────
  const uploadSignature = async () => {
    if (!sigFile) return;
    setSigUploading(true);
    try {
      const bgeId = currentUser?.bge_profile?.id;
      const fd = new FormData();
      fd.append('signature', sigFile);
      const res = await axios.post(
        `${API_ENDPOINTS.EXPERTS}${bgeId}/upload-signature/`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      setSigUrl(res.data.signature_url);
      setSigFile(null);
      notify('Signature uploaded successfully');
    } catch (err) {
      notify(err.response?.data?.error || 'Signature upload failed', 'error');
    } finally {
      setSigUploading(false);
    }
  };

  // ── BGE base location update ──────────────────────────────────────────────
  const handleSaveBgeLocation = async () => {
    if (!myBgeId) return;
    try {
      setBgeLocationForm(f => ({ ...f, saving: true }));
      const payload = {
        location: bgeLocationForm.location,
        latitude: bgeLocationForm.latitude ? parseFloat(bgeLocationForm.latitude) : null,
        longitude: bgeLocationForm.longitude ? parseFloat(bgeLocationForm.longitude) : null,
      };
      const res = await axios.patch(`${API_ENDPOINTS.EXPERTS}${myBgeId}/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyBgeProfile(prev => ({ ...prev, ...res.data }));
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser && storedUser.bge_profile) {
          storedUser.bge_profile = { ...storedUser.bge_profile, ...res.data };
          localStorage.setItem('user', JSON.stringify(storedUser));
        }
      } catch {}
      setBgeLocationDialog(false);
      notify('Base GPS location updated successfully ✓');
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to update base location', 'error');
    } finally {
      setBgeLocationForm(f => ({ ...f, saving: false }));
    }
  };

  // ── work order sign + download ────────────────────────────────────────────
  const signWo = async (wo) => {
    if (!window.confirm(`Sign work order ${wo.work_order_number}? This confirms your acceptance and cannot be undone.`)) return;
    setWoSigning(wo.id);
    try {
      const res = await axios.post(WORK_ORDER_SIGN_URL(wo.id), {}, { headers });
      setWorkOrders(prev => prev.map(w => w.id === wo.id ? res.data : w));
      notify(`Work order ${wo.work_order_number} signed successfully`);
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to sign work order', 'error');
    } finally {
      setWoSigning(null);
    }
  };

  const downloadWoPdf = async (wo) => {
    try {
      const res = await axios.get(WORK_ORDER_PDF_URL(wo.id), { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WorkOrder_${(wo.work_order_number || wo.id).replace(/\s/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { notify('Failed to download PDF', 'error'); }
  };

  const reviewWo = async (wo) => {
    setWoReview(wo);
    setWoPdfBlob(null);
    try {
      const res = await axios.get(WORK_ORDER_PDF_URL(wo.id), { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setWoPdfBlob(url);
    } catch { notify('Failed to load work order PDF', 'error'); }
  };

  const closeReview = () => {
    if (woPdfBlob) URL.revokeObjectURL(woPdfBlob);
    setWoPdfBlob(null);
    setWoReview(null);
  };

  // ── BGE Payment Receipt Confirmation ──────────────────────────────────────
  const openBgePaymentConfirm = (item, type = 'work_order') => {
    setBgePaymentConfirmTarget(item);
    setBgePaymentConfirmType(type);
    setBgePaymentConfirmForm({
      reference: '',
      notes: '',
      payment_method: 'Mobile Money',
    });
    setBgePaymentConfirmDialog(true);
  };

  const handleBgePaymentConfirm = async () => {
    if (!bgePaymentConfirmTarget) return;
    setBgePaymentConfirming(true);
    try {
      let endpoint = '';
      if (bgePaymentConfirmType === 'work_order') {
        endpoint = WORK_ORDER_CONFIRM_PAYMENT_URL(bgePaymentConfirmTarget.id);
      } else if (bgePaymentConfirmType === 'group_report') {
        endpoint = GROUP_REPORT_CONFIRM_PAYMENT_URL(bgePaymentConfirmTarget.id);
      } else {
        endpoint = REPORT_CONFIRM_PAYMENT_URL(bgePaymentConfirmTarget.id);
      }

      const res = await axios.post(endpoint, {
        reference: bgePaymentConfirmForm.reference,
        notes: `[${bgePaymentConfirmForm.payment_method}] ${bgePaymentConfirmForm.notes || ''}`.trim(),
      }, { headers });

      if (bgePaymentConfirmType === 'work_order') {
        setWorkOrders(prev => prev.map(w => w.id === bgePaymentConfirmTarget.id ? res.data : w));
      } else if (bgePaymentConfirmType === 'group_report') {
        setGroupReports(prev => prev.map(g => g.id === bgePaymentConfirmTarget.id ? res.data : g));
      } else {
        setReports(prev => prev.map(r => r.id === bgePaymentConfirmTarget.id ? res.data : r));
      }

      notify('Payment receipt confirmed! Admin has been notified via email ✓', 'success');
      setBgePaymentConfirmDialog(false);
      setBgePaymentConfirmTarget(null);
    } catch (err) {
      notify(err.response?.data?.error || err.response?.data?.detail || 'Failed to confirm payment', 'error');
    } finally {
      setBgePaymentConfirming(false);
    }
  };

  // ── timesheet & invoice submissions ─────────────────────────────────────────
  const uploadSubmission = async (woId) => {
    if (!timesheetFile && !invoiceFile) { notify('Choose a timesheet and/or invoice file (.xlsx or .xls)', 'error'); return; }
    setSubmissionUploading(true);
    try {
      const form = new FormData();
      form.append('work_order', woId);
      if (timesheetFile) form.append('timesheet', timesheetFile);
      if (invoiceFile) form.append('invoice', invoiceFile);
      await axios.post(API_ENDPOINTS.WORK_ORDER_SUBMISSIONS, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      notify('Timesheet/invoice uploaded successfully');
      setTimesheetFile(null);
      setInvoiceFile(null);
      setActiveUploadWoId(null);
      if (timesheetInputRef.current) timesheetInputRef.current.value = '';
      if (invoiceInputRef.current) invoiceInputRef.current.value = '';
      fetchSubmissions();
      fetchPayments();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.error || errData?.detail ||
        (typeof errData === 'object' ? Object.values(errData).flat()[0] : null) ||
        'Failed to upload files';
      notify(String(msg), 'error');
    } finally {
      setSubmissionUploading(false);
    }
  };

  const deleteSubmission = async (sub) => {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_ENDPOINTS.WORK_ORDER_SUBMISSIONS}${sub.id}/`, { headers });
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
      notify('Submission deleted');
    } catch {
      notify('Failed to delete submission', 'error');
    }
  };

  const replaceSubmission = async (sub) => {
    if (!replaceTimesheetFile && !replaceInvoiceFile) {
      notify('Choose a new timesheet and/or invoice file first', 'error');
      return;
    }
    setReplaceUploading(true);
    try {
      const form = new FormData();
      if (replaceTimesheetFile) form.append('timesheet', replaceTimesheetFile);
      if (replaceInvoiceFile) form.append('invoice', replaceInvoiceFile);
      const res = await axios.patch(
        `${API_ENDPOINTS.WORK_ORDER_SUBMISSIONS}${sub.id}/`,
        form,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      setSubmissions(prev => prev.map(s => s.id === sub.id ? res.data : s));
      setReplacingSubId(null);
      setReplaceTimesheetFile(null);
      setReplaceInvoiceFile(null);
      if (replaceTimesheetRef.current) replaceTimesheetRef.current.value = '';
      if (replaceInvoiceRef.current) replaceInvoiceRef.current.value = '';
      notify('Submission updated');
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to update submission', 'error');
    } finally {
      setReplaceUploading(false);
    }
  };

  const downloadSubmissionFile = async (sub, kind) => {
    const url = kind === 'timesheet' ? WORK_ORDER_SUBMISSION_TIMESHEET_URL(sub.id) : WORK_ORDER_SUBMISSION_INVOICE_URL(sub.id);
    const filename = kind === 'timesheet' ? sub.timesheet_filename : sub.invoice_filename;
    try {
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || `${kind}.xlsx`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      notify(`Failed to download ${kind}`, 'error');
    }
  };

  // ── work order attachments ────────────────────────────────────────────────────
  const uploadAttachment = async (woId) => {
    if (!attachFile) { notify('Choose a file to attach', 'error'); return; }
    setAttachUploading(true);
    try {
      const form = new FormData();
      form.append('work_order', woId);
      form.append('file_upload', attachFile);
      if (attachCaption.trim()) form.append('caption', attachCaption.trim());
      const res = await axios.post(API_ENDPOINTS.WORK_ORDER_ATTACHMENTS, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      notify('Document attached successfully');
      if (res.data) {
        setAttachments(prev => [res.data, ...prev.filter(a => a.id !== res.data.id)]);
      }
      setAttachFile(null);
      setAttachCaption('');
      setActiveAttachWoId(null);
      if (attachInputRef.current) attachInputRef.current.value = '';
      await fetchAttachments();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.detail || (typeof errData === 'object' ? Object.values(errData).flat()[0] : null) || 'Failed to upload attachment';
      notify(String(msg), 'error');
    } finally {
      setAttachUploading(false);
    }
  };


  const openAttachment = async (att) => {
    try {
      const res = await axios.get(WORK_ORDER_ATTACHMENT_DOWNLOAD_URL(att.id), {
        headers, responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
    } catch {
      notify('Failed to open attachment', 'error');
    }
  };

  // ── payments ─────────────────────────────────────────────────────────────────
  const recordPayment = async (woId) => {
    if (!paymentForm.payment_date || !paymentForm.amount) {
      notify('Date received and amount are required', 'error');
      return;
    }
    setPaymentSaving(true);
    try {
      const body = {
        work_order: woId,
        payment_date: paymentForm.payment_date,
        amount: paymentForm.amount,
      };
      if (paymentForm.balance !== '') body.balance = paymentForm.balance;
      const res = await axios.post(API_ENDPOINTS.WORK_ORDER_PAYMENTS, body, { headers });
      setPayments(prev => [...prev, res.data]);
      setActivePaymentWoId(null);
      setPaymentForm({ payment_date: '', amount: '', balance: '' });
      notify('Payment recorded');
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.error || errData?.detail ||
        (typeof errData === 'object' ? Object.values(errData).flat()[0] : null) ||
        'Failed to record payment';
      notify(String(msg), 'error');
    } finally {
      setPaymentSaving(false);
    }
  };

  // ── Enterprise Improvement Plans (TBIP) ──────────────────────────────────
  const handleDownloadTbipPdf = async (planObj) => {
    try {
      const res = await axios.get(TBIP_PDF_URL(planObj.id), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TBIP_${planObj.msme_name || 'MSME'}_${planObj.assessment_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      notify('Failed to download PDF', 'error');
    }
  };

  const handleDownloadTbipExcel = async (planObj) => {
    try {
      const res = await axios.get(TBIP_EXCEL_URL(planObj.id), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TBIP_${planObj.msme_name || 'MSME'}_${planObj.assessment_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      notify('Failed to download Excel workbook', 'error');
    }
  };

  const deleteTbipPlan = async (planObj) => {
    if (!window.confirm(`Delete draft assessment for ${planObj.msme_name || 'this MSME'}?`)) return;
    try {
      await axios.delete(`${API_ENDPOINTS.ENTERPRISE_IMPROVEMENT_PLANS}${planObj.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify('Draft plan deleted.', 'success');
      fetchTbipPlans();
    } catch {
      notify('Failed to delete draft plan.', 'error');
    }
  };

  // ── Financial Improvement Action Plans (FIAP) ───────────────────────────
  const handleDownloadFiapPdf = async (planObj) => {
    try {
      const res = await axios.get(FIAP_PDF_URL(planObj.id), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FIAP_${(planObj.msme_name || 'MSME').replace(/\s+/g, '_')}_${planObj.assessment_date || 'action_plan'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      notify('Failed to download Financial Action Plan PDF', 'error');
    }
  };

  const deleteFiapPlan = async (planObj) => {
    if (!window.confirm(`Delete draft Financial Action Plan for ${planObj.msme_name || 'this MSME'}?`)) return;
    try {
      await axios.delete(`${API_ENDPOINTS.FINANCIAL_IMPROVEMENT_PLANS}${planObj.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify('Draft Financial Action Plan deleted.', 'success');
      fetchFiapPlans();
    } catch {
      notify('Failed to delete draft plan.', 'error');
    }
  };


  // ── sidebar ─────────────────────────────────────────────────────────────────
  const navItems = [
    { key: 'msmes',       label: 'My MSMEs',      icon: <Business /> },
    { key: 'calendar',    label: 'Visit Planner', icon: <CalendarMonth /> },
    { key: 'maps',        label: 'MSME Maps',     icon: <Place /> },
    { key: 'groups',      label: 'My Groups',      icon: <GroupIcon /> },
    { key: 'reports',     label: 'My Reports',     icon: <Assignment /> },
    { key: 'tbip',        label: 'Improvement Plan', icon: <TrendingUp />,
      badge: tbipPlans.filter(p => p.status === 'draft').length || undefined },
    { key: 'fiap',        label: 'Financial Action Plans', icon: <AccountBalance />,
      badge: fiapPlans.filter(p => p.status === 'draft').length || undefined },
    { key: 'workorders',  label: 'Work Orders',    icon: <Description /> },
    { key: 'training',   label: 'Training',       icon: <School />,
      badge: leadSessions.length + mentorSessions.length + participantSessions.length || undefined },
    { key: 'tshirts',    label: 'T-Shirts',       icon: <Checkroom />,
      badge: tshirtEntries.filter(e => !e.signed).length || undefined },
  ];

  const SidebarContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: BRAND.sidebarBg }}>
      {/* GOPA Pro & GIZ dual brand accent stripe */}
      <Box sx={{
        height: 3, flexShrink: 0,
        background: `linear-gradient(90deg, ${BRAND.gopaGold} 0%, ${BRAND.gopaGold} 52%, ${BRAND.gizRed} 52%, ${BRAND.gizRed} 100%)`,
      }} />
      {/* Close button — mobile only */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', px: 1, pt: 1 }}>
        <IconButton size="small" onClick={() => setMobileOpen(false)}
          sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5, bgcolor: BRAND.gizRed,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(200,16,46,0.3)',
          }}>
            <Typography sx={{ fontWeight: 900, fontSize: 10, color: '#fff', lineHeight: 1 }}>GIZ</Typography>
          </Box>
          <Box sx={{
            width: 44, height: 32, borderRadius: 1.5, bgcolor: BRAND.gopaGold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(243,187,54,0.3)',
          }}>
            <Typography sx={{ fontWeight: 900, fontSize: 8.5, color: BRAND.primaryMain, lineHeight: 1.05, textAlign: 'center' }}>
              GOPA<br/><span style={{ fontSize: 7.5, fontWeight: 800 }}>Pro</span>
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13, mt: 1 }}>PRUDEV II</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>BGE Portal</Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      <List sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 1,
        pt: 1,
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 8 },
      }}>
        {navItems.map(({ key, label, icon, badge }) => (
          <ListItemButton
            key={key}
            selected={section === key}
            onClick={() => { startTransition(() => setSection(key)); setMobileOpen(false); }}
            sx={{
              borderRadius: 2, mb: 0.5, color: 'rgba(255,255,255,0.75)',
              transition: 'all 0.15s ease',
              '&.Mui-selected': {
                bgcolor: BRAND.sidebarSelected,
                color: '#fff',
                borderLeft: `3px solid ${BRAND.gopaGold}`,
                '& .MuiListItemIcon-root': { color: BRAND.gopaGold },
                fontWeight: 600,
              },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              {badge ? <Badge badgeContent={badge} color="warning">{icon}</Badge> : icon}
            </ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: BRAND.programmeGreen }}>
            {bgeName[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }} noWrap>{bgeName}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>BGE</Typography>
          </Box>
        </Box>
        <Button
          variant="outlined" size="small" startIcon={<HelpOutline />}
          onClick={() => { setHelpSection(0); setHelpDialog(true); setMobileOpen(false); }}
          fullWidth
          sx={{
            color: 'rgba(255,255,255,0.82)',
            borderColor: 'rgba(255,255,255,0.22)',
            mb: 1,
            '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
            fontSize: 12,
          }}
        >
          Help
        </Button>
        <Button
          variant="outlined" size="small" startIcon={<Logout />} onClick={onLogout} fullWidth
          sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)',
            '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' }, fontSize: 12 }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );

  // ── main content ─────────────────────────────────────────────────────────────
  const paged = (arr, page) => arr.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', overflowX: 'hidden' }}>
      {/* mobile appbar */}
      <AppBar position="fixed" sx={{
        display: { md: 'none' }, bgcolor: BRAND.sidebarBg,
        zIndex: (t) => t.zIndex.drawer + 1, boxShadow: 'none',
        borderBottom: `2px solid ${BRAND.gopaGold}`,
      }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => setMobileOpen(o => !o)}
            sx={{ mr: 1, p: 1.25, touchAction: 'manipulation' }} aria-label="Open navigation menu">
            <MenuIcon />
          </IconButton>
          <Typography fontWeight={700} fontSize={15} noWrap sx={{ flex: 1 }}>PRUDEV II · BGE Portal</Typography>
          <Tooltip title="Help">
            <IconButton color="inherit" onClick={() => { setHelpSection(0); setHelpDialog(true); }} edge="end">
              <HelpOutline />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* mobile drawer */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        keepMounted disableScrollLock
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, maxWidth: '86vw', height: '100dvh', boxSizing: 'border-box', border: 'none' } }}>
        <SidebarContent />
      </Drawer>

      {/* desktop sidebar */}
      <Box sx={{ width: DRAWER_WIDTH, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ width: DRAWER_WIDTH, position: 'fixed', top: 0, bottom: 0 }}>
          <SidebarContent />
        </Box>
      </Box>

      {/* main */}
      <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 3 }, mt: { xs: 7, md: 0 }, minWidth: 0, overflowX: 'hidden', pb: { xs: 10, md: 4 } }}>
        {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 4 }} />}

        {/* ── Visit Planner / Calendar ── */}
        {section === 'calendar' && (
          <CalendarPlanner
            token={token}
            currentUser={currentUser}
            currentBge={currentUser?.bge_profile}
            msmes={msmes.filter(m => m.assigned_bge === myBgeId || (m.co_assigned_bge_names || []).some(b => b.id === myBgeId))}
            experts={currentUser?.bge_profile ? [currentUser.bge_profile] : []}
            onViewMsme={(mId) => {
              const target = msmes.find(m => m.id === mId);
              if (target) { setSelectedMsme(target); setMsmeDetailDialog(true); }
            }}
            onOpenNewReport={(msmeId, type) => {
              openNewReport(msmeId, type);
            }}
          />
        )}

        {/* ── My MSMEs (direct + co-assigned) ── */}
        {section === 'msmes' && !loading && (() => {
          const directMsmes = msmes.filter(m => m.assigned_bge === myBgeId);
          const coAssignedMsmes = msmes.filter(m =>
            m.assigned_bge !== myBgeId &&
            (m.co_assigned_bge_names || []).some(b => b.id === myBgeId)
          );

          // ── Monthly visit tracking ──────────────────────────────────────
          const VISIT_TARGET = 3;
          const now = new Date();
          const nowYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const monthLabel = now.toLocaleString('default', { month: 'long' });

          const visitsThisMonthFor = (msmeId) =>
            reports.filter(r => r.msme === msmeId && (r.visit_date || '').startsWith(nowYM)).length;

          const visitDotColor = (count) =>
            count >= VISIT_TARGET ? '#2e7d32' : count >= 1 ? '#f57c00' : '#bdbdbd';

          const VisitTracker = ({ msmeId }) => {
            const count = visitsThisMonthFor(msmeId);
            const color = visitDotColor(count);
            const dots = Math.max(VISIT_TARGET, count); // show extra dots if over target
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 82 }}>
                  {monthLabel} visits
                </Typography>
                <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  {Array.from({ length: dots }, (_, i) => (
                    <Box key={i} sx={{
                      width: 11, height: 11, borderRadius: '50%',
                      bgcolor: i < count ? color : 'grey.300',
                      border: `1.5px solid ${i < count ? color : '#ccc'}`,
                      flexShrink: 0,
                    }} />
                  ))}
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ color }}>
                  {count}/{VISIT_TARGET}{count >= VISIT_TARGET ? ' ✓' : ''}
                </Typography>
              </Box>
            );
          };

          // Month-level summary across all direct MSMEs
          const metTarget   = directMsmes.filter(m => visitsThisMonthFor(m.id) >= VISIT_TARGET).length;
          const partialVisit = directMsmes.filter(m => { const v = visitsThisMonthFor(m.id); return v > 0 && v < VISIT_TARGET; }).length;
          const notVisited  = directMsmes.filter(m => visitsThisMonthFor(m.id) === 0).length;

          return (
            <Box>
              {/* Responsive header */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>My MSMEs</Typography>
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ color: BRAND.primaryMain, fontWeight: 600 }}>
                    {directMsmes.length}
                  </Box>
                  {' '}directly assigned to you
                  {coAssignedMsmes.length > 0 && (
                    <><Box component="span" sx={{ mx: 0.5 }}>&middot;</Box>
                    <Box component="span" sx={{ color: BRAND.primaryMain, fontWeight: 600 }}>
                      {coAssignedMsmes.length}
                    </Box>{' '}co-assigned to you</>
                  )}
                  {' '}· group MSMEs are in{' '}
                  <Box component="span"
                    sx={{ color: '#F9A825', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => startTransition(() => setSection('groups'))}>
                    My Groups
                  </Box>
                </Typography>
              </Box>

              {/* Monthly visit summary banner (direct MSMEs only) */}
              {directMsmes.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ mr: 0.5 }}>
                    {monthLabel} visit progress
                  </Typography>
                  <Chip size="small" label={`${metTarget} on track`}
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                  {partialVisit > 0 && (
                    <Chip size="small" label={`${partialVisit} in progress`}
                      sx={{ bgcolor: '#fff3e0', color: '#f57c00', fontWeight: 600 }} />
                  )}
                  {notVisited > 0 && (
                    <Chip size="small" label={`${notVisited} not visited`}
                      sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 600 }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    Target: {VISIT_TARGET} visits / MSME / month
                  </Typography>
                </Paper>
              )}

              {/* Active work order period banner */}
              {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const active = workOrders.find(wo =>
                  wo.start_date && wo.end_date &&
                  wo.start_date <= today && wo.end_date >= today
                );
                const upcoming = !active && workOrders.find(wo =>
                  wo.start_date && wo.start_date > today
                );
                const wo = active || upcoming;
                if (!wo) return null;
                return (
                  <Alert
                    severity={active ? 'info' : 'warning'}
                    icon={false}
                    sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
                          {active ? 'Active Assignment Period' : 'Upcoming Assignment'}
                          {' · '}{wo.work_order_number}
                        </Typography>
                        <Typography variant="caption">
                          {wo.work_order_type_display} · {wo.start_date} – {wo.end_date}
                          {' · '}{wo.location}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                          Your visit reports should fall within this date range.
                        </Typography>
                      </Box>
                      <Chip
                        label={active ? 'Active' : 'Upcoming'}
                        size="small"
                        color={active ? 'info' : 'warning'}
                        variant="filled"
                      />
                    </Box>
                  </Alert>
                );
              })()}

              {directMsmes.length === 0 && coAssignedMsmes.length === 0 ? (
                <Paper sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center' }}>
                  <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No MSMEs assigned to you yet. Contact your programme administrator.</Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  {directMsmes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Directly assigned MSMEs
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {directMsmes.map((m) => {
                          const msmeReportCount = reports.filter(r => r.msme === m.id).length;
                          const lastIndividual = reports.filter(r => r.msme === m.id).sort((a, b) => b.visit_date > a.visit_date ? 1 : -1)[0];
                          const lastSupportDate = m.last_support_date || lastIndividual?.visit_date;
                          const totalSupports = m.total_reports || msmeReportCount;
                          return (
                            <Card key={`direct-${m.id}`}
                              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 }, borderLeft: `4px solid ${BRAND.primaryMain}` }}
                              onClick={() => openMsmeDetail(m)}
                            >
                              <CardContent>
                                {/* Row 1: name + type + action buttons */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography fontWeight={700} fontSize={15}>{m.business_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{m.msme_code}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                    <Chip label={m.business_type || 'MSME'} size="small" variant="outlined" />
                                    {m.status && m.status !== 'active' && (() => {
                                      const sInfo = getMsmeStatusInfo(m.status);
                                      return (
                                        <Chip
                                          label={sInfo.label}
                                          size="small"
                                          sx={{
                                            bgcolor: sInfo.bgcolor,
                                            color: sInfo.textColor,
                                            border: `1px solid ${sInfo.border}`,
                                            fontWeight: 600,
                                            fontSize: 10,
                                            height: 20,
                                          }}
                                        />
                                      );
                                    })()}
                                    <Tooltip title="View details & reports">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openMsmeDetail(m); }}>
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit MSME status, location & contacts">
                                      <IconButton size="small" sx={{ color: BRAND.primaryMain }} onClick={(e) => { e.stopPropagation(); openEditMsme(m); }}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="New visit report">
                                      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openNewReport(m.id); }}>
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Quick visit check-in: GPS + revenue + employees">
                                      <IconButton size="small" sx={{ color: BRAND.gopaGoldDark }} onClick={(e) => { e.stopPropagation(); openQuickUpdate(m); }}>
                                        <MyLocation fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Full growth data update">
                                      <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openGrowthForm(m); }}>
                                        <TrendingUp fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Enterprise Improvement Plan (TBIP)">
                                      <IconButton
                                        size="small"
                                        sx={{ color: '#B45309' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const existing = tbipPlans.find(p => p.msme === m.id);
                                          setSelectedTbipMsme(m);
                                          setSelectedTbip(existing || null);
                                          setTbipDialog(true);
                                        }}
                                      >
                                        <Assignment fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Financial Improvement Action Plan (FIAP)">
                                      <IconButton
                                        size="small"
                                        sx={{ color: '#1E3A8A' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const existing = fiapPlans.find(p => p.msme === m.id);
                                          setSelectedFiapMsme(m);
                                          setSelectedFiap(existing || null);
                                          setFiapDialog(true);
                                        }}
                                      >
                                        <AccountBalance fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Request FIAP Approval Meeting with Rosemary or Harriet">
                                      <IconButton
                                        size="small"
                                        sx={{ color: '#1E3A8A' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const existing = fiapPlans.find(p => p.msme === m.id);
                                          setFiapBookMeetingMsme(m);
                                          setFiapBookMeetingPlan(existing || null);
                                          setFiapBookMeetingOpen(true);
                                        }}
                                      >
                                        <EventAvailable fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>

                                {/* Row 2: key fields grid */}
                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                  {m.owner_name && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Owner</Typography>
                                      <Typography variant="body2" fontWeight={500}>{m.owner_name}</Typography>
                                    </Grid>
                                  )}
                                  {m.phone && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                                      <Typography variant="body2">{m.phone}</Typography>
                                    </Grid>
                                  )}
                                  {(m.city || m.district || m.state) && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
                                      <Typography variant="body2">{[m.city, m.district || m.state].filter(Boolean).join(', ')}</Typography>
                                    </Grid>
                                  )}
                                  {m.sector && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Sector</Typography>
                                      <Typography variant="body2">{m.sector}</Typography>
                                    </Grid>
                                  )}
                                  {m.assignment_date && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Assigned</Typography>
                                      <Typography variant="body2">{m.assignment_date}</Typography>
                                    </Grid>
                                  )}
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Supports</Typography>
                                    <Typography variant="body2" fontWeight={600} color={totalSupports > 0 ? 'primary.main' : 'text.secondary'}>
                                      {totalSupports} total{lastSupportDate ? ` · last ${lastSupportDate}` : ''}
                                    </Typography>
                                  </Grid>
                                  {(m.alt_contact_name || m.alt_phone) && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>
                                        Alt Contact: <strong style={{ color: '#37474F' }}>{[m.alt_contact_name, m.alt_contact_role ? `(${m.alt_contact_role})` : '', m.alt_phone].filter(Boolean).join(' · ')}</strong>
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Monthly visit tracker */}
                                <VisitTracker msmeId={m.id} />

                                {/* Assignment objectives (truncated) */}
                                {m.assignment_objectives && (
                                  <Alert severity="info" icon={false} sx={{ py: 0.5, mt: 0.5 }}>
                                    <Typography variant="caption" fontWeight={600} display="block">Assignment objective</Typography>
                                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {m.assignment_objectives.length > 180
                                        ? m.assignment_objectives.slice(0, 180) + '…'
                                        : m.assignment_objectives}
                                    </Typography>
                                  </Alert>
                                )}

                                {/* Chips row */}
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                  {m.cohort_name && (
                                    <Chip label={`Cohort ${m.cohort_name}`} size="small"
                                      sx={{ bgcolor: BRAND.programmeGreen + '20', color: BRAND.programmeGreen, fontWeight: 600 }} />
                                  )}
                                  {m.session_number && (
                                    <Chip label={`Session ${m.session_number}`} size="small" variant="outlined" />
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {coAssignedMsmes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Co-assigned MSMEs
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {coAssignedMsmes.map((m) => {
                          const msmeReportCount = reports.filter(r => r.msme === m.id).length;
                          const lastIndividual = reports.filter(r => r.msme === m.id).sort((a, b) => b.visit_date > a.visit_date ? 1 : -1)[0];
                          const lastSupportDate = m.last_support_date || lastIndividual?.visit_date;
                          const totalSupports = m.total_reports || msmeReportCount;
                          return (
                            <Card key={`coassigned-${m.id}`}
                              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 }, borderLeft: `4px solid ${BRAND.warningMain}` }}
                              onClick={() => openMsmeDetail(m)}
                            >
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography fontWeight={700} fontSize={15}>{m.business_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{m.msme_code}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                    <Chip label={m.business_type || 'MSME'} size="small" variant="outlined" />
                                    <Chip label="Co-assigned" size="small" color="warning" />
                                    {m.status && m.status !== 'active' && (() => {
                                      const sInfo = getMsmeStatusInfo(m.status);
                                      return (
                                        <Chip
                                          label={sInfo.label}
                                          size="small"
                                          sx={{
                                            bgcolor: sInfo.bgcolor,
                                            color: sInfo.textColor,
                                            border: `1px solid ${sInfo.border}`,
                                            fontWeight: 600,
                                            fontSize: 10,
                                            height: 20,
                                          }}
                                        />
                                      );
                                    })()}
                                    <Tooltip title="View details & reports">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openMsmeDetail(m); }}>
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit MSME status, location & contacts">
                                      <IconButton size="small" sx={{ color: BRAND.primaryMain }} onClick={(e) => { e.stopPropagation(); openEditMsme(m); }}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="New visit report">
                                      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openNewReport(m.id); }}>
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Quick visit check-in: GPS + revenue + employees">
                                      <IconButton size="small" sx={{ color: BRAND.gopaGoldDark }} onClick={(e) => { e.stopPropagation(); openQuickUpdate(m); }}>
                                        <MyLocation fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Full growth data update">
                                      <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openGrowthForm(m); }}>
                                        <TrendingUp fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>

                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                  {m.owner_name && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Owner</Typography>
                                      <Typography variant="body2" fontWeight={500}>{m.owner_name}</Typography>
                                    </Grid>
                                  )}
                                  {m.phone && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                                      <Typography variant="body2">{m.phone}</Typography>
                                    </Grid>
                                  )}
                                  {(m.city || m.district || m.state) && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
                                      <Typography variant="body2">{[m.city, m.district || m.state].filter(Boolean).join(', ')}</Typography>
                                    </Grid>
                                  )}
                                  {m.sector && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Sector</Typography>
                                      <Typography variant="body2">{m.sector}</Typography>
                                    </Grid>
                                  )}
                                  {m.assignment_date && (
                                    <Grid item xs={6} sm={3}>
                                      <Typography variant="caption" color="text.secondary" display="block">Assigned</Typography>
                                      <Typography variant="body2">{m.assignment_date}</Typography>
                                    </Grid>
                                  )}
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Supports</Typography>
                                    <Typography variant="body2" fontWeight={600} color={totalSupports > 0 ? 'primary.main' : 'text.secondary'}>
                                      {totalSupports} total{lastSupportDate ? ` · last ${lastSupportDate}` : ''}
                                    </Typography>
                                  </Grid>
                                  {(m.alt_contact_name || m.alt_phone) && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>
                                        Alt Contact: <strong style={{ color: '#37474F' }}>{[m.alt_contact_name, m.alt_contact_role ? `(${m.alt_contact_role})` : '', m.alt_phone].filter(Boolean).join(' · ')}</strong>
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Monthly visit tracker */}
                                <VisitTracker msmeId={m.id} />

                                {m.assignment_objectives && (
                                  <Alert severity="info" icon={false} sx={{ py: 0.5, mt: 0.5 }}>
                                    <Typography variant="caption" fontWeight={600} display="block">Assignment objective</Typography>
                                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {m.assignment_objectives.length > 180
                                        ? m.assignment_objectives.slice(0, 180) + '…'
                                        : m.assignment_objectives}
                                    </Typography>
                                  </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                  {m.cohort_name && (
                                    <Chip label={`Cohort ${m.cohort_name}`} size="small"
                                      sx={{ bgcolor: BRAND.programmeGreen + '20', color: BRAND.programmeGreen, fontWeight: 600 }} />
                                  )}
                                  {m.session_number && (
                                    <Chip label={`Session ${m.session_number}`} size="small" variant="outlined" />
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* ── Northern Uganda Spatial Map ── */}
        {section === 'maps' && !loading && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Northern Uganda MSME Spatial Map</Typography>
                <Typography variant="caption" color="text.secondary">
                  Spatial view of your assigned and co-assigned MSMEs across Northern Uganda
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<Place sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setBgeLocationForm({
                    location: myBgeProfile?.location || '',
                    latitude: myBgeProfile?.latitude || '',
                    longitude: myBgeProfile?.longitude || '',
                    loading: false,
                    saving: false,
                  });
                  setBgeLocationDialog(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {myBgeProfile?.latitude ? '📍 My Base GPS Pin: Set ✓' : '📍 Set My Base GPS Pin'}
              </Button>
            </Box>
            <MSMEMap
              msmes={msmes.filter(m => m.assigned_bge === myBgeId || (m.co_assigned_bge_names || []).some(b => b.id === myBgeId))}
              experts={myBgeProfile ? [myBgeProfile] : []}
              onOpenMsme={openMsmeDetail}
            />
          </Box>
        )}

        {/* ── My Groups ── */}
        {section === 'groups' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>My Groups</Typography>
                <Typography variant="body2" color="text.secondary">
                  {groups.length} group{groups.length !== 1 ? 's' : ''} you're a member of
                </Typography>
              </Box>
            </Box>

            {/* Active work order period banner */}
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const active = workOrders.find(wo =>
                wo.start_date && wo.end_date &&
                wo.start_date <= today && wo.end_date >= today
              );
              const upcoming = !active && workOrders.find(wo =>
                wo.start_date && wo.start_date > today
              );
              const wo = active || upcoming;
              if (!wo) return null;
              return (
                <Alert
                  severity={active ? 'info' : 'warning'}
                  icon={false}
                  sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
                        {active ? 'Active Assignment Period' : 'Upcoming Assignment'}
                        {' · '}{wo.work_order_number}
                      </Typography>
                      <Typography variant="caption">
                        {wo.work_order_type_display} · {wo.start_date} – {wo.end_date}
                        {' · '}{wo.location}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                        Your group reports should fall within this date range.
                      </Typography>
                    </Box>
                    <Chip
                      label={active ? 'Active' : 'Upcoming'}
                      size="small"
                      color={active ? 'info' : 'warning'}
                      variant="filled"
                    />
                  </Box>
                </Alert>
              );
            })()}

            {groups.length === 0 && (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                You're not currently part of any BGE group.
              </Paper>
            )}

            <Grid container spacing={2}>
              {groups.map(g => {
                const groupMsmes = msmes.filter(m => m.assigned_group === g.id);
                const youAreLead = isTeamLeadOf(g);
                const reports = groupReports.filter(r => r.group === g.id);
                return (
                  <Grid item xs={12} key={g.id}>
                    <Card variant="outlined">
                      <CardContent>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight={700}>{g.name}</Typography>
                              {youAreLead && (
                                <Chip icon={<Star sx={{ fontSize: 14 }} />} label="You are the team lead" color="warning" size="small" />
                              )}
                            </Box>
                            {g.description && (
                              <Typography variant="caption" color="text.secondary">{g.description}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Team Lead: <strong>{g.team_lead_name || 'Not assigned'}</strong> · {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          {youAreLead && (
                            <Button variant="contained" size="small" startIcon={<Add />}
                                    onClick={() => openNewGroupReport(g.id)}>
                              File Group Report
                            </Button>
                          )}
                        </Box>

                        {/* Objectives banner */}
                        {g.objectives && (
                          <Alert severity="info" icon={<Assignment fontSize="small" />} sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight={600} display="block">Group objectives</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{g.objectives}</Typography>
                          </Alert>
                        )}

                        {/* Members */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {(g.members_detail || []).map(m => (
                            <Chip
                              key={m.id}
                              label={m.name}
                              size="small"
                              icon={m.id === g.team_lead ? <Star sx={{ fontSize: 14 }} /> : undefined}
                              color={m.id === g.team_lead ? 'warning' : (m.id === myBgeId ? 'primary' : 'default')}
                              variant={m.id === myBgeId ? 'filled' : 'outlined'}
                            />
                          ))}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Assigned MSMEs — full scrollable list */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <GroupIcon sx={{ fontSize: 16, color: '#F9A825' }} />
                          <Typography variant="caption" fontWeight={600}>
                            Assigned MSMEs ({groupMsmes.length})
                          </Typography>
                        </Box>
                        {groupMsmes.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No MSMEs assigned to this group yet.</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 240, overflow: 'auto' }}>
                            {groupMsmes.map(m => (
                              <Box key={m.id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1, py: 0.75, borderRadius: 1, bgcolor: 'background.default',
                              }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>{m.business_name}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {m.msme_code}{m.session_number ? ` · Session ${m.session_number}` : ''}{m.city ? ` · ${m.city}` : ''}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                  <Tooltip title="View details">
                                    <IconButton size="small" onClick={() => openMsmeDetail(m)}>
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit MSME status, location & contacts">
                                    <IconButton size="small" sx={{ color: BRAND.primaryMain }} onClick={(e) => { e.stopPropagation(); openEditMsme(m); }}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="New visit report">
                                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openNewReport(m.id); }}>
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Quick visit check-in: GPS + revenue + employees">
                                    <IconButton size="small" sx={{ color: BRAND.gopaGoldDark }} onClick={(e) => { e.stopPropagation(); openQuickUpdate(m); }}>
                                      <MyLocation fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Full growth data update">
                                    <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openGrowthForm(m); }}>
                                      <TrendingUp fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Group reports already filed */}
                        {reports.length > 0 && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                              Group reports ({reports.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {reports.map(r => {
                                const finalized = r.status === 'submitted' || r.status === 'approved';
                                return (
                                <Box key={r.id} sx={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  p: 1, borderRadius: 1,
                                  bgcolor: finalized ? '#f5f5f5' : 'background.default',
                                  opacity: finalized ? 0.8 : 1,
                                }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500} color={finalized ? 'text.secondary' : 'inherit'}>
                                      {r.visit_date}{r.session_number ? ` · Session ${r.session_number}` : ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {r.msme_count} MSME{r.msme_count !== 1 ? 's' : ''} · by {r.team_lead_name || '—'}
                                    </Typography>
                                    {r.status === 'submitted' && (
                                      <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 600 }}>
                                        Assignment report submitted — locked
                                      </Typography>
                                    )}
                                    {r.status === 'approved' && (
                                      <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 600 }}>
                                        Assignment completed ✓
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      label={r.status === 'approved' ? 'Completed' : r.status}
                                      size="small"
                                      color={r.status === 'approved' ? 'success' : (r.status === 'submitted' ? 'primary' : 'default')}
                                    />
                                    <Tooltip title="View PDF">
                                      <IconButton size="small" onClick={() => openGroupReportPdf(r.id, 'view')}><Visibility fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                      <IconButton size="small" onClick={() => openGroupReportPdf(r.id, 'download')}><PictureAsPdf fontSize="small" /></IconButton>
                                    </Tooltip>
                                    {youAreLead && !finalized && (
                                      <Tooltip title="Edit report">
                                        <IconButton size="small" onClick={() => openEditGroupReport(r)}><Edit fontSize="small" /></IconButton>
                                      </Tooltip>
                                    )}
                                    {!youAreLead && !finalized && (
                                      <Tooltip title="File my contribution">
                                        <IconButton size="small" color="warning" onClick={() => openContribution(r)}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                                );
                              })}
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ── My Reports ── */}
        {section === 'reports' && (
          <Box>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1.5, sm: 2 },
              mb: 3,
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700}>My Reports</Typography>
                <Typography variant="body2" color="text.secondary">{reports.length} report{reports.length !== 1 ? 's' : ''} submitted</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openNewReport()}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                New Report
              </Button>
            </Box>

            {reports.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No reports yet. Start by writing a report for one of your MSMEs.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Table size="small" sx={{ minWidth: 680 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>MSME</TableCell>
                      <TableCell>Visit Type</TableCell>
                      <TableCell>Visit Date</TableCell>
                      <TableCell>Latest Growth Update</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged(reports, reportPage).map((r) => {
                      const msmeSnaps = msmes.find(m => m.id === r.msme);
                      const lastSnap = msmeSnaps?.last_growth_snapshot;
                      return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={600}>{r.msme_name || r.msme}</Typography>
                          <Typography fontSize={11} color="text.secondary">{r.msme_code}</Typography>
                        </TableCell>
                        <TableCell>{VISIT_TYPE_LABELS[r.visit_type] || r.visit_type}</TableCell>
                        <TableCell>{r.visit_date}</TableCell>
                        <TableCell>
                          {lastSnap ? (
                            <Box>
                              <Typography fontSize={12} fontWeight={600}>{lastSnap.snapshot_date}</Typography>
                              {lastSnap.annual_turnover && (
                                <Typography fontSize={11} color="text.secondary">
                                  Rev: UGX {Number(lastSnap.annual_turnover).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography fontSize={11} color="text.disabled">No data</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            <Chip label={r.status} size="small" color={STATUS_COLORS[r.status] || 'default'} />
                            {r.payment_status === 'submitted' && (
                              <Tooltip title={`Submitted for payment: ${r.payment_reference || 'N/A'}`}>
                                <Chip label={r.payment_reference ? `Submitted: ${r.payment_reference}` : 'Submitted for Payment'} size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontSize: 10, height: 20 }} />
                              </Tooltip>
                            )}
                            {(r.payment_status === 'confirmed' || r.payment_confirmed_by_bge) && (
                              <Tooltip title={`Payment confirmed on ${r.payment_confirmed_at ? r.payment_confirmed_at.slice(0, 10) : ''}`}>
                                <Chip label="Payment Confirmed ✓" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontSize: 10, height: 20 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {r.payment_status === 'submitted' && !r.payment_confirmed_by_bge && (
                              <Tooltip title="Confirm Payment Received">
                                <Button
                                  variant="outlined" size="small" color="success"
                                  startIcon={<CheckCircle fontSize="small" />}
                                  onClick={() => openBgePaymentConfirm(r, 'msme_report')}
                                  sx={{ fontSize: 11, py: 0.25, height: 26, textTransform: 'none' }}
                                >
                                  Confirm Payment
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip title="View report">
                              <IconButton size="small" color="primary"
                                onClick={() => setViewReport({ ...r, _msme: msmes.find(m => m.id === r.msme), _bgeName: bgeName })}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {r.status === 'draft' ? (
                              <Tooltip title="Edit draft">
                                <IconButton size="small" onClick={() => openEditReport(r)}><Edit fontSize="small" /></IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Submitted — read only">
                                <span><IconButton size="small" disabled><Edit fontSize="small" /></IconButton></span>
                              </Tooltip>
                            )}
                            <Tooltip title="Download PDF not available">
                              <span>
                                <IconButton size="small" color="error" disabled>
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {r.status === 'draft' && (
                              <Tooltip title="Delete draft">
                                <IconButton size="small" color="error" onClick={() => deleteReport(r)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div" count={reports.length} page={reportPage}
                  rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                  onPageChange={(_, p) => setReportPage(p)}
                />
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── Enterprise Improvement Plans (TBIP) ── */}
        {section === 'tbip' && (() => {
          const filteredPlans = tbipPlans.filter(p => {
            if (tbipFilterStatus !== 'all' && p.status !== tbipFilterStatus) return false;
            if (tbipFilterPriority !== 'all' && p.overall_priority !== tbipFilterPriority) return false;
            if (tbipSearch.trim()) {
              const q = tbipSearch.toLowerCase();
              const name = (p.msme_name || '').toLowerCase();
              const code = (p.msme_code || '').toLowerCase();
              if (!name.includes(q) && !code.includes(q)) return false;
            }
            return true;
          });

          const totalTbips = tbipPlans.length;
          const highPriorityCount = tbipPlans.filter(p => p.overall_priority === 'High').length;
          const approvedCount = tbipPlans.filter(p => p.status === 'approved').length;
          const draftCount = tbipPlans.filter(p => p.status === 'draft').length;

          return (
            <Box>
              {/* Header */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
                mb: 3,
              }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={800} color={BRAND.primaryMain}>
                    Enterprise Improvement Plans (TBIP)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PRUDEV II MSME Diagnostic Assessments & Technical Improvement Plans
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarMonth />}
                    onClick={() => {
                      setTbipBookMeetingMsme(null);
                      setTbipBookMeetingPlan(null);
                      setTbipBookMeetingOpen(true);
                    }}
                    sx={{
                      borderColor: BRAND.primaryMain,
                      color: BRAND.primaryMain,
                      bgcolor: '#FFFFFF',
                      '&:hover': { bgcolor: '#F1F5F9' },
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Book Meeting with Richard & Stephen
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setNewTbipMsmePicker(true)}
                    sx={{
                      bgcolor: BRAND.gizRed,
                      '&:hover': { bgcolor: BRAND.gizDarkRed },
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    New Assessment / TBIP
                  </Button>
                </Box>
              </Box>


              {/* KPI Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Plans</Typography>
                    <Typography variant="h5" fontWeight={800} color={BRAND.primaryMain}>{totalTbips}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #FCA5A5', bgcolor: '#FEF2F2' }}>
                    <Typography variant="caption" color="error.main" fontWeight={700}>High Priority</Typography>
                    <Typography variant="h5" fontWeight={800} color="#991B1B">{highPriorityCount}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #FDE68A', bgcolor: '#FFFBEB' }}>
                    <Typography variant="caption" color="warning.dark" fontWeight={700}>Drafts</Typography>
                    <Typography variant="h5" fontWeight={800} color="#92400E">{draftCount}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #A7F3D0', bgcolor: '#ECFDF5' }}>
                    <Typography variant="caption" color="success.main" fontWeight={700}>HOA Approved</Typography>
                    <Typography variant="h5" fontWeight={800} color="#065F46">{approvedCount}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Filters Toolbar */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #CBD5E1', bgcolor: '#FFFFFF', mb: 2.5 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={5}>
                    <TextField
                      size="small"
                      placeholder="Search by MSME name or code..."
                      value={tbipSearch}
                      onChange={(e) => setTbipSearch(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={tbipFilterStatus}
                        onChange={(e) => setTbipFilterStatus(e.target.value)}
                      >
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        label="Priority"
                        value={tbipFilterPriority}
                        onChange={(e) => setTbipFilterPriority(e.target.value)}
                      >
                        <MenuItem value="all">All Priorities</MenuItem>
                        <MenuItem value="High">High Priority</MenuItem>
                        <MenuItem value="Medium">Medium Priority</MenuItem>
                        <MenuItem value="Low">Low Priority</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* Table */}
              {filteredPlans.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography fontWeight={600} color="text.secondary">No Enterprise Improvement Plans found.</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Click "New Assessment / TBIP" to conduct an MSME diagnostic assessment.
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', border: '1px solid #CBD5E1', borderRadius: 2 }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead sx={{ bgcolor: '#1A365D' }}>
                      <TableRow>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>MSME</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Assessment Date</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Diagnostic Snapshot</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Priority</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged(filteredPlans, tbipPage).map((p) => {
                        const diag = p.diagnostic_snapshot || {};
                        const totalGaps = diag.total_gaps != null ? diag.total_gaps : (diag.totalGaps || 0);
                        const totalApp = diag.total_applicable != null ? diag.total_applicable : (diag.totalApplicable || 0);
                        const isHigh = p.overall_priority === 'High';
                        const isMed = p.overall_priority === 'Medium';

                        return (
                          <TableRow key={p.id} hover>
                            <TableCell>
                              <Typography fontSize={13.5} fontWeight={700} color={BRAND.primaryMain}>
                                {p.msme_name || `MSME #${p.msme}`}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary">
                                {p.msme_code || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                              {p.assessment_date}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography fontSize={12} fontWeight={800} color={totalGaps > 0 ? '#C8102E' : '#009B62'}>
                                  {totalGaps} / {totalApp} gaps
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={totalApp > 0 ? (totalGaps / totalApp) * 100 : 0}
                                  sx={{
                                    width: 70,
                                    height: 4,
                                    borderRadius: 2,
                                    mt: 0.5,
                                    bgcolor: '#E2E8F0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: totalGaps > 6 ? '#C8102E' : totalGaps > 3 ? '#F59E0B' : '#009B62',
                                    },
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={p.overall_priority || 'Low'}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: 11,
                                  bgcolor: isHigh ? '#FEE2E2' : isMed ? '#FEF3C7' : '#ECFDF5',
                                  color: isHigh ? '#991B1B' : isMed ? '#92400E' : '#065F46',
                                  border: '1px solid',
                                  borderColor: isHigh ? '#F87171' : isMed ? '#FBBF24' : '#A7F3D0',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={(p.status || 'draft').toUpperCase()}
                                size="small"
                                color={p.status === 'approved' ? 'success' : p.status === 'submitted' ? 'primary' : p.status === 'rejected' ? 'error' : 'default'}
                                sx={{ fontWeight: 700, fontSize: 10.5 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Tooltip title="Book Meeting with Richard & Stephen">
                                  <IconButton
                                    size="small"
                                    sx={{ color: '#0F172A' }}
                                    onClick={() => {
                                      const mTarget = msmes.find(m => m.id === p.msme);
                                      setTbipBookMeetingMsme(mTarget || null);
                                      setTbipBookMeetingPlan(p);
                                      setTbipBookMeetingOpen(true);
                                    }}
                                  >
                                    <CalendarMonth fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View / Edit Assessment">
                                  <IconButton
                                    size="small"
                                    color="primary"

                                    onClick={() => {
                                      const mTarget = msmes.find(m => m.id === p.msme);
                                      setSelectedTbipMsme(mTarget || null);
                                      setSelectedTbip(p);
                                      setTbipDialog(true);
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Official PDF">
                                  <IconButton size="small" color="error" onClick={() => handleDownloadTbipPdf(p)}>
                                    <PictureAsPdf fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Excel Workbook">
                                  <IconButton size="small" color="success" onClick={() => handleDownloadTbipExcel(p)}>
                                    <Download fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {p.status === 'draft' && (
                                  <Tooltip title="Delete draft">
                                    <IconButton size="small" color="default" onClick={() => deleteTbipPlan(p)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredPlans.length}
                    page={tbipPage}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    onPageChange={(_, p) => setTbipPage(p)}
                  />
                </TableContainer>
              )}
            </Box>
          );
        })()}

        {/* ── Financial Improvement Action Plans (FIAP / Access to Finance) ── */}
        {section === 'fiap' && (() => {
          const filteredPlans = fiapPlans.filter(p => {
            if (fiapFilterCategory !== 'all' && String(p.category) !== String(fiapFilterCategory)) return false;
            if (fiapFilterPriority !== 'all' && p.support_priority !== fiapFilterPriority) return false;
            if (fiapFilterStatus !== 'all' && p.status !== fiapFilterStatus) return false;
            if (fiapSearch.trim()) {
              const q = fiapSearch.toLowerCase();
              const name = (p.msme_name || '').toLowerCase();
              const code = (p.msme_code || '').toLowerCase();
              const catName = (p.category_name || '').toLowerCase();
              const sector = (p.msme_sector || '').toLowerCase();
              if (!name.includes(q) && !code.includes(q) && !catName.includes(q) && !sector.includes(q)) return false;
            }
            return true;
          });

          const totalFiaps = fiapPlans.length;
          const highPriorityCount = fiapPlans.filter(p => p.support_priority === 'High').length;
          const mediumPriorityCount = fiapPlans.filter(p => p.support_priority === 'Medium').length;
          const lowPriorityCount = fiapPlans.filter(p => p.support_priority === 'Low').length;

          return (
            <Box>
              {/* Header */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
                mb: 3,
              }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={800} color="#1E3A8A">
                    Financial Improvement Action Plans (FIAP)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PRUDEV II / GOPA Pro MSME Diagnostic & Credit Readiness Roadmap (Access to Finance)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<EventAvailable />}
                    onClick={() => {
                      setFiapBookMeetingMsme(null);
                      setFiapBookMeetingPlan(null);
                      setFiapBookMeetingOpen(true);
                    }}
                    sx={{
                      borderColor: '#1E3A8A',
                      color: '#1E3A8A',
                      bgcolor: '#FFFFFF',
                      '&:hover': { bgcolor: '#EFF6FF' },
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Request Approval Meeting
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setNewFiapMsmePicker(true)}
                    sx={{
                      bgcolor: '#1E3A8A',
                      '&:hover': { bgcolor: '#172554' },
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    New Financial Action Plan
                  </Button>
                </Box>
              </Box>

              {/* KPI Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Action Plans</Typography>
                    <Typography variant="h5" fontWeight={800} color="#1E3A8A">{totalFiaps}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #FCA5A5', bgcolor: '#FEF2F2' }}>
                    <Typography variant="caption" color="error.main" fontWeight={700}>High Priority (Severe Gaps)</Typography>
                    <Typography variant="h5" fontWeight={800} color="#991B1B">{highPriorityCount}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #FCD34D', bgcolor: '#FFFBEB' }}>
                    <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 700 }}>Medium Priority (Few Gaps)</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#B45309' }}>{mediumPriorityCount}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #A7F3D0', bgcolor: '#F0FDF4' }}>
                    <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700 }}>Low Priority (Finance Ready)</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#047857' }}>{lowPriorityCount}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Filter Bar */}
              <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search MSME, code, sector..."
                      value={fiapSearch}
                      onChange={(e) => { setFiapSearch(e.target.value); setFiapPage(0); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#94A3B8' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={fiapFilterCategory}
                        label="Category"
                        onChange={(e) => { setFiapFilterCategory(e.target.value); setFiapPage(0); }}
                      >
                        <MenuItem value="all">All 9 Categories</MenuItem>
                        <MenuItem value="1">1. Financial Records & Documentation</MenuItem>
                        <MenuItem value="2">2. Cash-Flow & Financial Discipline</MenuItem>
                        <MenuItem value="3">3. Business Plan & Projections</MenuItem>
                        <MenuItem value="4">4. Collateral & Alternative Security</MenuItem>
                        <MenuItem value="5">5. Banking Relationship & Linkage</MenuItem>
                        <MenuItem value="6">6. Financial Literacy & Costing</MenuItem>
                        <MenuItem value="7">7. Loan Application Readiness</MenuItem>
                        <MenuItem value="8">8. Debt Management & Credit Growth</MenuItem>
                        <MenuItem value="9">9. Formalisation, Audit & Certification</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3} sm={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={fiapFilterPriority}
                        label="Priority"
                        onChange={(e) => { setFiapFilterPriority(e.target.value); setFiapPage(0); }}
                      >
                        <MenuItem value="all">All Priorities</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3} sm={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={fiapFilterStatus}
                        label="Status"
                        onChange={(e) => { setFiapFilterStatus(e.target.value); setFiapPage(0); }}
                      >
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* Table / List */}
              {filteredPlans.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #CBD5E1', bgcolor: '#F8FAFC' }}>
                  <AccountBalance sx={{ fontSize: 48, color: '#94A3B8', mb: 1.5 }} />
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                    No Financial Improvement Action Plans Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Formulate a credit readiness diagnostic and tailored roadmap for your assigned MSMEs.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setNewFiapMsmePicker(true)}
                    sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#172554' }, fontWeight: 700 }}
                  >
                    Create Action Plan
                  </Button>
                </Paper>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#1E3A8A' }}>
                      <TableRow>
                        <TableCell sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>MSME & Details</TableCell>
                        <TableCell sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>Action Plan Category</TableCell>
                        <TableCell sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>Gaps & Target Facility</TableCell>
                        <TableCell align="center" sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>Priority</TableCell>
                        <TableCell align="center" sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>Status</TableCell>
                        <TableCell align="right" sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 12 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPlans.slice(fiapPage * ROWS_PER_PAGE, fiapPage * ROWS_PER_PAGE + ROWS_PER_PAGE).map((p) => {
                        const isHigh = p.support_priority === 'High';
                        const isMed = p.support_priority === 'Medium';
                        const gapsCount = (p.identified_gaps || []).length;
                        const stepsCount = (p.action_plan_steps || []).length;

                        return (
                          <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography fontWeight={700} fontSize={13.5} color="#1E3A8A">
                                {p.msme_name || `MSME #${p.msme}`}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary">
                                {p.msme_code ? `${p.msme_code} · ` : ''}{p.msme_sector || 'General'}
                                {p.msme_district ? ` · ${p.msme_district}` : ''}
                              </Typography>
                              {p.assessment_date && (
                                <Typography fontSize={10.5} color="text.secondary" sx={{ mt: 0.25 }}>
                                  Assessed: {p.assessment_date}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={`#${p.category || '—'}`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#EFF6FF',
                                    color: '#1E3A8A',
                                    fontWeight: 800,
                                    fontSize: 11,
                                    border: '1px solid #BFDBFE',
                                    minWidth: 32,
                                  }}
                                />
                                <Box>
                                  <Typography fontSize={12.5} fontWeight={700} color="#1E293B">
                                    {p.category_name || `Category ${p.category}`}
                                  </Typography>
                                  <Typography fontSize={11} color="text.secondary" sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}>
                                    {p.category_addresses}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 240 }}>
                              <Typography fontSize={12} fontWeight={600} color="#334155">
                                {gapsCount} gap{gapsCount === 1 ? '' : 's'} identified · {stepsCount} step{stepsCount === 1 ? '' : 's'}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}>
                                {p.financing_target || 'Target financing not specified'}
                                {p.preferred_fsp ? ` (${p.preferred_fsp})` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={p.support_priority || 'Low'}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: 11,
                                  bgcolor: isHigh ? '#FEE2E2' : isMed ? '#FEF3C7' : '#ECFDF5',
                                  color: isHigh ? '#991B1B' : isMed ? '#92400E' : '#065F46',
                                  border: '1px solid',
                                  borderColor: isHigh ? '#F87171' : isMed ? '#FBBF24' : '#A7F3D0',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={p.status || 'draft'}
                                size="small"
                                color={p.status === 'approved' ? 'success' : p.status === 'submitted' ? 'primary' : 'default'}
                                sx={{ fontWeight: 700, fontSize: 10.5 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Tooltip title="View / Edit Action Plan">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      const mTarget = msmes.find(m => m.id === p.msme);
                                      setSelectedFiapMsme(mTarget || null);
                                      setSelectedFiap(p);
                                      setFiapDialog(true);
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Request Approval Meeting with Rosemary or Harriet">
                                  <IconButton
                                    size="small"
                                    sx={{ color: '#1E3A8A' }}
                                    onClick={() => {
                                      const mTarget = msmes.find(m => m.id === p.msme);
                                      setFiapBookMeetingPlan(p);
                                      setFiapBookMeetingMsme(mTarget || null);
                                      setFiapBookMeetingOpen(true);
                                    }}
                                  >
                                    <EventAvailable fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Official PRUDEV II PDF">
                                  <IconButton size="small" color="error" onClick={() => handleDownloadFiapPdf(p)}>
                                    <PictureAsPdf fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                {p.status === 'draft' && (
                                  <Tooltip title="Delete draft">
                                    <IconButton size="small" color="default" onClick={() => deleteFiapPlan(p)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredPlans.length}
                    page={fiapPage}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    onPageChange={(_, page) => setFiapPage(page)}
                  />
                </TableContainer>
              )}
            </Box>
          );
        })()}

        {/* ── Work Orders (read-only — admin issues) ── */}
        {section === 'workorders' && (
          <Box>
            {/* Signature card */}
            <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>My Signature</Typography>
                  {sigUrl ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'inline-block', bgcolor: '#f9f9f9' }}>
                        <img src={sigUrl} alt="My signature" style={{ maxHeight: 48, maxWidth: 180, display: 'block' }} />
                      </Box>
                      <Typography variant="caption" color="success.main" fontWeight={600}>Signature on file ✓</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Upload a JPEG or PNG of your signature — it will be embedded in your work orders and reports automatically.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                  <input type="file" accept="image/jpeg,image/png" ref={sigInputRef} style={{ display: 'none' }}
                    onChange={e => setSigFile(e.target.files[0] || null)} />
                  {sigFile ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sigFile.name}</Typography>
                      <Button variant="contained" size="small" onClick={uploadSignature} disabled={sigUploading}>
                        {sigUploading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                      </Button>
                      <Button size="small" onClick={() => setSigFile(null)}>Cancel</Button>
                    </>
                  ) : (
                    <Button variant={sigUrl ? 'text' : 'outlined'} size="small" onClick={() => sigInputRef.current?.click()}>
                      {sigUrl ? 'Update signature' : 'Choose file…'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>

            {/* Work orders list — submissions and payments embedded per card */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Issued Work Orders</Typography>
              <Typography variant="body2" color="text.secondary">
                {workOrders.length} work order{workOrders.length !== 1 ? 's' : ''} issued to you
                {myBgeCode && (
                  <Box component="span" sx={{ ml: 1, color: BRAND.primaryMain, fontWeight: 600 }}>
                    · BGE code: {myBgeCode}
                  </Box>
                )}
              </Typography>
            </Box>

            {workOrders.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No work orders have been issued to you yet.</Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Hidden file inputs shared across all work order cards */}
                <input type="file" accept=".xlsx,.xls" ref={timesheetInputRef} style={{ display: 'none' }}
                  onChange={e => setTimesheetFile(e.target.files[0] || null)} />
                <input type="file" accept=".xlsx,.xls" ref={invoiceInputRef} style={{ display: 'none' }}
                  onChange={e => setInvoiceFile(e.target.files[0] || null)} />
                <input type="file" accept=".xlsx,.xls" ref={replaceTimesheetRef} style={{ display: 'none' }}
                  onChange={e => setReplaceTimesheetFile(e.target.files[0] || null)} />
                <input type="file" accept=".xlsx,.xls" ref={replaceInvoiceRef} style={{ display: 'none' }}
                  onChange={e => setReplaceInvoiceFile(e.target.files[0] || null)} />
                <input type="file" accept="image/*,.pdf,application/pdf" ref={attachInputRef} style={{ display: 'none' }}
                  onChange={e => setAttachFile(e.target.files[0] || null)} />

                {workOrders.map(wo => {
                  const woSubs = submissions.filter(s => s.work_order === wo.id);
                  const woPays = payments.filter(p => p.work_order === wo.id);
                  const woAttachments = attachments.filter(a => {
                    const aWoId = typeof a.work_order === 'object' ? a.work_order?.id : a.work_order;
                    return String(aWoId) === String(wo.id);
                  });
                  return (
                    <Card variant="outlined" key={wo.id}
                      sx={wo.work_order_type === 'bge_technical_co_assignment' ? { borderLeft: `4px solid ${BRAND.gopaGoldDark}` } : {}}>
                      <CardContent>
                        {/* Header row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                              <Typography fontWeight={700}>{wo.work_order_number}</Typography>
                              {wo.work_order_type === 'bge_technical_co_assignment' && (
                                <Chip label="Specialist Co-Assignment" size="small"
                                  sx={{ bgcolor: BRAND.gopaGoldDark, color: '#fff', fontSize: 10, fontWeight: 700 }} />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {wo.work_order_type_display} · Issued: {wo.issue_date}
                              {wo.start_date && ` · ${wo.start_date}`}
                              {wo.end_date && ` – ${wo.end_date}`}
                            </Typography>
                            {wo.work_order_type === 'bge_technical_co_assignment' && (
                              <Box sx={{ mt: 0.5, mb: 0.5, p: 0.8, bgcolor: 'action.hover', borderRadius: 1 }}>
                                {wo.supported_bge_name && (
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'info.main' }}>
                                    Supporting Primary BGE: {wo.supported_bge_name} {wo.supported_bge_code ? `(${wo.supported_bge_code})` : ''}
                                  </Typography>
                                )}
                                {(wo.technical_area || wo.bge_top_skills) && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    Technical Specialty: {wo.technical_area || wo.bge_top_skills}
                                  </Typography>
                                )}
                                {wo.target_msmes_detail && wo.target_msmes_detail.length > 0 && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    Target MSMEs ({wo.target_msmes_detail.length}): {wo.target_msmes_detail.map(m => m.name).slice(0, 3).join(', ')}{wo.target_msmes_detail.length > 3 ? ` +${wo.target_msmes_detail.length - 3} more` : ''}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {wo.status === 'signed' && wo.bge_signed_date && (
                              <Typography variant="caption" color="success.main" display="block" fontWeight={600}>
                                Signed: {wo.bge_signed_date}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">{wo.location}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={wo.status_display || wo.status} size="small"
                              color={wo.status === 'signed' ? 'success' : 'primary'} />
                            {wo.status === 'issued' && (
                              <Tooltip title={sigUrl ? 'Sign this work order' : 'Upload your signature first'}>
                                <span>
                                  <Button variant="contained" size="small" color="success"
                                    disabled={woSigning === wo.id || !sigUrl}
                                    startIcon={woSigning === wo.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                                    onClick={() => signWo(wo)}>
                                    Sign
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Review work order PDF">
                              <IconButton size="small" color="info" onClick={() => reviewWo(wo)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download PDF">
                              <IconButton size="small" color={wo.status === 'signed' ? 'success' : 'default'} onClick={() => downloadWoPdf(wo)}>
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print / Save as PDF">
                              <IconButton size="small" onClick={() => setWorkOrderPreview(wo)}>
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Payment Banner & Confirmation Action for BGE */}
                        {wo.payment_status === 'submitted_for_payment' && (
                          <Alert
                            severity="warning"
                            sx={{ mt: 1.5, py: 0.75, bgcolor: '#FFF8E1', borderColor: '#FFE082', border: '1px solid', borderRadius: 1.5 }}
                            action={
                              <Button
                                color="success"
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => openBgePaymentConfirm(wo, 'work_order')}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
                              >
                                Confirm Payment Received
                              </Button>
                            }
                          >
                            <Typography variant="body2" fontWeight={700} color="#E65100">
                              💰 Payment Submitted to Finance
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Net Payable: <strong>UGX {(wo.rate_per_day * wo.max_days * 0.94).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                              {wo.payment_reference ? ` · Reference: ${wo.payment_reference}` : ''}
                              {wo.payment_submitted_at ? ` · Submitted on: ${wo.payment_submitted_at.slice(0, 10)}` : ''}
                            </Typography>
                          </Alert>
                        )}

                        {(wo.payment_confirmed_by_bge || wo.payment_status === 'payment_confirmed') && (
                          <Alert severity="success" sx={{ mt: 1.5, py: 0.5, bgcolor: '#E8F5E9', borderColor: '#A5D6A7', border: '1px solid', borderRadius: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} color="#2E7D32" display="block">
                              ✓ Payment Receipt Confirmed by You
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Confirmed on: {wo.payment_confirmed_at ? wo.payment_confirmed_at.slice(0, 16).replace('T', ' ') : 'Recently'}
                              {wo.payment_reference ? ` · Ref: ${wo.payment_reference}` : ''}
                            </Typography>
                          </Alert>
                        )}

                        {wo.objective && (
                          <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }} icon={false}>
                            <Typography variant="caption" fontWeight={600}>Objective</Typography>
                            <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                              {wo.objective.length > 200 ? wo.objective.slice(0, 200) + '…' : wo.objective}
                            </Typography>
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            Rate: <strong>UGX {Number(wo.rate_per_day).toLocaleString()}/day</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Max: <strong>{wo.max_days} days</strong>
                          </Typography>
                          {wo.team_leader_name && (
                            <Typography variant="caption" color="text.secondary">
                              Team Leader: <strong>{wo.team_leader_name}</strong>
                            </Typography>
                          )}
                        </Box>

                        {/* ── Timesheet & Invoice submissions ── */}
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                              TIMESHEET &amp; INVOICE
                            </Typography>
                            <Button size="small" startIcon={<CloudUpload />}
                              onClick={() => {
                                setActiveUploadWoId(prev => prev === wo.id ? null : wo.id);
                                setTimesheetFile(null);
                                setInvoiceFile(null);
                                if (timesheetInputRef.current) timesheetInputRef.current.value = '';
                                if (invoiceInputRef.current) invoiceInputRef.current.value = '';
                              }}>
                              {activeUploadWoId === wo.id ? 'Cancel' : 'Upload files'}
                            </Button>
                          </Box>

                          {/* Existing submissions */}
                          {woSubs.length > 0 && (
                            <TableContainer sx={{ mb: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ py: 0.5 }}>Submitted</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Timesheet</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Invoice</TableCell>
                                  <TableCell sx={{ py: 0.5 }} align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {woSubs.map(sub => (
                                  <React.Fragment key={sub.id}>
                                    <TableRow>
                                      <TableCell sx={{ py: 0.5 }}>{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>
                                        {sub.has_timesheet ? (
                                          <Tooltip title={sub.timesheet_filename}>
                                            <IconButton size="small" color="primary" onClick={() => downloadSubmissionFile(sub, 'timesheet')}>
                                              <Download fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.5 }}>
                                        {sub.has_invoice ? (
                                          <Tooltip title={sub.invoice_filename}>
                                            <IconButton size="small" color="primary" onClick={() => downloadSubmissionFile(sub, 'invoice')}>
                                              <Download fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.5 }} align="right">
                                        <Tooltip title="Replace files">
                                          <IconButton size="small" color="default" onClick={() => {
                                            setReplacingSubId(prev => prev === sub.id ? null : sub.id);
                                            setReplaceTimesheetFile(null);
                                            setReplaceInvoiceFile(null);
                                            if (replaceTimesheetRef.current) replaceTimesheetRef.current.value = '';
                                            if (replaceInvoiceRef.current) replaceInvoiceRef.current.value = '';
                                          }}>
                                            <Edit fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton size="small" color="error" onClick={() => deleteSubmission(sub)}>
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    {replacingSubId === sub.id && (
                                      <TableRow>
                                        <TableCell colSpan={4} sx={{ bgcolor: 'action.hover', py: 1 }}>
                                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography variant="caption" fontWeight={700}>Replace:</Typography>
                                            <Button size="small" variant="outlined" startIcon={<CloudUpload />}
                                              onClick={() => replaceTimesheetRef.current?.click()}>
                                              {replaceTimesheetFile ? replaceTimesheetFile.name : 'New Timesheet'}
                                            </Button>
                                            <Button size="small" variant="outlined" startIcon={<CloudUpload />}
                                              onClick={() => replaceInvoiceRef.current?.click()}>
                                              {replaceInvoiceFile ? replaceInvoiceFile.name : 'New Invoice'}
                                            </Button>
                                            <Button size="small" variant="contained" disabled={replaceUploading}
                                              onClick={() => replaceSubmission(sub)}>
                                              {replaceUploading ? <CircularProgress size={14} color="inherit" /> : 'Update'}
                                            </Button>
                                            <Button size="small" onClick={() => setReplacingSubId(null)}>Cancel</Button>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                ))}
                              </TableBody>
                            </Table>
                            </TableContainer>
                          )}

                          {woSubs.length === 0 && activeUploadWoId !== wo.id && (
                            <Typography variant="caption" color="text.disabled">No files submitted yet.</Typography>
                          )}

                          {/* Inline upload form */}
                          {activeUploadWoId === wo.id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Button size="small" variant="outlined" startIcon={<CloudUpload />}
                                onClick={() => timesheetInputRef.current?.click()}>
                                {timesheetFile ? timesheetFile.name : 'Choose Timesheet'}
                              </Button>
                              <Button size="small" variant="outlined" startIcon={<CloudUpload />}
                                onClick={() => invoiceInputRef.current?.click()}>
                                {invoiceFile ? invoiceFile.name : 'Choose Invoice'}
                              </Button>
                              <Button size="small" variant="contained" disabled={submissionUploading}
                                onClick={() => uploadSubmission(wo.id)}>
                                {submissionUploading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                              </Button>
                            </Box>
                          )}
                        </Box>

                        {/* ── Supporting Documents ── */}
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary">
                                SUPPORTING DOCUMENTS &amp; PHOTOS
                              </Typography>
                              <Tooltip title="Photos uploaded here automatically sync in real time to your dedicated Google Drive folder ('PRUDEV II - BGE Photos')">
                                <Chip
                                  size="small"
                                  label="Auto-Syncs to Google Drive"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                />
                              </Tooltip>
                            </Box>
                            <Button size="small" startIcon={<CloudUpload />}
                              onClick={() => {
                                setActiveAttachWoId(prev => prev === wo.id ? null : wo.id);
                                setAttachFile(null);
                                setAttachCaption('');
                                if (attachInputRef.current) attachInputRef.current.value = '';
                              }}>
                              {activeAttachWoId === wo.id ? 'Cancel' : 'Attach file'}
                            </Button>
                          </Box>

                          {woAttachments.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                              {woAttachments.map(att => {
                                const isImage = att.content_type && att.content_type.startsWith('image/');
                                const isPdf   = att.content_type === 'application/pdf';
                                return (
                                  <Box key={att.id} sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                    p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    {isImage
                                      ? <img src={WORK_ORDER_ATTACHMENT_DOWNLOAD_URL(att.id)}
                                          alt={att.filename}
                                          style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                          onError={e => { e.target.style.display = 'none'; }} />
                                      : <PictureAsPdf fontSize="small" color={isPdf ? 'error' : 'action'} sx={{ flexShrink: 0 }} />
                                    }
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" noWrap display="block" fontWeight={600}>
                                        {att.filename}
                                      </Typography>
                                      {att.caption && (
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                          {att.caption}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Tooltip title="View / Download">
                                      <IconButton size="small" color="primary" onClick={() => openAttachment(att)}>
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {woAttachments.length === 0 && activeAttachWoId !== wo.id && (
                            <Typography variant="caption" color="text.disabled">
                              No supporting documents yet.
                            </Typography>
                          )}

                          {activeAttachWoId === wo.id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                              mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Button size="small" variant="outlined" startIcon={<CloudUpload />}
                                onClick={() => attachInputRef.current?.click()}>
                                {attachFile ? attachFile.name : 'Choose file (photo or PDF)'}
                              </Button>
                              <TextField
                                size="small" placeholder="Title (e.g. Attendance sheet)"
                                value={attachCaption}
                                onChange={e => setAttachCaption(e.target.value)}
                                sx={{ flex: 1, minWidth: 140 }}
                              />
                              <Button size="small" variant="contained" disabled={attachUploading}
                                onClick={() => uploadAttachment(wo.id)}>
                                {attachUploading ? <CircularProgress size={14} color="inherit" /> : 'Attach'}
                              </Button>
                            </Box>
                          )}
                        </Box>

                        {/* ── Payments ── */}
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">PAYMENTS</Typography>
                            <Button size="small" onClick={() => {
                              setActivePaymentWoId(prev => prev === wo.id ? null : wo.id);
                              setPaymentForm({ payment_date: '', amount: '', balance: '' });
                            }}>
                              {activePaymentWoId === wo.id ? 'Cancel' : woPays.length > 0 ? 'Record another' : 'Record payment'}
                            </Button>
                          </Box>

                          {woPays.length > 0 && (
                            <TableContainer sx={{ mb: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ py: 0.5 }}>Date received</TableCell>
                                    <TableCell sx={{ py: 0.5 }} align="right">Amount (UGX)</TableCell>
                                    <TableCell sx={{ py: 0.5 }} align="right">Balance (UGX)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {woPays.map(p => (
                                    <TableRow key={p.id}>
                                      <TableCell sx={{ py: 0.5 }}>{p.payment_date}</TableCell>
                                      <TableCell sx={{ py: 0.5 }} align="right">
                                        <strong>{Number(p.amount).toLocaleString()}</strong>
                                      </TableCell>
                                      <TableCell sx={{ py: 0.5 }} align="right">
                                        {p.balance != null
                                          ? Number(p.balance).toLocaleString()
                                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}

                          {woPays.length === 0 && activePaymentWoId !== wo.id && (
                            <Typography variant="caption" color="text.disabled">No payments recorded yet.</Typography>
                          )}

                          {activePaymentWoId === wo.id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <TextField
                                size="small" type="date" label="Date received"
                                InputLabelProps={{ shrink: true }}
                                value={paymentForm.payment_date}
                                onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                                sx={{ width: 160 }}
                              />
                              <TextField
                                size="small" type="number" label="Amount received (UGX)"
                                value={paymentForm.amount}
                                onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                                sx={{ width: 180 }}
                              />
                              <TextField
                                size="small" type="number" label="Balance on invoice (UGX)"
                                value={paymentForm.balance}
                                onChange={e => setPaymentForm(f => ({ ...f, balance: e.target.value }))}
                                sx={{ width: 180 }}
                              />
                              <Button variant="contained" size="small" color="success"
                                disabled={paymentSaving}
                                onClick={() => recordPayment(wo.id)}>
                                {paymentSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ── Training tab ─────────────────────────────────────────────────── */}
        {section === 'training' && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Training & Peer-to-Peer Sessions</Typography>
                <Typography variant="body2" color="text.secondary">
                  Facilitate peer learning sessions with mentor syndicates and participating MSMEs.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                size="small"
                onClick={openNewP2pSession}
                sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark } }}
              >
                New Peer-to-Peer Session
              </Button>
            </Box>

            <Tabs value={trainingTab} onChange={(_, v) => setTrainingTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={`Facilitated (${leadSessions.length + mentorSessions.length})`} />
              <Tab label={`Participant (${participantSessions.length})`} />
              <Tab label={`My Reports (${ptReports.length})`} />
            </Tabs>

            {trainingTab === 0 && (
            <>
            {leadSessions.length === 0 && mentorSessions.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <School sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" gutterBottom>No sessions found.</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Start a new Peer-to-Peer session with your mentor team and participating MSMEs.
                </Typography>
                <Button variant="contained" startIcon={<Add />} size="small" onClick={openNewP2pSession}>
                  New Peer-to-Peer Session
                </Button>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {leadSessions.map(s => (
                  <Card key={s.id} sx={{ borderLeft: `4px solid ${BRAND.primaryMain}`, '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                          <School sx={{ color: BRAND.primaryMain, fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label="Lead" size="small" sx={{ bgcolor: BRAND.primaryMain, color: '#fff', fontWeight: 700, fontSize: 11 }} />
                              <Typography fontWeight={700} fontSize={15}>{s.title}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {s.date}{s.location ? ` · ${s.location}` : ''}
                              {s.topic_name ? ` · Topic: ${s.topic_name}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                          <Chip label={s._tr ? (s._tr.status === 'submitted' ? 'Submitted' : 'Draft') : 'Pending'}
                            size="small" color={s._tr?.status === 'submitted' ? 'success' : s._tr ? 'warning' : 'default'} />
                          <Chip label={`${s.attendance_count ?? 0} present`} size="small" variant="outlined"
                            color={s.attendance_count > 0 ? 'success' : 'default'} />
                          <Button size="small" variant="outlined" startIcon={<Assignment />}
                            onClick={() => openAttendance(s)}
                            sx={{ fontSize: 12, borderColor: BRAND.primaryMain, color: BRAND.primaryMain }}>
                            Attendance
                          </Button>
                          <Button size="small" variant="contained" startIcon={<Edit />}
                            onClick={() => openTrReport(s)}
                            sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark }, fontSize: 12 }}>
                            {s._tr ? 'Edit Report' : 'Write Report'}
                          </Button>
                          {s._tr && (
                            <Tooltip title="View / Download PDF">
                              <IconButton size="small" onClick={() => openTrPdf(s._tr.id, 'view')}>
                                <PictureAsPdf fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      {(s.team || []).filter(m => m.role === 'mentor').length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Mentor BGEs:</Typography>
                          {(s.team || []).filter(m => m.role === 'mentor').map(m => (
                            <Chip key={m.id || m.bge_id} label={m.bge_name || 'Mentor'} size="small" sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2', fontSize: 11, height: 22 }} />
                          ))}
                        </Box>
                      )}
                      {(s.businesses_detail || []).length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>MSMEs:</Typography>
                          {s.businesses_detail.map(m => (
                            <Chip key={m.id} label={`${m.business_name}${m.owner_name ? ` (${m.owner_name})` : ''}`} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {mentorSessions.map(s => (
                  <Card key={s.id} sx={{ borderLeft: '4px solid #7B1FA2', '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                          <People sx={{ color: '#7B1FA2', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label="Mentor" size="small" sx={{ bgcolor: '#7B1FA2', color: '#fff', fontWeight: 700, fontSize: 11 }} />
                              <Typography fontWeight={700} fontSize={15}>{s.title}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {s.date}{s.location ? ` · ${s.location}` : ''}
                              {(s.team || []).find(m => m.role === 'lead')?.bge_name ? ` · Lead: ${(s.team || []).find(m => m.role === 'lead').bge_name}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                          <Chip label={s._mr ? (s._mr.status === 'submitted' ? 'Submitted' : 'Draft') : 'Pending'}
                            size="small" color={s._mr?.status === 'submitted' ? 'success' : s._mr ? 'warning' : 'default'} />
                          <Button size="small" variant="contained" startIcon={<Edit />}
                            onClick={() => openMrReport(s)}
                            sx={{ bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#6a1b9a' }, fontSize: 12 }}>
                            {s._mr ? 'Edit Report' : 'Write Report'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            </>
            )}

            {/* ── Tab 1: Sessions assigned as participant ── */}
            {trainingTab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Training sessions where you are registered as a participant learner.
                  Click "Write Report" to document your learning outcomes.
                </Typography>
                {participantSessions.length === 0 ? (
                  <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <School sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>No participant sessions yet.</Typography>
                    <Typography variant="caption" color="text.secondary">
                      You will appear here once the programme administrator registers you as a participant in a training session.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {participantSessions.map(s => {
                      // Match by session FK first (if the report was saved with a session link),
                      // then fall back to title + computed date range (same format the dialog pre-fills).
                      const tDates = s.date + (s.end_date && s.end_date !== s.date ? ` – ${s.end_date}` : '');
                      const existing = ptReports.find(r =>
                        (r.session && r.session === s.id) ||
                        (r.training_title === s.title && r.training_dates === tDates)
                      );
                      return (
                        <Card key={s.id} sx={{ borderLeft: `4px solid ${BRAND.primaryMain}`, '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                                <School sx={{ color: BRAND.primaryMain, fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip label="Participant" size="small" sx={{ bgcolor: BRAND.primaryMain, color: '#fff', fontWeight: 700, fontSize: 11 }} />
                                    <Typography fontWeight={700} fontSize={15}>{s.title}</Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {s.date}{s.end_date && s.end_date !== s.date ? ` – ${s.end_date}` : ''}
                                    {s.location ? ` · ${s.location}` : ''}
                                    {(s.team || []).find(m => m.role === 'lead')?.bge_name
                                      ? ` · Lead: ${(s.team || []).find(m => m.role === 'lead').bge_name}` : ''}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                <Chip
                                  label={existing ? (existing.status === 'submitted' ? 'Submitted' : 'Draft') : 'Pending'}
                                  size="small"
                                  color={existing?.status === 'submitted' ? 'success' : existing ? 'warning' : 'default'} />
                                <Button size="small" variant="contained" startIcon={<Edit />}
                                  onClick={() => openPtDialog(existing, {
                                    session: s.id,
                                    training_title: s.title,
                                    training_dates: s.date + (s.end_date && s.end_date !== s.date ? ` – ${s.end_date}` : ''),
                                    venue: s.location || '',
                                    attendees: (s.bge_participants_detail || []).map(p => ({
                                      name: p.name, bge_code: p.bge_code || '', phone: '', gender: '', organisation: '',
                                    })),
                                  })}
                                  sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark }, fontSize: 12 }}>
                                  {existing ? 'Edit Report' : 'Write Report'}
                                </Button>
                                {existing && (
                                  <>
                                    <Tooltip title="Open PDF">
                                      <IconButton size="small" onClick={() => openPtPdf(existing.id, 'view')}>
                                        <PictureAsPdf fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                      <IconButton size="small" onClick={() => openPtPdf(existing.id, 'download')}>
                                        <Download fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* ── Tab 2: Standalone participant training reports ── */}
            {trainingTab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Reports for any training you attended as a participant, including external trainings.
                  </Typography>
                  <Button variant="contained" startIcon={<Add />} size="small" onClick={() => openPtDialog()}>
                    New Report
                  </Button>
                </Box>

                {ptReports.length === 0 ? (
                  <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <School sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>No participant training reports yet.</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click "New Report" to document a training session you attended as a learner.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {ptReports.map(r => (
                      <Card key={r.id} sx={{ borderLeft: `4px solid ${BRAND.primaryMain}`, '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={700} fontSize={15}>{r.training_title || '(Untitled)'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {r.training_dates}{r.venue ? ` · ${r.venue}` : ''}
                                {r.facilitator_name ? ` · Facilitator: ${r.facilitator_name}` : ''}
                              </Typography>
                              {(r.attendees || []).length > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {r.attendees.length} attendees in register
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                              <Chip label={r.status} size="small"
                                color={r.status === 'submitted' ? 'success' : 'warning'} />
                              <Tooltip title="Open PDF">
                                <IconButton size="small" onClick={() => openPtPdf(r.id, 'view')}>
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton size="small" onClick={() => openPtPdf(r.id, 'download')}>
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {r.status !== 'submitted' && (
                                <Button size="small" variant="contained" startIcon={<Edit />}
                                  onClick={() => openPtDialog(r)}
                                  sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark }, fontSize: 12 }}>
                                  Edit
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── T-Shirt Receipts tab ────────────────────────────────────────── */}
        {section === 'tshirts' && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>My T-Shirt Receipt</Typography>
              <Typography variant="body2" color="text.secondary">
                Sign to acknowledge receipt of your programme t-shirt.
              </Typography>
            </Box>

            {tshirtEntriesLoading && <LinearProgress sx={{ mb: 2 }} />}

            {!tshirtEntriesLoading && tshirtEntries.length === 0 && (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Checkroom sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" gutterBottom>No t-shirt receipts assigned to you yet.</Typography>
                <Typography variant="caption" color="text.secondary">
                  The programme administrator will create a receipt when t-shirts are distributed.
                </Typography>
              </Paper>
            )}

            {tshirtEntries.map(entry => (
              <Card key={entry.id} sx={{
                mb: 2,
                borderLeft: `4px solid ${entry.signed ? '#009B62' : '#F59E0B'}`,
                '&:hover': { boxShadow: 3 },
                transition: 'box-shadow 0.2s',
              }}>
                <CardContent>
                  {/* Title row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Checkroom sx={{ color: entry.signed ? '#009B62' : '#F59E0B', fontSize: 20 }} />
                    <Typography fontWeight={700} fontSize={15} sx={{ flex: 1 }}>
                      {entry.receipt_title || 'T-Shirt Receipt'}
                    </Typography>
                    {entry.signed ? (
                      <Chip label="Signed" size="small" color="success" icon={<CheckCircle />} />
                    ) : (
                      <Chip label="Pending Signature" size="small" color="warning" />
                    )}
                  </Box>

                  {/* Colour + signed-date row (always read-only) */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: entry.signed ? 0 : 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Colour</Typography>
                      <Typography variant="body2" fontWeight={600}>{entry.receipt_colour || 'Blue'}</Typography>
                    </Box>
                    {entry.signed && entry.signed_at && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Signed on</Typography>
                        <Typography variant="body2" fontWeight={600}>{new Date(entry.signed_at).toLocaleDateString()}</Typography>
                      </Box>
                    )}
                    {entry.signed && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Size</Typography>
                          <Typography variant="body2" fontWeight={600}>{entry.size}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>Qty</Typography>
                          <Typography variant="body2" fontWeight={600}>{entry.quantity}</Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Editable size + qty — only shown before signing */}
                  {!entry.signed && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
                          T-Shirt Size
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={(tshirtEdits[entry.id]?.size) ?? entry.size}
                            onChange={e => setTshirtEdits(prev => ({ ...prev, [entry.id]: { ...(prev[entry.id] || {}), size: e.target.value } }))}
                          >
                            {['L', 'XL', '2XL'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
                          Quantity
                        </Typography>
                        <TextField
                          type="number" size="small"
                          value={(tshirtEdits[entry.id]?.quantity) ?? entry.quantity}
                          inputProps={{ min: 1, max: 5, style: { width: 52, textAlign: 'center' } }}
                          onChange={e => setTshirtEdits(prev => ({ ...prev, [entry.id]: { ...(prev[entry.id] || {}), quantity: Math.max(1, parseInt(e.target.value) || 1) } }))}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }} />
                      {!entry.has_signature && (
                        <Alert severity="warning" sx={{ py: 0.5 }} icon={false}>
                          <Typography variant="body2">No signature on file — upload yours in your profile first.</Typography>
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained" size="small"
                          startIcon={tshirtSigningId === entry.id
                            ? <CircularProgress size={14} color="inherit" />
                            : <DrawOutlined />}
                          disabled={tshirtSigningId === entry.id || !entry.has_signature}
                          onClick={() => handleSignTshirtEntry(entry.id)}
                          sx={{ bgcolor: '#009B62', '&:hover': { bgcolor: '#007a4d' } }}
                        >
                          Confirm & Sign
                        </Button>
                        {entry.receipt_id && (
                          <Button
                            variant="outlined" size="small"
                            startIcon={<Download />}
                            onClick={() => handleDownloadTshirtPdf(entry.receipt_id, entry.receipt_title)}
                          >
                            PDF
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Download button when already signed */}
                  {entry.signed && entry.receipt_id && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="outlined" size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadTshirtPdf(entry.receipt_id, entry.receipt_title)}
                      >
                        Download PDF
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Training report dialog (lead sessions) ─────────────────────────── */}
      {trDialog && (
        <Dialog open onClose={() => setTrDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {trData ? 'Edit Peer-to-Peer Session Report' : 'Write Peer-to-Peer Session Report'}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {trSession?.title} · {trSession?.date}
                {trForm.venue ? ` · ${trForm.venue}` : ''}
                {trData?.status === 'submitted' ? (
                  <Chip label="Submitted" size="small" color="success" sx={{ ml: 1, fontSize: 10, height: 20 }} />
                ) : (
                  <Chip label="Draft" size="small" color="warning" sx={{ ml: 1, fontSize: 10, height: 20 }} />
                )}
              </Typography>
            </Box>
            {trData?.id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => openTrPdf(trData.id, 'view')}
                sx={{ fontSize: 12 }}
              >
                View PDF
              </Button>
            )}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* ── Session Metadata & Venue Log ── */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} color={BRAND.primaryMain} sx={{ mb: 1.5 }}>
                1. SESSION METADATA & FACILITATION TEAM
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth size="small" label="Training Title"
                    value={trForm.training_title || ''} onChange={e => setTrForm(f => ({ ...f, training_title: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Date(s)" placeholder="e.g. 17–19 Feb 2026"
                    value={trForm.training_dates || ''} onChange={e => setTrForm(f => ({ ...f, training_dates: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Venue / Location"
                    value={trForm.venue || ''} onChange={e => setTrForm(f => ({ ...f, venue: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="District"
                    value={trForm.district || ''} onChange={e => setTrForm(f => ({ ...f, district: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Duration / Time Allocation" placeholder="e.g. 9:00 AM – 3:30 PM (6.5 hrs)"
                    value={trForm.time_allocation || ''} onChange={e => setTrForm(f => ({ ...f, time_allocation: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Facilitation Team (Lead & Mentor BGEs)"
                    helperText="List names and roles of lead and syndicate mentor facilitators"
                    value={trForm.facilitation_team || ''} onChange={e => setTrForm(f => ({ ...f, facilitation_team: e.target.value }))} />
                </Grid>
              </Grid>
            </Box>

            {/* ── Attendance & Participant Demographics ── */}
            <Box sx={{ p: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} color="#166534">
                  2. MSME ATTENDANCE REGISTER & DEMOGRAPHIC SUMMARY
                </Typography>
                <Chip
                  label={`Total Attending: ${(Number(trForm.participants_male_youth || 0) + Number(trForm.participants_female_youth || 0) + Number(trForm.participants_adult_male || 0) + Number(trForm.participants_adult_female || 0))}`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Box>

              {/* Attendance quick chips if recorded */}
              {(() => {
                const stats = trSession?.attendance_stats || {};
                const list = trData?.attendance_list || trSession?.attendance_list || [];
                return (
                  <>
                    {stats.total_present ? (
                      <Box sx={{ mb: 1.5, display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip label={`${stats.total_present} Recorded Present`} size="small" color="success" />
                        <Chip label={`${stats.male ?? 0} Male · ${stats.female ?? 0} Female`} size="small" variant="outlined" />
                        <Chip label={`${(stats.youth_male || 0) + (stats.youth_female || 0)} Youth`} size="small" variant="outlined" />
                        <Chip label={`${stats.refugee ?? 0} Refugee · ${stats.host_community ?? 0} Host`} size="small" variant="outlined" />
                      </Box>
                    ) : null}

                    {/* Attending MSMEs chips */}
                    {list.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                          Verified MSME Attendees:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxHeight: 80, overflowY: 'auto' }}>
                          {list.map((a, idx) => (
                            <Chip
                              key={a.id || idx}
                              label={`${a.attendee_name}${a.msme_name ? ` (${a.msme_name})` : ''}`}
                              size="small"
                              sx={{ fontSize: 11, bgcolor: '#DCFCE7', color: '#14532D' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                );
              })()}

              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="number" label="Male Youth (18–35)"
                    value={trForm.participants_male_youth ?? 0}
                    onChange={e => setTrForm(f => ({ ...f, participants_male_youth: parseInt(e.target.value) || 0 }))} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="number" label="Female Youth (18–35)"
                    value={trForm.participants_female_youth ?? 0}
                    onChange={e => setTrForm(f => ({ ...f, participants_female_youth: parseInt(e.target.value) || 0 }))} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="number" label="Adult Male (36+)"
                    value={trForm.participants_adult_male ?? 0}
                    onChange={e => setTrForm(f => ({ ...f, participants_adult_male: parseInt(e.target.value) || 0 }))} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="number" label="Adult Female (36+)"
                    value={trForm.participants_adult_female ?? 0}
                    onChange={e => setTrForm(f => ({ ...f, participants_adult_female: parseInt(e.target.value) || 0 }))} />
                </Grid>
              </Grid>
            </Box>

            {/* ── Syndicate Mentorship Reports & Synthesize Button ── */}
            {trData?.mentor_reports && trData.mentor_reports.length > 0 && (
              <Box sx={{ p: 2, bgcolor: '#FAF5FF', borderRadius: 2, border: '1px solid #E9D5FF' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="#7E22CE">
                      3. SYNDICATE MENTOR OBSERVATIONS ({trData.mentor_reports.length} Reports Submitted)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mentor BGEs have submitted small-group syndicate breakout observations for this session.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesome />}
                    onClick={synthesizeMentorReports}
                    sx={{ bgcolor: '#7E22CE', '&:hover': { bgcolor: '#6B21A8' }, fontSize: 11 }}
                  >
                    Synthesize Mentor Insights
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {trData.mentor_reports.map((mr, i) => (
                    <Accordion key={mr.id || i} sx={{ boxShadow: 'none', border: '1px solid #E9D5FF', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2" fontWeight={600} color="#6B21A8">
                          {mr.bge_name || `Mentor #${i + 1}`} · {mr.status === 'submitted' ? 'Submitted' : 'Draft'}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, fontSize: 12 }}>
                        {mr.msmes_mentored && (
                          <Typography variant="caption" display="block"><strong>MSMEs Mentored:</strong> {mr.msmes_mentored}</Typography>
                        )}
                        {mr.key_observations && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}><strong>Observations:</strong> {mr.key_observations}</Typography>
                        )}
                        {mr.challenges && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}><strong>Challenges:</strong> {mr.challenges}</Typography>
                        )}
                        {mr.recommendations && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}><strong>Recommendations:</strong> {mr.recommendations}</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Section 1: Session Purpose & Objectives ── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Section I: Purpose & Specific Objectives
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q1: Background & Purpose of the Session"
                placeholder="Outline the core purpose of the peer-to-peer session, the thematic focus (e.g. cash flow, credit readiness, inventory), and why this group session was organized."
                sx={{ mb: 2 }}
                value={trForm.training_purpose || ''}
                onChange={e => setTrForm(f => ({ ...f, training_purpose: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q2: Specific Session Objectives"
                placeholder="What targeted competencies and practical takeaways were the participating MSME owners expected to gain from this session?"
                sx={{ mb: 1 }}
                value={trForm.session_objectives || ''}
                onChange={e => setTrForm(f => ({ ...f, session_objectives: e.target.value }))}
              />
            </Box>

            <Divider />

            {/* ── Section 2: Delivery & Lessons Learned ── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Section II: Plenary Delivery & Peer Lessons
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q3: Plenary Activities Delivered & Concepts Introduced"
                placeholder="Summarize plenary topics, interactive exercises delivered, tools and templates demonstrated (e.g., cash flow sheets, loan application packaging, inventory logs)."
                sx={{ mb: 2 }}
                value={trForm.activities_delivered || ''}
                onChange={e => setTrForm(f => ({ ...f, activities_delivered: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q4: Key Lessons Learned & Peer Exchanges"
                placeholder="How did enterprise owners interact, share failure experiences, cross-pollinate solutions, and engage in problem-solving?"
                sx={{ mb: 1 }}
                value={trForm.key_lessons || ''}
                onChange={e => setTrForm(f => ({ ...f, key_lessons: e.target.value }))}
              />
            </Box>

            <Divider />

            {/* ── Section 3: Diagnostic Findings & Mentorship Dynamics ── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Section III: Diagnostic Findings & Mentorship Dynamics
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q5: Growth Support Areas & BDS Opportunities Discovered"
                placeholder="Opportunities discovered for bulk purchasing, joint transport, cooperative branding, market linkages, or clustering."
                sx={{ mb: 2 }}
                value={trForm.growth_support_areas || ''}
                onChange={e => setTrForm(f => ({ ...f, growth_support_areas: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q6: Key Diagnostic Findings & Systemic Bottlenecks"
                placeholder="Top common operational, financial, and regulatory challenges identified across the participating MSMEs (e.g., bookkeeping, machinery power supply, packaging)."
                sx={{ mb: 2 }}
                value={trForm.key_findings || ''}
                onChange={e => setTrForm(f => ({ ...f, key_findings: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q7: Mentorship Dynamics & BGE Contributions"
                placeholder="Observations from small-group syndicate breakouts, mentor BGE facilitation effectiveness, and diagnostic capacity gaps observed."
                sx={{ mb: 1 }}
                value={trForm.bge_contributions || ''}
                onChange={e => setTrForm(f => ({ ...f, bge_contributions: e.target.value }))}
              />
            </Box>

            <Divider />

            {/* ── Section 4: 30-Day Commitments, Recommendations & Sign-off ── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Section IV: 30-Day Action Commitments & Way Forward
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q8: Proposed BDS Actions & MSME 30-Day Commitments (Acid Test)"
                placeholder="Tangible, measurable operational commitments made by MSME owners to execute within 30 days (e.g. open separate bank account, introduce inventory log, complete UNBS checklist)."
                sx={{ mb: 2 }}
                value={trForm.bds_actions || ''}
                onChange={e => setTrForm(f => ({ ...f, bds_actions: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q9: Recommendations for Future Sessions & Management Support"
                placeholder="Required specialist technical co-assignments, financial linkages, or programme inputs needed from PRUDEV II / GOPA Pro."
                sx={{ mb: 2 }}
                value={trForm.recommendations || ''}
                onChange={e => setTrForm(f => ({ ...f, recommendations: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q10: Agreed Next Steps & Implementation Roadmap"
                placeholder="Immediate follow-up actions to be verified by BGEs during 1-on-1 coaching visits over the next 4–6 weeks."
                sx={{ mb: 2 }}
                value={trForm.next_steps || ''}
                onChange={e => setTrForm(f => ({ ...f, next_steps: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Q11: Summary Conclusion & Lead Facilitator Declaration"
                placeholder="Executive summary of session outcomes and certification of delivery according to PRUDEV II guidelines."
                value={trForm.conclusion || ''}
                onChange={e => setTrForm(f => ({ ...f, conclusion: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setTrDialog(false)} disabled={trSaving}>Cancel</Button>
            {trData?.id && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => openTrPdf(trData.id, 'view')}
                disabled={trSaving}
              >
                Download PDF
              </Button>
            )}
            <Button variant="outlined" onClick={() => saveTrReport(false)} disabled={trSaving}>
              {trSaving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button variant="contained" color="success" onClick={() => saveTrReport(true)} disabled={trSaving}>
              {trSaving ? 'Submitting…' : 'Submit Report'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Create Peer-to-Peer Session Dialog ─────────────────────────────── */}
      {p2pSessionDialog && (
        <Dialog open onClose={() => setP2pSessionDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>
              Create Peer-to-Peer Learning Session
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure session facilitation (Lead BGE & Mentor BGEs) and select participating MSMEs from the master database.
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Session Details */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} color={BRAND.primaryMain} sx={{ mb: 1.5 }}>
                SESSION DETAILS & VENUE
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Session Title *"
                    value={p2pForm.title}
                    onChange={e => setP2pForm(f => ({ ...f, title: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Start Date *"
                    InputLabelProps={{ shrink: true }}
                    value={p2pForm.date}
                    onChange={e => setP2pForm(f => ({ ...f, date: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="End Date (Optional)"
                    InputLabelProps={{ shrink: true }}
                    value={p2pForm.end_date}
                    onChange={e => setP2pForm(f => ({ ...f, end_date: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Venue / Location *"
                    placeholder="e.g. Commercial Hall, Gulu"
                    value={p2pForm.location}
                    onChange={e => setP2pForm(f => ({ ...f, location: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="District"
                    placeholder="e.g. Gulu / Lira / Arua"
                    value={p2pForm.district}
                    onChange={e => setP2pForm(f => ({ ...f, district: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Thematic Topic</InputLabel>
                    <Select
                      value={p2pForm.topic || ''}
                      label="Thematic Topic"
                      onChange={e => setP2pForm(f => ({ ...f, topic: e.target.value }))}
                    >
                      <MenuItem value=""><em>None / Custom</em></MenuItem>
                      {allTopics.map(t => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description / Key Focus"
                    placeholder="e.g. Cash flow management & loan packaging"
                    value={p2pForm.description}
                    onChange={e => setP2pForm(f => ({ ...f, description: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Facilitation Team */}
            <Box sx={{ p: 2, bgcolor: '#FAF5FF', borderRadius: 2, border: '1px solid #E9D5FF' }}>
              <Typography variant="subtitle2" fontWeight={700} color="#7E22CE" sx={{ mb: 1.5 }}>
                FACILITATION ROLES: LEAD BGE & MENTOR BGES
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Lead BGE *</InputLabel>
                    <Select
                      value={p2pForm.lead_bge_id || ''}
                      label="Lead BGE *"
                      onChange={e => {
                        const newLead = e.target.value;
                        setP2pForm(f => ({
                          ...f,
                          lead_bge_id: newLead,
                          mentor_bge_ids: f.mentor_bge_ids.filter(id => id !== newLead)
                        }));
                      }}
                    >
                      {allExperts.map(exp => (
                        <MenuItem key={exp.id} value={exp.id}>
                          {exp.name} {exp.bge_code ? `(${exp.bge_code})` : ''} {exp.district ? `· ${exp.district}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mentor BGE(s)</InputLabel>
                    <Select
                      multiple
                      value={p2pForm.mentor_bge_ids}
                      label="Mentor BGE(s)"
                      onChange={e => {
                        const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setP2pForm(f => ({ ...f, mentor_bge_ids: val }));
                      }}
                      renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(id => {
                            const exp = allExperts.find(x => x.id === id);
                            return (
                              <Chip
                                key={id}
                                label={exp ? exp.name : id}
                                size="small"
                                sx={{ height: 22, fontSize: 11, bgcolor: '#F3E5F5', color: '#7B1FA2' }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {allExperts
                        .filter(exp => exp.id !== p2pForm.lead_bge_id)
                        .map(exp => (
                          <MenuItem key={exp.id} value={exp.id}>
                            <Checkbox checked={p2pForm.mentor_bge_ids.indexOf(exp.id) > -1} size="small" />
                            <ListItemText primary={`${exp.name}${exp.district ? ` (${exp.district})` : ''}`} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* MSME Participant Multi-Select (Wired to live database) */}
            <Box sx={{ p: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#166534">
                    PARTICIPATING MSMES (LIVE DATABASE)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select enterprise participants to auto-populate the session roster, attendance register, and 30-day commitments.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={`${p2pForm.msme_ids.length} Selected`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                  {p2pForm.msme_ids.length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => setP2pForm(f => ({ ...f, msme_ids: [] }))}
                      sx={{ fontSize: 11 }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Search / Filter input */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search MSMEs by business name, owner, or district…"
                value={p2pSearchMsme}
                onChange={e => setP2pSearchMsme(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5, bgcolor: '#fff' }}
              />

              {/* MSME List */}
              {(() => {
                const filtered = (trainingMsmes || []).filter(m => {
                  if (!p2pSearchMsme) return true;
                  const q = p2pSearchMsme.toLowerCase();
                  return (
                    (m.business_name || m.name || '').toLowerCase().includes(q) ||
                    (m.owner_name || '').toLowerCase().includes(q) ||
                    (m.district || '').toLowerCase().includes(q) ||
                    (m.sector || '').toLowerCase().includes(q)
                  );
                });

                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Showing {filtered.length} of {trainingMsmes.length} MSMEs
                      </Typography>
                      {filtered.length > 0 && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            const newIds = filtered.map(m => m.id);
                            setP2pForm(f => ({
                              ...f,
                              msme_ids: Array.from(new Set([...f.msme_ids, ...newIds]))
                            }));
                          }}
                          sx={{ fontSize: 11, color: '#166534', p: 0 }}
                        >
                          Select All Filtered
                        </Button>
                      )}
                    </Box>

                    <Paper variant="outlined" sx={{ maxHeight: 240, overflowY: 'auto', bgcolor: '#fff' }}>
                      {filtered.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {trainingMsmes.length === 0 ? 'Loading MSMEs from database…' : 'No MSMEs match your search.'}
                          </Typography>
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {filtered.map(m => {
                            const isSelected = p2pForm.msme_ids.includes(m.id);
                            return (
                              <ListItemButton
                                key={m.id}
                                onClick={() => {
                                  setP2pForm(f => ({
                                    ...f,
                                    msme_ids: isSelected ? f.msme_ids.filter(x => x !== m.id) : [...f.msme_ids, m.id]
                                  }));
                                }}
                                sx={{
                                  borderBottom: '1px solid #F1F5F9',
                                  bgcolor: isSelected ? '#DCFCE7' : 'transparent',
                                  '&:hover': { bgcolor: isSelected ? '#BBF7D0' : '#F8FAFC' },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox edge="start" checked={isSelected} size="small" tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {m.business_name || m.name}
                                      </Typography>
                                      {m.district && (
                                        <Chip label={m.district} size="small" sx={{ height: 18, fontSize: 10 }} />
                                      )}
                                      {m.sector && (
                                        <Chip label={m.sector} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      Owner: {m.owner_name || '—'} {m.contact_phone ? `· ${m.contact_phone}` : ''}
                                      {m.assigned_bge_name ? ` · BGE: ${m.assigned_bge_name}` : ''}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      )}
                    </Paper>
                  </Box>
                );
              })()}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setP2pSessionDialog(false)} disabled={p2pSessionSaving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveP2pSession}
              disabled={p2pSessionSaving}
              sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark } }}
            >
              {p2pSessionSaving ? 'Creating…' : 'Create Session'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Mentor report dialog ────────────────────────────────────────────── */}
      {mrDialog && (
        <Dialog open onClose={() => setMrDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 0.5 }}>
            {mrData ? 'Edit Mentor Report' : 'Write Mentor Report'}
            <Typography variant="caption" display="block" color="text.secondary">
              {mrSession?.title} · {mrSession?.date}
              {mrData?.status === 'submitted' && <Chip label="Submitted" size="small" color="success" sx={{ ml: 1, fontSize: 10 }} />}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            {/* ── Attendance summary (from lead's sheet) ── */}
            {(() => {
              const stats = mrSession?.attendance_stats || {};
              const list  = mrSession?.attendance_list  || [];
              if (!stats.total_present) return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Attendance has not been recorded yet. The lead facilitator will fill in the attendance sheet.
                </Alert>
              );
              return (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    ATTENDANCE SUMMARY (from lead's sheet)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip label={`${stats.total_present} present`} size="small" color="success" />
                    <Chip label={`${stats.male ?? 0} M · ${stats.female ?? 0} F`} size="small" variant="outlined" />
                    <Chip label={`${stats.youth_male + stats.youth_female} youth`} size="small" variant="outlined" />
                    <Chip label={`${stats.refugee ?? 0} refugee · ${stats.host_community ?? 0} host`} size="small" variant="outlined" />
                  </Box>
                  {list.length > 0 && (
                    <TableContainer sx={{ maxHeight: 180 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Name</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>MSME</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Sex</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Age</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {list.map((a, i) => (
                            <TableRow key={a.id ?? i} hover>
                              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{i + 1}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{a.attendee_name}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{a.msme_name || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{a.gender || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{a.age_group || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{a.refugee_status === 'R' ? 'Refugee' : 'Host'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              );
            })()}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Date(s)"
                value={mrForm.training_dates || ''} onChange={e => setMrForm(f => ({ ...f, training_dates: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Venue"
                value={mrForm.venue || ''} onChange={e => setMrForm(f => ({ ...f, venue: e.target.value }))} /></Grid>
            </Grid>
            {[
              { key: 'mentoring_activities', label: 'Mentoring Activities' },
              { key: 'msmes_mentored',       label: 'MSMEs / Participants Mentored' },
              { key: 'key_observations',     label: 'Key Observations' },
              { key: 'challenges',           label: 'Challenges' },
              { key: 'recommendations',      label: 'Recommendations' },
              { key: 'next_steps',           label: 'Next Steps' },
            ].map(({ key, label }) => (
              <TextField key={key} fullWidth multiline minRows={2} size="small" label={label} sx={{ mb: 2 }}
                value={mrForm[key] || ''} onChange={e => setMrForm(f => ({ ...f, [key]: e.target.value }))} />
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setMrDialog(false)} disabled={mrSaving}>Cancel</Button>
            <Button variant="outlined" onClick={() => saveMrReport(false)} disabled={mrSaving}>
              {mrSaving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button variant="contained" color="success" onClick={() => saveMrReport(true)} disabled={mrSaving}>
              {mrSaving ? 'Submitting…' : 'Submit Report'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Participant Training Report dialog ───────────────────────────── */}
      {ptDialog && (
        <Dialog open onClose={() => setPtDialog(false)} maxWidth="md" fullWidth
          PaperProps={{ sx: { maxHeight: '95vh' } }}>
          <DialogTitle sx={{ pb: 0.5 }}>
            {ptEdit ? 'Edit Participant Training Report' : 'New Participant Training Report'}
            <Typography variant="caption" display="block" color="text.secondary">
              Report for training you attended as a participant (not as a facilitator)
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}><TextField fullWidth size="small" label="Training Title *"
                value={ptForm.training_title} onChange={e => setPtForm(f => ({ ...f, training_title: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Training Dates (e.g. 17–19 Feb 2026)"
                value={ptForm.training_dates} onChange={e => setPtForm(f => ({ ...f, training_dates: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Facilitator / Trainer Name"
                value={ptForm.facilitator_name} onChange={e => setPtForm(f => ({ ...f, facilitator_name: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Venue"
                value={ptForm.venue} onChange={e => setPtForm(f => ({ ...f, venue: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="District"
                value={ptForm.district} onChange={e => setPtForm(f => ({ ...f, district: e.target.value }))} /></Grid>
            </Grid>

            {[
              { key: 'topics_covered',         label: 'Topics / Modules Covered', rows: 3 },
              { key: 'key_learnings',          label: 'Key Learnings & Insights', rows: 3 },
              { key: 'practical_application',  label: 'How will you apply these learnings with your MSMEs?', rows: 3 },
              { key: 'msmes_as_test_subjects', label: 'MSMEs Used as Practice Subjects During Training', rows: 2 },
              { key: 'challenges',             label: 'Challenges Encountered', rows: 2 },
              { key: 'action_plan',            label: 'Action Plan & Next Steps', rows: 3 },
            ].map(({ key, label, rows }) => (
              <TextField key={key} fullWidth multiline minRows={rows} size="small" label={label} sx={{ mb: 2 }}
                value={ptForm[key] || ''} onChange={e => setPtForm(f => ({ ...f, [key]: e.target.value }))} />
            ))}

            {/* Attendance register */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Attendance Register — BGE Participants
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {['Name *', 'BGE Code', 'Phone', 'Gender (M/F)', 'Organisation'].map((lbl, i) => {
                  const keys = ['name', 'bge_code', 'phone', 'gender', 'organisation'];
                  return (
                    <TextField key={i} size="small" label={lbl} sx={{ flex: '1 1 100px', minWidth: 80 }}
                      value={ptAttendeeRow[keys[i]] || ''}
                      onChange={e => setPtAttendeeRow(r => ({ ...r, [keys[i]]: e.target.value }))} />
                  );
                })}
                <Button variant="outlined" size="small"
                  onClick={() => {
                    if (!ptAttendeeRow.name.trim()) return;
                    setPtForm(f => ({ ...f, attendees: [...(f.attendees || []), { ...ptAttendeeRow }] }));
                    setPtAttendeeRow({ name: '', bge_code: '', phone: '', gender: '', organisation: '' });
                  }}>
                  Add
                </Button>
              </Box>
              {(ptForm.attendees || []).length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        {['#', 'Name', 'BGE Code', 'Phone', 'Gender', 'Organisation', ''].map(h => (
                          <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ptForm.attendees.map((a, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontSize: 11 }}>{i + 1}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{a.name}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{a.bge_code || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{a.phone || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{a.gender || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{a.organisation || '—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => setPtForm(f => ({ ...f, attendees: f.attendees.filter((_, j) => j !== i) }))}>
                              <Close fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setPtDialog(false)} disabled={ptSaving}>Cancel</Button>
            <Button variant="outlined" onClick={() => savePtReport(false)} disabled={ptSaving}>
              {ptSaving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button variant="contained" color="success" onClick={() => savePtReport(true)} disabled={ptSaving}>
              {ptSaving ? 'Submitting…' : 'Submit Report'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Attendance dialog (lead — editable) ─────────────────────────── */}
      {attDialog && (
        <Dialog open onClose={() => setAttDialog(false)} maxWidth="xl" fullWidth
          PaperProps={{ sx: { height: { xs: '95dvh', md: '88vh' }, display: 'flex', flexDirection: 'column' } }}>
          <DialogTitle sx={{ pb: 0.5 }}>
            <Typography fontWeight={700}>Attendance — {attSession?.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {attSession?.date}{attSession?.location ? ` · ${attSession.location}` : ''}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
            {attLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                      <TableCell sx={{ width: 36, fontWeight: 700, fontSize: 11 }}>#</TableCell>
                      <TableCell sx={{ minWidth: 160, fontWeight: 700, fontSize: 11 }}>NAME</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 700, fontSize: 11 }}>PHONE</TableCell>
                      <TableCell sx={{ minWidth: 180, fontWeight: 700, fontSize: 11 }}>MSME / BUSINESS</TableCell>
                      <TableCell sx={{ minWidth: 70,  fontWeight: 700, fontSize: 11 }}>SEX</TableCell>
                      <TableCell sx={{ minWidth: 100, fontWeight: 700, fontSize: 11 }}>AGE GROUP</TableCell>
                      <TableCell sx={{ minWidth: 90,  fontWeight: 700, fontSize: 11 }}>STATUS</TableCell>
                      <TableCell sx={{ minWidth: 80,  fontWeight: 700, fontSize: 11 }}>CONSENT</TableCell>
                      <TableCell sx={{ width: 36 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attRows.map((row, idx) => (
                      <TableRow key={row._key} hover>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="Full name" value={row.attendee_name}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, attendee_name: e.target.value } : r))}
                            sx={{ minWidth: 140 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="Phone" value={row.attendee_phone}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, attendee_phone: e.target.value } : r))}
                            sx={{ minWidth: 100 }} />
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" displayEmpty value={row.msme || ''}
                            onChange={e => {
                              const msmeList = trainingMsmes.length ? trainingMsmes : msmes;
                              const m = msmeList.find(x => x.id === e.target.value);
                              setAttRows(p => p.map(r => r._key === row._key
                                ? { ...r, msme: e.target.value, attendee_name: r.attendee_name || (m?.owner_name || '') }
                                : r));
                            }}
                            sx={{ minWidth: 150 }}>
                            <MenuItem value=""><em>— walk-in —</em></MenuItem>
                            {(trainingMsmes.length ? trainingMsmes : msmes).map(m => (
                              <MenuItem key={m.id} value={m.id}>{m.business_name}</MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" displayEmpty value={row.gender || ''}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, gender: e.target.value } : r))}
                            sx={{ minWidth: 55 }}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            <MenuItem value="M">M</MenuItem>
                            <MenuItem value="F">F</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" displayEmpty value={row.age_group || ''}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, age_group: e.target.value } : r))}
                            sx={{ minWidth: 85 }}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            {['18-34', '35-45', '46-55', '56+'].map(ag => <MenuItem key={ag} value={ag}>{ag}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" value={row.refugee_status || 'H'}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, refugee_status: e.target.value } : r))}
                            sx={{ minWidth: 75 }}>
                            <MenuItem value="H">Host</MenuItem>
                            <MenuItem value="R">Refugee</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Checkbox size="small" checked={!!row.consent_photo}
                            onChange={e => setAttRows(p => p.map(r => r._key === row._key ? { ...r, consent_photo: e.target.checked } : r))} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setAttRows(p => p.filter(r => r._key !== row._key))}>
                            <Delete fontSize="small" sx={{ color: 'error.main' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
            <Button size="small" startIcon={<Add />} onClick={() => setAttRows(p => [...p, newAttRow()])}>
              Add row
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setAttDialog(false)} disabled={attLoading}>Cancel</Button>
              <Button variant="contained" onClick={saveAttendance} disabled={attLoading}>
                {attLoading ? <CircularProgress size={18} /> : 'Save Attendance'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Edit MSME Status, Location & Alternative Contact Dialog ── */}
      <Dialog open={editMsmeDialog} onClose={() => !editMsmeSaving && setEditMsmeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontWeight={700} fontSize={17}>Edit MSME Information</Typography>
            <Typography variant="caption" color="text.secondary">{editMsmeForm.business_name}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setEditMsmeDialog(false)} disabled={editMsmeSaving}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {editMsmeError && (
            <Alert severity="error" sx={{ mb: 2 }}>{editMsmeError}</Alert>
          )}

          {/* 1. Operational Status */}
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Operational Status
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2.5 }}>
            {[
              { key: 'active', label: 'Active', desc: 'Operating normally', cfg: MSME_STATUS_CONFIG.active },
              { key: 'temporarily_closed', label: 'Temporarily Closed', desc: 'Seasonal/temporary shutdown', cfg: MSME_STATUS_CONFIG.temporarily_closed },
              { key: 'out_of_business', label: 'Out of Business', desc: 'Permanently ceased operations', cfg: MSME_STATUS_CONFIG.out_of_business },
              { key: 'unavailable', label: 'Unavailable', desc: 'Cannot be reached/relocated', cfg: MSME_STATUS_CONFIG.unavailable },
            ].map(st => {
              const isSelected = editMsmeForm.status === st.key;
              return (
                <Grid item xs={6} key={st.key}>
                  <Box
                    onClick={() => setEditMsmeForm(f => ({ ...f, status: st.key }))}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      border: `2px solid ${isSelected ? st.cfg.border : '#E0E0E0'}`,
                      bgcolor: isSelected ? st.cfg.bgcolor : '#FAFAFA',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: st.cfg.bgcolor, borderColor: st.cfg.border },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: st.cfg.textColor }} />
                      <Typography fontSize={13} fontWeight={isSelected ? 700 : 600} color={isSelected ? st.cfg.textColor : 'text.primary'}>
                        {st.label}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontSize={11} display="block" sx={{ pl: 1.75 }}>
                      {st.desc}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 2. Location */}
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Location
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select
                  value={editMsmeForm.district}
                  label="District"
                  onChange={e => setEditMsmeForm(f => ({ ...f, district: e.target.value }))}
                >
                  <MenuItem value=""><em>— Select District —</em></MenuItem>
                  {NORTHERN_UGANDA_DISTRICTS.map(d => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                  {editMsmeForm.district && !NORTHERN_UGANDA_DISTRICTS.includes(editMsmeForm.district) && (
                    <MenuItem value={editMsmeForm.district}>{editMsmeForm.district}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="City / Town"
                value={editMsmeForm.city}
                onChange={e => setEditMsmeForm(f => ({ ...f, city: e.target.value }))}
                placeholder="e.g. Gulu City, Lira Town, Layibi"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Physical Address / Landmark"
                value={editMsmeForm.address}
                onChange={e => setEditMsmeForm(f => ({ ...f, address: e.target.value }))}
                placeholder="e.g. Plot 14 Main Street, opposite Central Market"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    GPS COORDINATES (LATITUDE / LONGITUDE)
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color={editMsmeForm.latitude ? 'success' : 'primary'}
                    startIcon={editMsmeForm._gpsLoading ? <CircularProgress size={12} /> : (editMsmeForm.latitude ? <GpsFixed sx={{ fontSize: 14 }} /> : <GpsNotFixed sx={{ fontSize: 14 }} />)}
                    disabled={editMsmeForm._gpsLoading}
                    onClick={() => {
                      if (!navigator.geolocation) {
                        notify('Geolocation is not supported by this device/browser.', 'error');
                        return;
                      }
                      setEditMsmeForm(f => ({ ...f, _gpsLoading: true }));
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          setEditMsmeForm(f => ({
                            ...f,
                            latitude:  pos.coords.latitude.toFixed(6),
                            longitude: pos.coords.longitude.toFixed(6),
                            _gpsLoading: false,
                          }));
                          notify('Captured live location coordinates ✓', 'success');
                        },
                        (err) => {
                          setEditMsmeForm(f => ({ ...f, _gpsLoading: false }));
                          notify(err.code === 1 ? 'Location permission denied' : 'GPS location unavailable', 'error');
                        },
                        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
                      );
                    }}
                    sx={{ fontSize: 11, py: 0.25, textTransform: 'none' }}
                  >
                    {editMsmeForm._gpsLoading ? 'Capturing GPS…' : editMsmeForm.latitude ? 'Recapture Live Location' : 'Capture Live Location'}
                  </Button>
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Latitude" type="number"
                      value={editMsmeForm.latitude || ''}
                      onChange={e => setEditMsmeForm(f => ({ ...f, latitude: e.target.value }))}
                      inputProps={{ step: 'any' }}
                      helperText="e.g. 2.774950"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Longitude" type="number"
                      value={editMsmeForm.longitude || ''}
                      onChange={e => setEditMsmeForm(f => ({ ...f, longitude: e.target.value }))}
                      inputProps={{ step: 'any' }}
                      helperText="e.g. 32.299110"
                    />
                  </Grid>
                </Grid>
                {editMsmeForm.latitude && editMsmeForm.longitude && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 600 }}>
                      📍 {Number(editMsmeForm.latitude).toFixed(6)}, {Number(editMsmeForm.longitude).toFixed(6)}
                    </Typography>
                    <a href={`https://maps.google.com/?q=${editMsmeForm.latitude},${editMsmeForm.longitude}`}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: BRAND.primaryMain, textDecoration: 'none', fontWeight: 600 }}>
                      View on Google Maps ↗
                    </a>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 3. Primary Contact */}
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Primary Contact
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Owner / Contact Person"
                value={editMsmeForm.owner_name}
                onChange={e => setEditMsmeForm(f => ({ ...f, owner_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Primary Phone"
                value={editMsmeForm.phone}
                onChange={e => setEditMsmeForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="e.g. 0772123456"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Primary Email"
                value={editMsmeForm.email}
                onChange={e => setEditMsmeForm(f => ({ ...f, email: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 4. Alternative Contact */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Alternative / Secondary Contact
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Backup person to reach if the primary contact is unavailable
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Alternative Contact Name"
                value={editMsmeForm.alt_contact_name}
                onChange={e => setEditMsmeForm(f => ({ ...f, alt_contact_name: e.target.value }))}
                placeholder="e.g. Okello David"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Alternative Phone"
                value={editMsmeForm.alt_phone}
                onChange={e => setEditMsmeForm(f => ({ ...f, alt_phone: e.target.value }))}
                placeholder="e.g. 0782987654"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Relationship / Role"
                value={editMsmeForm.alt_contact_role}
                onChange={e => setEditMsmeForm(f => ({ ...f, alt_contact_role: e.target.value }))}
                placeholder="e.g. Manager, Caretaker, Spouse, Accountant"
              />
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {['Manager', 'Caretaker', 'Spouse', 'Accountant', 'Partner'].map(role => (
                  <Chip
                    key={role}
                    size="small"
                    label={role}
                    variant={editMsmeForm.alt_contact_role === role ? 'filled' : 'outlined'}
                    color={editMsmeForm.alt_contact_role === role ? 'primary' : 'default'}
                    onClick={() => setEditMsmeForm(f => ({ ...f, alt_contact_role: role }))}
                    sx={{ fontSize: 10, height: 20, cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Alternative Email"
                value={editMsmeForm.alt_email}
                onChange={e => setEditMsmeForm(f => ({ ...f, alt_email: e.target.value }))}
                placeholder="e.g. manager@example.com"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setEditMsmeDialog(false)} disabled={editMsmeSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEditMsme}
            disabled={editMsmeSaving}
            sx={{ bgcolor: BRAND.primaryMain }}
          >
            {editMsmeSaving ? <CircularProgress size={18} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MSME detail dialog ── */}
      <Dialog open={msmeDetailDialog} onClose={() => setMsmeDetailDialog(false)} maxWidth="md" fullWidth>
        {selectedMsme && (() => {
          const m = selectedMsme;
          const hasDiag = !!m.diag_imported_at;
          const Field = ({ label, value, xs = 6 }) => value ? (
            <Grid item xs={xs}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography fontSize={13} fontWeight={500}>{value}</Typography>
            </Grid>
          ) : null;
          const BoolBadge = ({ label, value }) => value == null ? null : (
            <Chip size="small" label={label}
              color={value ? 'success' : 'default'} variant={value ? 'filled' : 'outlined'}
              sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
          );
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography fontWeight={700} fontSize={17}>{m.business_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.msme_code}</Typography>
                    {(m.programme_groups_detail || []).map(g => (
                      <Chip key={g.id} size="small" label={g.name} sx={{ ml: 0.5, fontSize: 10, bgcolor: g.color, color: '#fff' }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<Edit />}
                      onClick={() => openEditMsme(m)}>
                      Edit Details
                    </Button>
                    <Button variant="contained" size="small" startIcon={<Add />}
                      onClick={() => { setMsmeDetailDialog(false); openNewReport(m.id); }}>
                      New Report
                    </Button>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent dividers sx={{ p: 0 }}>
                <Tabs value={msmeDetailTab} onChange={(_, v) => setMsmeDetailTab(v)}
                  sx={{ borderBottom: '1px solid #E8EDF2', px: 2, minHeight: 40 }}
                  TabIndicatorProps={{ style: { height: 3 } }}>
                  <Tab label="Profile" sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
                  <Tab label={`Reports (${msmeReports.length})`} sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
                  <Tab label={`Growth History (${msmeDetailSnapshots.length})`} sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
                  <Tab label="Planned Visits" sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
                </Tabs>

                {/* ── Tab 0: Profile ── */}
                {msmeDetailTab === 0 && (
                  <>
                    <Box sx={{ p: 2, bgcolor: '#F8F9FA', borderBottom: '1px solid #E8EDF2' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>Business Profile</Typography>
                        {(() => {
                          const sInfo = getMsmeStatusInfo(m.status);
                          return (
                            <Chip
                              label={sInfo.label}
                              size="small"
                              sx={{
                                bgcolor: sInfo.bgcolor,
                                color: sInfo.textColor,
                                border: `1px solid ${sInfo.border}`,
                                fontWeight: 700,
                                fontSize: 11,
                                height: 22,
                              }}
                            />
                          );
                        })()}
                      </Box>
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        <Field label="Owner"         value={m.owner_name} />
                        <Field label="Phone"          value={m.phone} />
                        <Field label="Email"          value={m.email || m.business_email} />
                        <Field label="Sector"         value={m.sector} />
                        <Field label="Business Type"  value={m.business_type} />
                        <Field label="Cohort"         value={m.cohort_name} />
                        <Field label="District"       value={m.district || m.state || m.diag_district} />
                        <Field label="City / Town"    value={m.city} />
                        <Field label="Address"        value={m.address} />
                        <Field label="Assigned BGE"   value={m.assigned_bge_name} />
                        <Field label="Registration #" value={m.registration_number} />
                      </Grid>

                      {(m.alt_contact_name || m.alt_phone || m.alt_email) && (
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #CFD8DC' }}>
                          <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10} display="block" sx={{ mb: 0.5 }}>
                            Alternative / Secondary Contact
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Field label="Contact Person" value={m.alt_contact_name} />
                            <Field label="Role / Relationship" value={m.alt_contact_role} />
                            <Field label="Phone" value={m.alt_phone} />
                            <Field label="Email" value={m.alt_email} />
                          </Grid>
                        </Box>
                      )}
                    </Box>

                    {hasDiag && (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>
                          Diagnostic Baseline
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          <Field label="Annual Turnover (band)" value={m.diag_annual_turnover} />
                          <Field label="Total Assets (band)"    value={m.diag_total_assets} />
                          <Field label="Years Operating"        value={m.diag_years_operating} />
                          <Field label="Owner Sex"              value={m.diag_owner_sex} />
                          <Field label="Owner Age"              value={m.diag_owner_age} />
                          <Field label="Owner Education"        value={m.diag_owner_education} />
                        </Grid>
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>Workforce at baseline</Typography>
                          <Grid container spacing={1}>
                            {[
                              { label: 'FT Male',   val: m.diag_employees_ft_male },
                              { label: 'FT Female', val: m.diag_employees_ft_female },
                              { label: 'PT Male',   val: m.diag_employees_pt_male },
                              { label: 'PT Female', val: m.diag_employees_pt_female },
                            ].map(({ label, val }) => val != null ? (
                              <Grid item key={label}>
                                <Box sx={{ textAlign: 'center', px: 1.5, py: 0.75, bgcolor: '#E8EDF2', borderRadius: 1 }}>
                                  <Typography fontWeight={700} fontSize={16}>{val}</Typography>
                                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                                </Box>
                              </Grid>
                            ) : null)}
                          </Grid>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Compliance & Access at Baseline</Typography>
                          <BoolBadge label="TIN"           value={m.diag_has_tin} />
                          <BoolBadge label="URSB"          value={m.diag_has_unbs} />
                          <BoolBadge label="Business Bank" value={m.diag_has_business_bank} />
                          <BoolBadge label="Mobile Money"  value={m.diag_has_mobile_money} />
                        </Box>
                        {m.diag_is_green_business && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Green Categories</Typography>
                            {(m.diag_green_categories || []).map((cat, i) => (
                              <Chip key={i} size="small" label={cat} color="success" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {/* ── Tab 1: Reports ── */}
                {msmeDetailTab === 1 && (
                  <Box sx={{ p: 2 }}>
                    {msmeReports.length === 0 ? (
                      <Typography color="text.secondary" fontSize={13}>No visit reports yet.</Typography>
                    ) : (
                      msmeReports.map(r => (
                        <Box key={r.id} sx={{ border: '1px solid #E8EDF2', borderRadius: 2, p: 1.5, mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography fontSize={13} fontWeight={600}>{VISIT_TYPE_LABELS[r.visit_type] || r.visit_type}</Typography>
                              <Typography fontSize={11} color="text.secondary">{r.visit_date}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip label={r.status} size="small" color={STATUS_COLORS[r.status] || 'default'} />
                              {r.status === 'draft' && (
                                <IconButton size="small" onClick={() => { setMsmeDetailDialog(false); openEditReport(r); }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                )}

                {/* ── Tab 2: Growth History ── */}
                {msmeDetailTab === 2 && (
                  <Box sx={{ p: 2 }}>
                    {msmeDetailSnapshots.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary" fontSize={13}>No growth updates recorded yet.</Typography>
                        <Button size="small" variant="outlined" color="success" sx={{ mt: 1 }}
                          onClick={() => { setMsmeDetailDialog(false); openGrowthForm(m); }}>
                          Record First Update
                        </Button>
                      </Box>
                    ) : (
                      [...msmeDetailSnapshots].reverse().map((s, i) => {
                        const prev = [...msmeDetailSnapshots].reverse()[i + 1];
                        const revDelta = prev?.annual_turnover && s.annual_turnover
                          ? ((Number(s.annual_turnover) - Number(prev.annual_turnover)) / Number(prev.annual_turnover) * 100).toFixed(1)
                          : null;
                        return (
                          <Box key={s.id} sx={{ border: '1px solid #E8EDF2', borderRadius: 1.5, p: 2, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography fontSize={13} fontWeight={700}>{s.snapshot_date}</Typography>
                                {i === 0 && <Chip size="small" label="Latest" color="success" sx={{ fontSize: 10 }} />}
                              </Box>
                              <Chip size="small" label={s.source?.replace('_', ' ')} variant="outlined" sx={{ fontSize: 10 }} />
                            </Box>

                            {/* Financials */}
                            {(s.annual_turnover || s.last_month_revenue || s.total_assets) && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="overline" fontSize={9} color="text.secondary">Financials (UGX)</Typography>
                                <Grid container spacing={1} sx={{ mt: 0 }}>
                                  {s.annual_turnover && (
                                    <Grid item xs={6}>
                                      <Typography fontSize={11} color="text.secondary">Annual Revenue</Typography>
                                      <Typography fontSize={13} fontWeight={600}>
                                        {Number(s.annual_turnover).toLocaleString()}
                                        {revDelta !== null && (
                                          <Box component="span" sx={{ ml: 0.5, fontSize: 11, color: Number(revDelta) >= 0 ? 'success.main' : 'error.main' }}>
                                            {Number(revDelta) >= 0 ? '▲' : '▼'}{Math.abs(revDelta)}%
                                          </Box>
                                        )}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {s.last_month_revenue && (
                                    <Grid item xs={6}>
                                      <Typography fontSize={11} color="text.secondary">Last Month</Typography>
                                      <Typography fontSize={13} fontWeight={600}>{Number(s.last_month_revenue).toLocaleString()}</Typography>
                                    </Grid>
                                  )}
                                  {s.total_assets && (
                                    <Grid item xs={6}>
                                      <Typography fontSize={11} color="text.secondary">Total Assets</Typography>
                                      <Typography fontSize={13} fontWeight={600}>{Number(s.total_assets).toLocaleString()}</Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}

                            {/* Workforce */}
                            {(s.employees_ft_male != null || s.employees_ft_female != null) && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="overline" fontSize={9} color="text.secondary">Workforce</Typography>
                                <Typography fontSize={12}>
                                  FT: {(s.employees_ft_male || 0) + (s.employees_ft_female || 0)} ({s.employees_ft_female || 0}F)
                                  {(s.employees_pt_male != null || s.employees_pt_female != null) &&
                                    ` · PT: ${(s.employees_pt_male || 0) + (s.employees_pt_female || 0)}`}
                                  {(s.employees_ft_refugee != null || s.employees_pt_refugee != null) &&
                                    ` · ${(s.employees_ft_refugee || 0) + (s.employees_pt_refugee || 0)} refugees`}
                                </Typography>
                              </Box>
                            )}

                            {/* Compliance */}
                            {(s.has_tin != null || s.has_ursb != null || s.has_business_bank != null || s.has_sacco != null || s.has_mobile_money != null) && (
                              <Box sx={{ mb: s.notes ? 1 : 0 }}>
                                <Typography variant="overline" fontSize={9} color="text.secondary">Compliance & Access</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                  {s.has_tin != null && <Chip size="small" label={`TIN${s.tin_number ? `: ${s.tin_number}` : ''}`} color={s.has_tin ? 'success' : 'default'} variant={s.has_tin ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                  {s.has_ursb != null && <Chip size="small" label={`URSB${s.ursb_reg_number ? `: ${s.ursb_reg_number}` : ''}`} color={s.has_ursb ? 'success' : 'default'} variant={s.has_ursb ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                  {s.has_business_bank != null && <Chip size="small" label={s.bank_name || 'Bank Account'} color={s.has_business_bank ? 'success' : 'default'} variant={s.has_business_bank ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                  {s.has_sacco != null && <Chip size="small" label="SACCO" color={s.has_sacco ? 'success' : 'default'} variant={s.has_sacco ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                  {s.has_mobile_money != null && <Chip size="small" label="Mobile Money" color={s.has_mobile_money ? 'success' : 'default'} variant={s.has_mobile_money ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                  {s.has_momo_pay != null && <Chip size="small" label={`MoMo Pay${s.momo_pay_code ? `: ${s.momo_pay_code}` : ''}`} color={s.has_momo_pay ? 'success' : 'default'} variant={s.has_momo_pay ? 'filled' : 'outlined'} sx={{ fontSize: 10 }} />}
                                </Box>
                              </Box>
                            )}

                            {s.notes && (
                              <Typography fontSize={11} color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>{s.notes}</Typography>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}

                {/* ── Tab 3: Visit Schedule ── */}
                {msmeDetailTab === 3 && (
                  <Box sx={{ p: 2 }}>
                    <MSMEVisitSchedule
                      token={token}
                      msme={m}
                      currentUser={currentUser}
                      currentBge={currentUser?.bge_profile}
                      onOpenNewReport={(msmeId, type) => {
                        setMsmeDetailDialog(false);
                        openNewReport(msmeId, type);
                      }}
                    />
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" color="success"
                  onClick={() => { setMsmeDetailDialog(false); openGrowthForm(m); }}>
                  Growth Update
                </Button>
                <Button variant="outlined" size="small"
                  sx={{ borderColor: '#B45309', color: '#B45309' }}
                  onClick={() => {
                    setMsmeDetailDialog(false);
                    const existing = tbipPlans.find(p => p.msme === m.id);
                    setSelectedTbipMsme(m);
                    setSelectedTbip(existing || null);
                    setTbipDialog(true);
                  }}>
                  TBIP Plan
                </Button>
                <Button variant="contained" size="small"
                  startIcon={<AccountBalance />}
                  sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#172554' } }}
                  onClick={() => {
                    setMsmeDetailDialog(false);
                    const existing = fiapPlans.find(p => p.msme === m.id);
                    setSelectedFiapMsme(m);
                    setSelectedFiap(existing || null);
                    setFiapDialog(true);
                  }}>
                  Financial Action Plan
                </Button>
                <Button variant="outlined" size="small"
                  startIcon={<EventAvailable />}
                  sx={{ borderColor: '#1E3A8A', color: '#1E3A8A' }}
                  onClick={() => {
                    setMsmeDetailDialog(false);
                    const existing = fiapPlans.find(p => p.msme === m.id);
                    setFiapBookMeetingMsme(m);
                    setFiapBookMeetingPlan(existing || null);
                    setFiapBookMeetingOpen(true);
                  }}>
                  Request Approval Meeting
                </Button>
                <Button onClick={() => setMsmeDetailDialog(false)}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Quick Visit Check-in dialog ── */}
      <Dialog open={quickUpdateDialog} onClose={() => setQuickUpdateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MyLocation color="primary" />
            <Box>
              <Typography fontWeight={700}>Visit Check-in</Typography>
              {quickUpdateMsme && (
                <Typography variant="caption" color="text.secondary">{quickUpdateMsme.business_name}</Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* GPS status */}
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: quickUpdateForm.gpsStatus === 'ok' ? '#E8F5E9' : 'grey.100', display: 'flex', alignItems: 'center', gap: 1 }}>
            {quickUpdateForm.gpsStatus === 'loading' && <CircularProgress size={16} />}
            {quickUpdateForm.gpsStatus === 'ok'      && <MyLocation sx={{ color: 'success.dark', fontSize: 18 }} />}
            {quickUpdateForm.gpsStatus === 'denied'  && <MyLocation sx={{ color: 'text.disabled', fontSize: 18 }} />}
            <Box sx={{ flex: 1 }}>
              {quickUpdateForm.gpsStatus === 'loading' && <Typography variant="caption">Acquiring device GPS location…</Typography>}
              {quickUpdateForm.gpsStatus === 'ok'      && (
                <Typography variant="caption" color="success.dark" fontWeight={600}>
                  📍 {Number(quickUpdateForm.latitude).toFixed(6)}, {Number(quickUpdateForm.longitude).toFixed(6)}
                </Typography>
              )}
              {quickUpdateForm.gpsStatus === 'denied'  && (
                <Typography variant="caption" color="text.secondary">
                  GPS not available — visit recorded without coordinates
                </Typography>
              )}
            </Box>
          </Box>

          {/* Last month revenue */}
          <TextField fullWidth size="small" label="Last Month's Revenue (UGX)" type="number"
            inputProps={{ min: 0 }} sx={{ mb: 2 }}
            value={quickUpdateForm.last_month_revenue}
            onChange={e => setQuickUpdateForm(f => ({ ...f, last_month_revenue: e.target.value }))} />

          {/* Employee counts */}
          <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10} display="block" sx={{ mb: 1 }}>
            Employees
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {[
              { key: 'employees_ft_male',   label: 'FT Male' },
              { key: 'employees_ft_female', label: 'FT Female' },
              { key: 'employees_pt_male',   label: 'PT Male' },
              { key: 'employees_pt_female', label: 'PT Female' },
            ].map(({ key, label }) => (
              <Grid item xs={6} key={key}>
                <TextField fullWidth size="small" label={label} type="number"
                  inputProps={{ min: 0 }}
                  value={quickUpdateForm[key]}
                  onChange={e => setQuickUpdateForm(f => ({ ...f, [key]: e.target.value }))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickUpdateDialog(false)} disabled={quickUpdateSaving}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={quickUpdateSaving || quickUpdateForm.gpsStatus === 'loading'}
            onClick={saveQuickUpdate}>
            {quickUpdateSaving ? 'Saving…' : 'Save Check-in'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── BGE Base Location Dialog ── */}
      <Dialog open={bgeLocationDialog} onClose={() => setBgeLocationDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Place color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Set My Field Base Location</Typography>
          </Box>
          <IconButton size="small" onClick={() => setBgeLocationDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your base pin appears as a gold star badge (★) on the Northern Uganda spatial map.
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Base District / Town"
            placeholder="e.g. Gulu City, Lira City, Arua, Kitgum"
            value={bgeLocationForm.location}
            onChange={e => setBgeLocationForm(f => ({ ...f, location: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2, bgcolor: '#F8FAFC' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                LIVE BASE GPS COORDINATES
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={bgeLocationForm.loading ? <CircularProgress size={12} color="inherit" /> : <MyLocation sx={{ fontSize: 14 }} />}
                disabled={bgeLocationForm.loading}
                onClick={() => {
                  if (!navigator.geolocation) {
                    notify('Geolocation is not supported by your browser', 'error');
                    return;
                  }
                  setBgeLocationForm(f => ({ ...f, loading: true }));
                  navigator.geolocation.getCurrentPosition(
                    (pos) => setBgeLocationForm(f => ({
                      ...f,
                      latitude: pos.coords.latitude.toFixed(6),
                      longitude: pos.coords.longitude.toFixed(6),
                      loading: false,
                    })),
                    () => {
                      notify('Unable to acquire device GPS. Please check browser location permissions.', 'error');
                      setBgeLocationForm(f => ({ ...f, loading: false }));
                    },
                    { enableHighAccuracy: true, timeout: 15000 }
                  );
                }}
                sx={{ fontSize: 11, py: 0.3, textTransform: 'none' }}
              >
                {bgeLocationForm.loading ? 'Locating…' : bgeLocationForm.latitude ? 'Recapture GPS' : 'Capture Live GPS'}
              </Button>
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Latitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={bgeLocationForm.latitude}
                  onChange={e => setBgeLocationForm(f => ({ ...f, latitude: e.target.value }))}
                  placeholder="e.g. 2.774950"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Longitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={bgeLocationForm.longitude}
                  onChange={e => setBgeLocationForm(f => ({ ...f, longitude: e.target.value }))}
                  placeholder="e.g. 32.299110"
                />
              </Grid>
            </Grid>

            {bgeLocationForm.latitude && bgeLocationForm.longitude && (
              <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                📍 {Number(bgeLocationForm.latitude).toFixed(6)}, {Number(bgeLocationForm.longitude).toFixed(6)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBgeLocationDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveBgeLocation}
            disabled={bgeLocationForm.saving || bgeLocationForm.loading}
          >
            {bgeLocationForm.saving ? <CircularProgress size={16} color="inherit" /> : 'Save Base Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Growth update form ── */}
      <Dialog open={growthDialog} onClose={() => setGrowthDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography fontWeight={700}>Record Growth Update</Typography>
          {growthMsme && <Typography variant="caption" color="text.secondary">{growthMsme.business_name}</Typography>}
        </DialogTitle>
        <DialogContent dividers>
          {growthError && <Alert severity="error" sx={{ mb: 2 }}>{growthError}</Alert>}

          <Grid container spacing={2}>
            {/* Date + Source */}
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Date" type="date" InputLabelProps={{ shrink: true }}
                value={growthForm.snapshot_date}
                onChange={e => setGrowthForm(f => ({ ...f, snapshot_date: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={growthForm.source} label="Type"
                  onChange={e => setGrowthForm(f => ({ ...f, source: e.target.value }))}>
                  <MenuItem value="bge_visit">BGE Visit</MenuItem>
                  <MenuItem value="quarterly">Quarterly Review</MenuItem>
                  <MenuItem value="annual">Data Update</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Revenue & Assets */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>Financials (UGX)</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Annual Revenue / Turnover"
                type="number" inputProps={{ min: 0 }}
                value={growthForm.annual_turnover}
                onChange={e => setGrowthForm(f => ({ ...f, annual_turnover: e.target.value }))}
                helperText="Total sales last 12 months" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Last Month's Revenue"
                type="number" inputProps={{ min: 0 }}
                value={growthForm.last_month_revenue}
                onChange={e => setGrowthForm(f => ({ ...f, last_month_revenue: e.target.value }))}
                helperText="Total sales last calendar month" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Total Assets"
                type="number" inputProps={{ min: 0 }}
                value={growthForm.total_assets}
                onChange={e => setGrowthForm(f => ({ ...f, total_assets: e.target.value }))}
                helperText="Business investment in assets" />
            </Grid>

            {/* Employees */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>Employees</Typography>
            </Grid>
            {[
              { key: 'employees_ft_male',   label: 'FT Male' },
              { key: 'employees_ft_female', label: 'FT Female' },
              { key: 'employees_pt_male',   label: 'PT Male' },
              { key: 'employees_pt_female', label: 'PT Female' },
            ].map(({ key, label }) => (
              <Grid item xs={3} key={key}>
                <TextField fullWidth size="small" label={label} type="number"
                  inputProps={{ min: 0 }}
                  value={growthForm[key]}
                  onChange={e => setGrowthForm(f => ({ ...f, [key]: e.target.value }))} />
              </Grid>
            ))}

            {/* Refugee employees sub-section */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>Refugee Staff</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="FT Refugees" type="number"
                inputProps={{ min: 0 }}
                helperText="Full-time refugee staff"
                value={growthForm.employees_ft_refugee}
                onChange={e => setGrowthForm(f => ({ ...f, employees_ft_refugee: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="PT Refugees" type="number"
                inputProps={{ min: 0 }}
                helperText="Part-time refugee staff"
                value={growthForm.employees_pt_refugee}
                onChange={e => setGrowthForm(f => ({ ...f, employees_pt_refugee: e.target.value }))} />
            </Grid>

            {/* Compliance */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>Compliance & Financial Access</Typography>
            </Grid>

            {/* TIN */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Has TIN (Tax ID)</InputLabel>
                <Select value={growthForm.has_tin} label="Has TIN (Tax ID)"
                  onChange={e => setGrowthForm(f => ({ ...f, has_tin: e.target.value }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {growthForm.has_tin === 'true' ? (
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="TIN Number"
                  placeholder="e.g. 1234567890"
                  value={growthForm.tin_number}
                  onChange={e => setGrowthForm(f => ({ ...f, tin_number: e.target.value }))} />
              </Grid>
            ) : <Grid item xs={6} />}

            {/* URSB */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Registered with URSB</InputLabel>
                <Select value={growthForm.has_ursb} label="Registered with URSB"
                  onChange={e => setGrowthForm(f => ({ ...f, has_ursb: e.target.value }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {growthForm.has_ursb === 'true' ? (
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="URSB Registration Number"
                  placeholder="e.g. 80000012345"
                  value={growthForm.ursb_reg_number}
                  onChange={e => setGrowthForm(f => ({ ...f, ursb_reg_number: e.target.value }))} />
              </Grid>
            ) : <Grid item xs={6} />}

            {/* Business Bank Account */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Business Bank Account</InputLabel>
                <Select value={growthForm.has_business_bank} label="Business Bank Account"
                  onChange={e => setGrowthForm(f => ({ ...f, has_business_bank: e.target.value, bank_name: '' }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {growthForm.has_business_bank === 'true' ? (
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Which Bank?</InputLabel>
                  <Select value={growthForm.bank_name} label="Which Bank?"
                    onChange={e => setGrowthForm(f => ({ ...f, bank_name: e.target.value }))}>
                    <MenuItem value="">— Select bank —</MenuItem>
                    {[
                      'Absa Bank Uganda',
                      'Bank of Africa Uganda',
                      'Bank of Baroda Uganda',
                      'Cairo International Bank',
                      'Centenary Bank',
                      'Citibank Uganda',
                      'DFCU Bank',
                      'Diamond Trust Bank Uganda',
                      'Ecobank Uganda',
                      'Equity Bank Uganda',
                      'Exim Bank Uganda',
                      'Finance Trust Bank',
                      'Guaranty Trust Bank Uganda',
                      'Housing Finance Bank',
                      'I&M Bank Uganda',
                      'KCB Bank Uganda',
                      'NC Bank Uganda',
                      'Opportunity Bank Uganda',
                      'PostBank Uganda',
                      'Pride Microfinance',
                      'Stanbic Bank Uganda',
                      'Standard Chartered Bank Uganda',
                      'Tropical Bank Uganda',
                      'United Bank for Africa Uganda',
                      'Other',
                    ].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            ) : <Grid item xs={6} />}

            {/* SACCO */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>SACCO Member</InputLabel>
                <Select value={growthForm.has_sacco} label="SACCO Member"
                  onChange={e => setGrowthForm(f => ({ ...f, has_sacco: e.target.value }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} />

            {/* Mobile Money */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Mobile Money Account</InputLabel>
                <Select value={growthForm.has_mobile_money} label="Mobile Money Account"
                  onChange={e => setGrowthForm(f => ({ ...f, has_mobile_money: e.target.value }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>MOMO Pay Code</InputLabel>
                <Select value={growthForm.has_momo_pay} label="MOMO Pay Code"
                  onChange={e => setGrowthForm(f => ({ ...f, has_momo_pay: e.target.value }))}>
                  <MenuItem value="">— Unknown —</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {growthForm.has_momo_pay === 'true' && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="MOMO Pay Merchant Code"
                  placeholder="e.g. 123456"
                  helperText="MTN / Airtel MOMO Pay merchant code number"
                  value={growthForm.momo_pay_code}
                  onChange={e => setGrowthForm(f => ({ ...f, momo_pay_code: e.target.value }))} />
              </Grid>
            )}

            {/* ── Digital Tools ── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>
                Digital Tools Adopted
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Which digital tools has this business adopted? (select all that apply)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {DIGITAL_TOOLS_OPTIONS.map(tool => {
                  const checked = growthForm.digital_tools.includes(tool);
                  return (
                    <Chip
                      key={tool}
                      label={tool}
                      size="small"
                      clickable
                      variant={checked ? 'filled' : 'outlined'}
                      color={checked ? 'primary' : 'default'}
                      onClick={() => setGrowthForm(f => ({
                        ...f,
                        digital_tools: checked
                          ? f.digital_tools.filter(t => t !== tool)
                          : [...f.digital_tools, tool],
                        digital_tools_other: tool === 'Other' && checked ? '' : f.digital_tools_other,
                      }))}
                    />
                  );
                })}
              </Box>
            </Grid>
            {growthForm.digital_tools.includes('Other') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Other digital tool(s) — please specify"
                  value={growthForm.digital_tools_other}
                  onChange={e => setGrowthForm(f => ({ ...f, digital_tools_other: e.target.value }))}
                  placeholder="e.g. Sage, Odoo, custom app…" />
              </Grid>
            )}

            {/* ── Training Impact ── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>
                Training Impact
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Has training made any changes to this business?</InputLabel>
                <Select
                  value={growthForm.training_made_changes}
                  label="Has training made any changes to this business?"
                  onChange={e => setGrowthForm(f => ({
                    ...f,
                    training_made_changes: e.target.value,
                    training_changes: [],
                    training_changes_other: '',
                  }))}>
                  <MenuItem value="">— Not answered —</MenuItem>
                  <MenuItem value="true">Yes — training has made changes</MenuItem>
                  <MenuItem value="false">No — no changes observed yet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {growthForm.training_made_changes === 'true' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    What changes has the training led to? (select all that apply)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {TRAINING_CHANGE_OPTIONS.map(change => {
                      const checked = growthForm.training_changes.includes(change);
                      return (
                        <Chip
                          key={change}
                          label={change}
                          size="small"
                          clickable
                          variant={checked ? 'filled' : 'outlined'}
                          color={checked ? 'success' : 'default'}
                          onClick={() => setGrowthForm(f => ({
                            ...f,
                            training_changes: checked
                              ? f.training_changes.filter(c => c !== change)
                              : [...f.training_changes, change],
                            training_changes_other: change === 'Other' && checked ? '' : f.training_changes_other,
                          }))}
                        />
                      );
                    })}
                  </Box>
                </Grid>
                {growthForm.training_changes.includes('Other') && (
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Other training change(s) — please describe"
                      multiline rows={2}
                      value={growthForm.training_changes_other}
                      onChange={e => setGrowthForm(f => ({ ...f, training_changes_other: e.target.value }))}
                      placeholder="Describe the other change(s) the training has made to this business…" />
                  </Grid>
                )}
              </>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes / Observations" multiline rows={3}
                value={growthForm.notes}
                onChange={e => setGrowthForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Key observations, what changed since last visit, challenges, etc." />
            </Grid>
          </Grid>

          {/* ── Narrative section (Annual / Quarterly reviews only) ── */}
          {(growthForm.source === 'annual' || growthForm.source === 'quarterly') && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#FAF9F6', borderRadius: 2, border: '1px solid #E7E5E4' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Description sx={{ fontSize: 16, color: BRAND.primaryMain }} />
                <Typography fontWeight={700} fontSize={13} color={BRAND.primaryMain}>
                  Review Narrative
                </Typography>
                <Chip label="Creates linked draft report" size="small" variant="outlined"
                  sx={{ ml: 'auto', fontSize: 10, color: 'text.secondary' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Add a written narrative to accompany the data update. A draft visit report will be automatically created and can be completed, edited, and submitted separately.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={3}
                    label="Business Overview — current state of the business"
                    value={growthForm.narrative_business_overview}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_business_overview: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={2}
                    label="Key Achievement / Progress since last review"
                    value={growthForm.narrative_key_achievement}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_key_achievement: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={2}
                    label="Challenges Identified"
                    value={growthForm.narrative_challenges}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_challenges: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" multiline rows={2}
                    label="Support Provided"
                    value={growthForm.narrative_support_provided}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_support_provided: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" multiline rows={2}
                    label="Recommendations"
                    value={growthForm.narrative_recommendations}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_recommendations: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={2}
                    label="Next Steps / Agreed Actions"
                    value={growthForm.narrative_next_steps}
                    onChange={e => setGrowthForm(f => ({ ...f, narrative_next_steps: e.target.value }))} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ── History ── */}
          {growthSnapshots.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} fontSize={10}>
                Previous Snapshots ({growthSnapshots.length})
              </Typography>
              {[...growthSnapshots].reverse().map((s, i) => (
                <Box key={s.id} sx={{ border: '1px solid #E8EDF2', borderRadius: 1.5, p: 1.5, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography fontSize={12} fontWeight={600}>{s.snapshot_date}</Typography>
                    <Chip size="small" variant="outlined" sx={{ fontSize: 10 }}
                      label={{ diagnostic: 'Baseline', bge_visit: 'BGE Visit', quarterly: 'Quarterly', annual: 'Data Update', other: 'Other' }[s.source] || s.source} />
                  </Box>
                  <Grid container spacing={1}>
                    {s.annual_turnover    && <Grid item xs={6}><Typography fontSize={11} color="text.secondary">Annual Revenue</Typography><Typography fontSize={12} fontWeight={600}>UGX {Number(s.annual_turnover).toLocaleString()}</Typography></Grid>}
                    {s.last_month_revenue && <Grid item xs={6}><Typography fontSize={11} color="text.secondary">Last Month</Typography><Typography fontSize={12} fontWeight={600}>UGX {Number(s.last_month_revenue).toLocaleString()}</Typography></Grid>}
                    {s.total_assets       && <Grid item xs={6}><Typography fontSize={11} color="text.secondary">Assets</Typography><Typography fontSize={12} fontWeight={600}>UGX {Number(s.total_assets).toLocaleString()}</Typography></Grid>}
                    {(s.employees_ft_male != null || s.employees_ft_female != null) && (
                      <Grid item xs={12}>
                        <Typography fontSize={11} color="text.secondary">
                          Employees: {(s.employees_ft_male||0)+(s.employees_ft_female||0)} FT
                          {(s.employees_pt_male != null || s.employees_pt_female != null)
                            ? ` · ${(s.employees_pt_male||0)+(s.employees_pt_female||0)} PT` : ''}
                          {' '}({s.employees_ft_female||0} female FT)
                          {(s.employees_ft_refugee != null || s.employees_pt_refugee != null)
                            ? ` · ${(s.employees_ft_refugee||0)+(s.employees_pt_refugee||0)} refugees` : ''}
                        </Typography>
                      </Grid>
                    )}
                    {(s.has_tin != null || s.has_ursb != null || s.has_business_bank != null || s.has_sacco != null || s.has_mobile_money != null || s.has_momo_pay != null) && (
                      <Grid item xs={12}>
                        <Typography fontSize={11} color="text.secondary">
                          {[
                            s.has_tin != null && `TIN: ${s.has_tin ? (s.tin_number || 'Yes') : 'No'}`,
                            s.has_ursb != null && `URSB: ${s.has_ursb ? (s.ursb_reg_number || 'Yes') : 'No'}`,
                            s.has_business_bank != null && `Bank: ${s.has_business_bank ? (s.bank_name || 'Yes') : 'No'}`,
                            s.has_sacco != null && `SACCO: ${s.has_sacco ? 'Yes' : 'No'}`,
                            s.has_mobile_money != null && `MoMo: ${s.has_mobile_money ? 'Yes' : 'No'}`,
                            s.has_momo_pay != null && `MoMo Pay: ${s.has_momo_pay ? (s.momo_pay_code || 'Yes') : 'No'}`,
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                      </Grid>
                    )}
                    {s.notes && <Grid item xs={12}><Typography fontSize={11} color="text.secondary" sx={{ fontStyle: 'italic' }}>{s.notes}</Typography></Grid>}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrowthDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={growthSaving} onClick={saveGrowthSnapshot}>
            {growthSaving ? 'Saving…' : 'Save Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New full-screen visit report form ── */}
      <VisitReportForm
        open={visitReportOpen}
        onClose={() => setVisitReportOpen(false)}
        onSaved={() => { fetchMsmes(); fetchReports(); notify('Report saved.'); }}
        msme={visitReportMsme}
        initialVisitType={visitReportType}
        msmes={msmes}
        token={token}
        bgeProfile={currentUser?.bge_profile}
        editingReport={visitReportEdit}
      />

      {/* ── Enterprise Improvement Plan (TBIP) Dialog ── */}
      <EnterpriseImprovementPlanDialog
        open={tbipDialog}
        onClose={() => setTbipDialog(false)}
        plan={selectedTbip}
        msme={selectedTbipMsme || (selectedTbip ? msmes.find(m => m.id === selectedTbip.msme) : null)}
        bge={currentUser?.bge_profile}
        currentUser={currentUser}
        token={token}
        onSaved={(updated) => {
          fetchTbipPlans();
          setSelectedTbip(updated);
        }}
        notify={notify}
      />

      {/* ── Book Meeting with Richard & Stephen Dialog ── */}
      <BookTBIPMeetingDialog
        open={tbipBookMeetingOpen}
        onClose={() => setTbipBookMeetingOpen(false)}
        plan={tbipBookMeetingPlan}
        msme={tbipBookMeetingMsme}
        msmes={msmes}
        currentUser={currentUser}
        token={token}
        notify={notify}
      />


      {/* ── MSME Picker for New TBIP ── */}
      <Dialog open={newTbipMsmePicker} onClose={() => setNewTbipMsmePicker(false)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>Select MSME for Assessment</DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, px: 0.5 }}>
            Choose an assigned MSME to conduct the diagnostic assessment and formulate the Technical Business Improvement Plan (TBIP).
          </Typography>
          <List sx={{ pt: 0, maxHeight: 360, overflowY: 'auto' }}>
            {msmes.map((m) => (
              <ListItemButton
                key={m.id}
                onClick={() => {
                  setSelectedTbipMsme(m);
                  setSelectedTbip(null);
                  setNewTbipMsmePicker(false);
                  setTbipDialog(true);
                }}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.75,
                  border: '1px solid #E2E8F0',
                  '&:hover': { bgcolor: '#F1F5F9', borderColor: BRAND.primaryMain },
                }}
              >
                <ListItemText
                  primary={<Typography fontWeight={700} fontSize={13.5} color={BRAND.primaryMain}>{m.name}</Typography>}
                  secondary={<Typography fontSize={11} color="text.secondary">{m.msme_code || '—'} · {m.district || ''} · {m.sector || ''}</Typography>}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setNewTbipMsmePicker(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Financial Improvement Action Plan (FIAP) Dialog ── */}
      <FinancialImprovementPlanDialog
        open={fiapDialog}
        onClose={() => setFiapDialog(false)}
        plan={selectedFiap}
        msme={selectedFiapMsme || (selectedFiap ? msmes.find(m => m.id === selectedFiap.msme) : null)}
        bge={currentUser?.bge_profile}
        currentUser={currentUser}
        token={token}
        onSaved={(updated) => {
          fetchFiapPlans();
          setSelectedFiap(updated);
        }}
        notify={notify}
      />

      {/* ── Book FIAP Approval Meeting Dialog (Rosemary / Harriet) ── */}
      <BookFIAPMeetingDialog
        open={fiapBookMeetingOpen}
        onClose={() => setFiapBookMeetingOpen(false)}
        plan={fiapBookMeetingPlan}
        msme={fiapBookMeetingMsme}
        msmes={msmes}
        currentUser={currentUser}
        token={token}
        onBooked={(resData) => {
          fetchFiapPlans();
        }}
        notify={notify}
      />


      {/* ── MSME Picker for New FIAP ── */}
      <Dialog open={newFiapMsmePicker} onClose={() => setNewFiapMsmePicker(false)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>Select MSME for Financial Action Plan</DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, px: 0.5 }}>
            Choose an assigned MSME to assess its credit readiness and formulate a Financial Improvement Action Plan (FIAP).
          </Typography>
          <List sx={{ pt: 0, maxHeight: 360, overflowY: 'auto' }}>
            {msmes.map((m) => (
              <ListItemButton
                key={m.id}
                onClick={() => {
                  setSelectedFiapMsme(m);
                  const existing = fiapPlans.find(p => p.msme === m.id);
                  setSelectedFiap(existing || null);
                  setNewFiapMsmePicker(false);
                  setFiapDialog(true);
                }}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.75,
                  border: '1px solid #E2E8F0',
                  '&:hover': { bgcolor: '#F1F5F9', borderColor: '#1E3A8A' },
                }}
              >
                <ListItemText
                  primary={<Typography fontWeight={700} fontSize={13.5} color="#1E3A8A">{m.name}</Typography>}
                  secondary={<Typography fontSize={11} color="text.secondary">{m.msme_code || '—'} · {m.district || ''} · {m.sector || ''}</Typography>}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setNewFiapMsmePicker(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
        </DialogActions>
      </Dialog>


      {/* ── Group Report (team lead only) ────────────────────────────────── */}
      <Dialog open={groupReportDialog} onClose={() => setGroupReportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGroupReport ? 'Edit Group Report' : 'New Group Report'}
          <Typography variant="caption" display="block" color="text.secondary">
            Team-lead-only. Document the session for the whole group.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {groupReportErrors && <Alert severity="error" sx={{ mb: 2 }}>{groupReportErrors}</Alert>}

          {/* Draft recovery banner */}
          {grpDraftBanner && (
            <Alert severity="warning" sx={{ mb: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" color="inherit" variant="outlined" onClick={restoreGrpDraft}>Restore</Button>
                  <Button size="small" color="inherit" onClick={discardGrpDraft}>Discard</Button>
                </Box>
              }
            >
              Unsaved draft from{' '}
              {new Date(grpDraftBanner.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' '}— restore it?
            </Alert>
          )}

          {/* Group + objectives reminder banner */}
          {(() => {
            const g = groups.find(x => x.id === groupReportForm.group);
            if (!g) return null;
            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={600} display="block">
                  {g.name} · objectives
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {g.objectives || '(no objectives recorded)'}
                </Typography>
              </Alert>
            );
          })()}

          {/* ── Member contributions panel (team lead only, editing existing report) ── */}
          {editingGroupReport && reportContributions.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Member Contributions ({reportContributions.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Review what your members filed, then integrate into the report fields below.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={() => {
                    // Merge all contributions into the group report narrative fields
                    const withAttrib = (arr, key) =>
                      arr.filter(c => (c[key] || '').trim()).map(c => `[${c.bge_name}]\n${c[key]}`).join('\n\n---\n\n');

                    setGroupReportForm(f => {
                      const notes       = withAttrib(reportContributions, 'notes');
                      const challenges  = withAttrib(reportContributions, 'challenges_observed');
                      const interventions = withAttrib(reportContributions, 'interventions_made');
                      const followUp    = withAttrib(reportContributions, 'follow_up_needed');

                      const append = (existing, incoming) =>
                        incoming ? (existing ? `${existing}\n\n--- Member contributions ---\n\n${incoming}` : incoming) : existing;

                      // Collect unique MSME ids observed across all contributions
                      const observedIds = [...new Set(
                        reportContributions.flatMap(c => c.msmes_observed || [])
                      )];
                      const mergedMsmes = [...new Set([...(f.msmes_supported || []), ...observedIds])];

                      return {
                        ...f,
                        session_overview:      append(f.session_overview, notes),
                        challenges_identified: append(f.challenges_identified, challenges),
                        interventions_delivered: append(f.interventions_delivered, interventions),
                        next_steps:            append(f.next_steps, followUp),
                        msmes_supported:       mergedMsmes,
                      };
                    });
                    notify('Contributions integrated — review and edit the fields below before saving.');
                  }}
                >
                  Integrate All
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reportContributions.map(c => (
                  <Box key={c.id} sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #FFE082' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={700}>{c.bge_name} ({c.bge_code})</Typography>
                      <Button
                        size="small" variant="outlined" color="warning"
                        sx={{ minWidth: 0, px: 1.5, py: 0.25, fontSize: 11 }}
                        onClick={() => {
                          setGroupReportForm(f => {
                            const append = (existing, incoming) =>
                              incoming ? (existing ? `${existing}\n\n[${c.bge_name}] ${incoming}` : `[${c.bge_name}] ${incoming}`) : existing;
                            const observedIds = c.msmes_observed || [];
                            const mergedMsmes = [...new Set([...(f.msmes_supported || []), ...observedIds])];
                            return {
                              ...f,
                              session_overview:      append(f.session_overview, c.notes),
                              challenges_identified: append(f.challenges_identified, c.challenges_observed),
                              interventions_delivered: append(f.interventions_delivered, c.interventions_made),
                              next_steps:            append(f.next_steps, c.follow_up_needed),
                              msmes_supported:       mergedMsmes,
                            };
                          });
                          notify(`${c.bge_name}'s contribution integrated`);
                        }}
                      >
                        Integrate
                      </Button>
                    </Box>
                    {c.notes && <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.25 }}><strong>Notes:</strong> {c.notes.length > 120 ? c.notes.slice(0, 120) + '…' : c.notes}</Typography>}
                    {c.challenges_observed && <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.25 }}><strong>Challenges:</strong> {c.challenges_observed.length > 100 ? c.challenges_observed.slice(0, 100) + '…' : c.challenges_observed}</Typography>}
                    {c.interventions_made && <Typography variant="caption" display="block" color="text.secondary"><strong>Interventions:</strong> {c.interventions_made.length > 100 ? c.interventions_made.slice(0, 100) + '…' : c.interventions_made}</Typography>}
                    {(c.msmes_observed || []).length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        <strong>MSMEs observed:</strong> {c.msmes_observed.length}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Group</InputLabel>
                <Select
                  value={groupReportForm.group}
                  label="Group"
                  // Switching group also clears the MSME multi-select — otherwise
                  // ids from the previous group's MSMEs would silently slip into
                  // the POST even though the dropdown filtered them out.
                  onChange={e => setGrf(prev => ({
                    ...prev,
                    group: e.target.value,
                    msmes_supported: [],
                  }))}
                  disabled={!!editingGroupReport}
                >
                  {groups.filter(g => isTeamLeadOf(g)).map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth size="small" type="number" label="Session # (optional)"
                value={groupReportForm.session_number}
                onChange={e => setGrf(prev => ({ ...prev, session_number: e.target.value }))}
                inputProps={{ min: 1, max: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth size="small" type="date" label="Visit Date" required
                InputLabelProps={{ shrink: true }}
                value={groupReportForm.visit_date}
                onChange={e => setGrf(prev => ({ ...prev, visit_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>MSMEs supported</InputLabel>
                <Select
                  multiple
                  value={groupReportForm.msmes_supported}
                  label="MSMEs supported"
                  onChange={e => setGrf(prev => ({ ...prev, msmes_supported: e.target.value }))}
                  renderValue={(ids) => `${ids.length} selected`}
                >
                  {msmes
                    .filter(m => m.assigned_group === groupReportForm.group)
                    .map(m => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.business_name} {m.session_number ? `· S${m.session_number}` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* ── Attendance ── */}
          {(() => {
            const g = groups.find(x => x.id === groupReportForm.group);
            const members = g?.members_detail || [];
            if (!members.length) return null;
            const toggleAttendee = (bgeId) => {
              setGrf(prev => ({
                ...prev,
                attendees: (prev.attendees || []).includes(bgeId)
                  ? (prev.attendees || []).filter(id => id !== bgeId)
                  : [...(prev.attendees || []), bgeId],
              }));
            };
            const allPresent = members.every(m => (groupReportForm.attendees || []).includes(m.id));
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" fontWeight={700}>
                    Attendance · {(groupReportForm.attendees || []).length}/{members.length} present
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setGrf(prev => ({
                      ...prev,
                      attendees: allPresent ? [] : members.map(m => m.id),
                    }))}
                  >
                    {allPresent ? 'Mark none' : 'Mark all present'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {members.map(m => {
                    const present = (groupReportForm.attendees || []).includes(m.id);
                    const isLead  = m.id === g.team_lead;
                    return (
                      <Chip
                        key={m.id}
                        label={m.name}
                        onClick={() => toggleAttendee(m.id)}
                        icon={isLead ? <Star sx={{ fontSize: 14 }} /> : (present ? <CheckCircle sx={{ fontSize: 14 }} /> : undefined)}
                        color={present ? (isLead ? 'warning' : 'success') : 'default'}
                        variant={present ? 'filled' : 'outlined'}
                        size="small"
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })()}

          {/* ── MSME Attendance ── */}
          {(() => {
            const groupMsmes = msmes.filter(m => m.assigned_group === groupReportForm.group);
            const updateRow = (key, field, val) =>
              setGrpAttendees(prev => prev.map(r => r._key === key ? { ...r, [field]: val } : r));
            const male   = grpAttendees.filter(a => a.gender === 'M');
            const female = grpAttendees.filter(a => a.gender === 'F');
            const youth  = grpAttendees.filter(a => a.age_group === '18-34');
            const adult  = grpAttendees.filter(a => ['35-45','46-55','56+'].includes(a.age_group));
            const ref    = grpAttendees.filter(a => a.refugee_status === 'R');
            return (
              <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" fontWeight={700}>
                    MSME Attendance ({grpAttendees.filter(a => a.attendee_name || a.msme).length} recorded)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {groupMsmes.length > 0 && grpAttendees.filter(a => a.attendee_name || a.msme).length === 0 && (
                      <Button size="small" onClick={() => {
                        setGrpAttendees(groupMsmes.map(m => ({
                          ...newGrpRow(),
                          msme: m.id,
                          attendee_name: m.owner_name || '',
                          attendee_phone: m.phone || '',
                        })));
                      }}>
                        Pre-fill from group
                      </Button>
                    )}
                    <Button size="small" startIcon={<Add />} onClick={() => setGrpAttendees(prev => [...prev, newGrpRow()])}>
                      Add row
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                      <TableRow>
                        <TableCell sx={{ minWidth: 30, fontWeight: 700, fontSize: 11 }}>#</TableCell>
                        <TableCell sx={{ minWidth: 150, fontWeight: 700, fontSize: 11 }}>Name</TableCell>
                        <TableCell sx={{ minWidth: 110, fontWeight: 700, fontSize: 11 }}>Phone</TableCell>
                        <TableCell sx={{ minWidth: 170, fontWeight: 700, fontSize: 11 }}>MSME / Business</TableCell>
                        <TableCell sx={{ minWidth: 55, fontWeight: 700, fontSize: 11 }}>Sex</TableCell>
                        <TableCell sx={{ minWidth: 85, fontWeight: 700, fontSize: 11 }}>Age Group</TableCell>
                        <TableCell sx={{ minWidth: 80, fontWeight: 700, fontSize: 11 }}>Status</TableCell>
                        <TableCell sx={{ minWidth: 50, fontWeight: 700, fontSize: 11 }} align="center">Photo</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grpAttendees.map((row, idx) => (
                        <TableRow key={row._key} hover>
                          <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{idx + 1}</TableCell>
                          <TableCell>
                            <TextField size="small" placeholder="Full name" variant="standard"
                              value={row.attendee_name}
                              onChange={e => updateRow(row._key, 'attendee_name', e.target.value)}
                              sx={{ minWidth: 130 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" placeholder="Phone" variant="standard"
                              value={row.attendee_phone}
                              onChange={e => updateRow(row._key, 'attendee_phone', e.target.value)}
                              sx={{ minWidth: 100 }} />
                          </TableCell>
                          <TableCell>
                            <Select size="small" variant="standard" displayEmpty
                              value={row.msme || ''}
                              onChange={e => {
                                const m = msmes.find(x => x.id === e.target.value);
                                updateRow(row._key, 'msme', e.target.value);
                                if (m && !row.attendee_name) updateRow(row._key, 'attendee_name', m.owner_name || '');
                              }}
                              sx={{ minWidth: 150 }}>
                              <MenuItem value=""><em>— walk-in —</em></MenuItem>
                              {groupMsmes.map(m => (
                                <MenuItem key={m.id} value={m.id}>{m.business_name}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select size="small" variant="standard" displayEmpty value={row.gender}
                              onChange={e => updateRow(row._key, 'gender', e.target.value)} sx={{ minWidth: 50 }}>
                              <MenuItem value=""><em>—</em></MenuItem>
                              <MenuItem value="M">M</MenuItem>
                              <MenuItem value="F">F</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select size="small" variant="standard" displayEmpty value={row.age_group}
                              onChange={e => updateRow(row._key, 'age_group', e.target.value)} sx={{ minWidth: 75 }}>
                              <MenuItem value=""><em>—</em></MenuItem>
                              {['18-34','35-45','46-55','56+'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select size="small" variant="standard" value={row.refugee_status}
                              onChange={e => updateRow(row._key, 'refugee_status', e.target.value)} sx={{ minWidth: 75 }}>
                              <MenuItem value="H">Host</MenuItem>
                              <MenuItem value="R">Refugee</MenuItem>
                            </Select>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Photo consent">
                              <Checkbox size="small" checked={!!row.consent_photo}
                                onChange={e => updateRow(row._key, 'consent_photo', e.target.checked)} />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => setGrpAttendees(prev => prev.filter(r => r._key !== row._key))}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {grpAttendees.filter(a => a.attendee_name || a.msme).length > 0 && (
                  <Box sx={{ px: 2, py: 1.5, bgcolor: '#FAF9F6', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={1}>
                      {[
                        { label: 'Total', value: grpAttendees.length, color: BRAND.primaryMain },
                        { label: 'Female', value: female.length, color: '#AD1457' },
                        { label: 'Male', value: male.length, color: BRAND.primaryMain },
                        { label: 'Youth F', value: youth.filter(a => a.gender === 'F').length, color: '#AD1457' },
                        { label: 'Youth M', value: youth.filter(a => a.gender === 'M').length, color: BRAND.primaryMain },
                        { label: 'Adult F', value: adult.filter(a => a.gender === 'F').length, color: '#AD1457' },
                        { label: 'Adult M', value: adult.filter(a => a.gender === 'M').length, color: BRAND.primaryMain },
                        { label: 'Refugees', value: ref.length, color: '#E65100' },
                        { label: 'Host Comm.', value: grpAttendees.filter(a => a.refugee_status === 'H').length, color: '#2E7D32' },
                      ].map(({ label, value, color }) => (
                        <Grid item xs={4} sm={3} md={2} key={label}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fff', borderRadius: 1, border: `1px solid ${color}20` }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color, fontSize: 14 }}>{value}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* ── Member contributions ── (only when editing an existing report) */}
          {editingGroupReport && (editingGroupReport.contributions_detail || []).length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF8E1', borderRadius: 1, border: '1px solid #F9A825' }}>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1, color: '#8a6d00' }}>
                Member contributions ({editingGroupReport.contributions_detail.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Notes submitted by your group members. Use these as source material to consolidate the report below.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {editingGroupReport.contributions_detail.map(c => (
                  <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fff', p: 1, borderRadius: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{c.bge_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.has_notes ? 'Notes provided' : 'No notes'} · {c.msmes_observed.length} MSMEs observed
                      </Typography>
                    </Box>
                    <Button size="small"
                      onClick={() => openContribution(editingGroupReport)}>
                      Open
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {[
            { field: 'session_overview',        label: 'Session overview',        hint: 'How the session ran, attendance, format used.' },
            { field: 'challenges_identified',   label: 'Challenges identified',   hint: 'Cross-cutting challenges observed across the cohort.' },
            { field: 'interventions_delivered', label: 'Interventions delivered', hint: 'Group-level coaching, training, or facilitated activities.' },
            { field: 'outcomes_achieved',       label: 'Outcomes achieved',       hint: 'Quantitative + qualitative outcomes from the session.' },
            { field: 'next_steps',              label: 'Next steps',              hint: 'Follow-up plan agreed with the group.' },
            { field: 'additional_notes',        label: 'Additional notes',        hint: 'Anything else worth recording.' },
          ].map(({ field, label, hint }) => (
            <TextField
              key={field}
              fullWidth multiline rows={3} size="small" label={label}
              placeholder={hint}
              value={groupReportForm[field]}
              onChange={e => setGrf(prev => ({ ...prev, [field]: e.target.value }))}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setGroupReportDialog(false)}>Cancel</Button>
          <Button variant="outlined" onClick={() => saveGroupReport('draft')} disabled={groupReportSaving}>
            Save Draft
          </Button>
          <Button
            variant="contained" color="success"
            onClick={() => saveGroupReport('submitted')}
            disabled={groupReportSaving}
            startIcon={groupReportSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── In-App Report Viewer ─────────────────────────────────────────── */}
      <Dialog open={!!viewReport} onClose={() => setViewReport(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        {viewReport && (() => {
          const vr = viewReport;
          const visitLabels = { initial: 'Initial Assessment', followup: 'Follow-up Visit', final: 'Final Assessment', training: 'Training Support', mentoring: 'Mentoring Session' };
          const statusColors = { draft: 'default', submitted: 'primary', reviewed: 'success' };
          const SECTIONS = [
            { key: 'business_overview',     label: 'Business Overview' },
            { key: 'challenges_identified', label: 'Challenges Identified' },
            { key: 'support_provided',      label: 'Support Provided' },
            { key: 'recommendations',       label: 'Recommendations' },
            { key: 'action_plan',           label: 'Action Plan' },
            { key: 'next_steps',            label: 'Next Steps' },
            { key: 'additional_notes',      label: 'Additional Notes' },
          ];
          return <>
            {/* Branded letterhead — hidden on screen, only shown when the
                user prints the page so the print fallback looks official.
                Uses raw <div> to avoid MUI's @media-print quirks. */}
            <div className="print-letterhead">
              <img className="gopa" src="/gopa-logo.png" alt="GOPA Pro" />
              <div className="wordmark">
                <div className="title">PRUDEV II</div>
                <div className="subtitle">MSME Portfolio Management</div>
              </div>
              <img className="giz" src="/giz-logo.png" alt="German Cooperation · Implemented by GIZ" />
            </div>
            <Box sx={{ bgcolor: BRAND.sidebarBg, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Visit Report</Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}>
                  {visitLabels[vr.visit_type] || vr.visit_type}
                </Typography>
              </Box>
              <Chip label={vr.status} size="small" color={statusColors[vr.status] || 'default'} />
            </Box>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', gap: 3, px: 3, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap' }}>
                {[
                  ['Business', vr._msme?.business_name || vr.msme_name],
                  ['MSME Code', vr._msme?.msme_code || vr.msme_code],
                  ['BGE Expert', vr._bgeName],
                  ['Visit Date', vr.visit_date],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{val}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                {SECTIONS.map(({ key, label }, idx) => (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: BRAND.sidebarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Typography sx={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>{idx + 1}</Typography>
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">{label}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#F4F6F9', borderRadius: 1.5, px: 2, py: 1.5, borderLeft: `3px solid ${vr[key] ? '#009B62' : '#E5E7EB'}` }}>
                      {vr[key] ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{vr[key]}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No information recorded.</Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #E5E7EB', gap: 1 }}>
              <Button onClick={() => openMsmeReportPdf(viewReport.id, 'view')}>
                Open PDF
              </Button>
              <Button startIcon={<PictureAsPdf />} onClick={() => openMsmeReportPdf(viewReport.id, 'download')}>
                Download PDF
              </Button>
              <Button onClick={() => setViewReport(null)}>Close</Button>
            </DialogActions>
          </>;
        })()}
      </Dialog>

      {/* ── Member contribution dialog ── */}
      <Dialog open={contributionDialog} onClose={() => setContributionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>My Contribution</DialogTitle>
        <DialogContent dividers>
          {contributionErrors && <Alert severity="error" sx={{ mb: 2 }}>{contributionErrors}</Alert>}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Share your field notes with the team lead to help consolidate the group report.
          </Typography>
          <TextField fullWidth multiline minRows={3} label="Notes / Observations" sx={{ mb: 2 }}
            value={contributionForm.notes}
            onChange={e => setContributionForm(f => ({ ...f, notes: e.target.value }))} />
          <TextField fullWidth multiline minRows={2} label="Challenges observed" sx={{ mb: 2 }}
            value={contributionForm.challenges_observed}
            onChange={e => setContributionForm(f => ({ ...f, challenges_observed: e.target.value }))} />
          <TextField fullWidth multiline minRows={2} label="Interventions made" sx={{ mb: 2 }}
            value={contributionForm.interventions_made}
            onChange={e => setContributionForm(f => ({ ...f, interventions_made: e.target.value }))} />
          <TextField fullWidth multiline minRows={2} label="Follow-up needed"
            value={contributionForm.follow_up_needed}
            onChange={e => setContributionForm(f => ({ ...f, follow_up_needed: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContributionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveContribution} disabled={contributionSaving}>
            {contributionSaving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Work Order print/preview ── */}
      {workOrderPreview && (
        <Dialog open={Boolean(workOrderPreview)} onClose={() => setWorkOrderPreview(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography fontWeight={700}>{workOrderPreview.work_order_number}</Typography>
              <Button startIcon={<Print />} variant="outlined" onClick={() => window.print()}>Print / Save PDF</Button>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ fontFamily: 'Arial, sans-serif', fontSize: 13, lineHeight: 1.6 }} className="printable-wo">
              <Typography variant="h6" fontWeight={900} align="center" gutterBottom>
                WORK ORDER – Business Growth Expert (BGE)
              </Typography>
              <Typography align="center" variant="subtitle2" color="text.secondary" gutterBottom>
                PRUDEV II Project – {workOrderPreview.work_order_type_display}
              </Typography>
              <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                {workOrderPreview.work_order_type === 'bge_business_diagnostics' ? (
                  <>Pursuant to the Service Contract dated 12th day of October 2026 between GOPA AFC and <strong>{workOrderPreview.bge_name}</strong>, this Work Order is issued under the PRUDEV II Project framework.</>
                ) : (
                  <>Pursuant to the Service Contract between GOPA Pro GmbH and <strong>{workOrderPreview.bge_name}</strong>, this Work Order is issued under the PRUDEV II Project framework.</>
                )}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Header table */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  ['Work Order Number', workOrderPreview.work_order_number],
                  ['Project Name', workOrderPreview.project_name],
                  ['Issue Date', workOrderPreview.issue_date],
                  ['Start Date', workOrderPreview.start_date || '—'],
                  ['Completion Date', workOrderPreview.end_date || '—'],
                  ['Location', workOrderPreview.location],
                  ['Duration', workOrderPreview.duration],
                  ...(workOrderPreview.work_order_type === 'bge_technical_co_assignment' ? [
                    ['Supported Primary BGE', `${workOrderPreview.supported_bge_name || '—'} ${workOrderPreview.supported_bge_code ? `(${workOrderPreview.supported_bge_code})` : ''}`],
                    ['Technical Specialty Area', workOrderPreview.technical_area || workOrderPreview.bge_top_skills || '—'],
                  ] : []),
                ].map(([label, val]) => (
                  <React.Fragment key={label}>
                    <Grid item xs={4}>
                      <Typography variant="caption" fontWeight={700}>{label}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="caption">{val}</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
              {workOrderPreview.work_order_type === 'bge_technical_co_assignment' && workOrderPreview.target_msmes_detail?.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFFDF5', borderRadius: 1, border: '1px solid #FDE68A' }}>
                  <Typography variant="caption" fontWeight={700} color="#92400E" display="block" sx={{ mb: 0.5 }}>
                    Target MSMEs for Technical Support ({workOrderPreview.target_msmes_detail.length}):
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {workOrderPreview.target_msmes_detail.map(m => `${m.name}${m.sector ? ` (${m.sector})` : ''}`).join(' • ')}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />

              {/* Sections */}
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>1. SCOPE OF SERVICES – Schedule 1</Typography>
              <Typography variant="caption" fontWeight={700}>Objective:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{workOrderPreview.objective}</Typography>

              <Typography fontWeight={700} sx={{ mb: 0.5 }}>2. KEY TASKS</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{workOrderPreview.key_tasks}</Typography>

              {workOrderPreview.deliverables_json?.length > 0 && (
                <>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>3. DELIVERABLES</Typography>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: 12 }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ bgcolor: '#f0f4f8' }}>
                        {['#', 'Deliverable', 'Due Date'].map(h => (
                          <Box component="th" key={h} sx={{ border: '1px solid #ccc', p: 0.75, textAlign: 'left', fontWeight: 700 }}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {workOrderPreview.deliverables_json.map(d => (
                        <Box component="tr" key={d.task_num}>
                          <Box component="td" sx={{ border: '1px solid #ccc', p: 0.75, width: 30 }}>{d.task_num}</Box>
                          <Box component="td" sx={{ border: '1px solid #ccc', p: 0.75 }}>{d.description}</Box>
                          <Box component="td" sx={{ border: '1px solid #ccc', p: 0.75, whiteSpace: 'nowrap' }}>{d.due_date}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              <Typography fontWeight={700} sx={{ mb: 0.5 }}>4. PAYMENT TERMS – Schedule 2</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Rate: <strong>UGX {Number(workOrderPreview.rate_per_day).toLocaleString()} per day</strong> (maximum of {workOrderPreview.max_days} days)
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Transport: Transport costs will be refunded based on public transport rates upon attendance and submission of valid receipts.
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Payment Terms: Paid upon submission and approval of all deliverables listed above, a duly filled and signed timesheet, and an approved invoice.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                BGE must use their official BGE code (<strong>{workOrderPreview.bge_code_display || myBgeCode}</strong>) in all documentation.
              </Typography>

              <Typography fontWeight={700} sx={{ mb: 0.5 }}>5. CONDITIONS</Typography>
              {workOrderPreview.work_order_type === 'bge_business_diagnostics' ? (
                [
                  'BGE must use only the standardized PRUDEV II Diagnostic Tool and submit reports in provided formats.',
                  'All data and business information must be handled confidentially.',
                  'GOPA Pro retains ownership of all outputs.',
                  'GOPA Pro reserves the right to terminate this Work Order in cases of non-performance or breach of contract.',
                ].map((c, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>• {c}</Typography>
                ))
              ) : (
                STANDARD_CONDITIONS.map((c, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>• {c}</Typography>
                ))
              )}

              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={700} sx={{ mb: 1 }}>SIGNATURES</Typography>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Typography variant="caption" fontWeight={700} display="block">
                    {workOrderPreview.work_order_type === 'bge_business_diagnostics' ? 'On behalf of GOPA AFC' : 'On behalf of GOPA Pro GmbH'}
                  </Typography>
                  <Typography variant="caption" display="block">Name: {workOrderPreview.team_leader_name}</Typography>
                  <Typography variant="caption" display="block">Position: {workOrderPreview.team_leader_position}</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 3 }}>Signature: ____________________________</Typography>
                  <Typography variant="caption" display="block">Date: ____________________</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" fontWeight={700} display="block">Business Growth Expert (BGE)</Typography>
                  <Typography variant="caption" display="block">Name: {workOrderPreview.bge_name}</Typography>
                  <Typography variant="caption" display="block">BGE Code: {workOrderPreview.bge_code_display || myBgeCode}</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 3 }}>Signature: ____________________________</Typography>
                  <Typography variant="caption" display="block">
                    Date: {workOrderPreview.bge_signed_date || '____________________'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkOrderPreview(null)}>Close</Button>
            <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print / Save as PDF</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Work Order PDF Review dialog ─────────────────────────────────── */}
      {woReview && (
        <Dialog open onClose={closeReview} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
          <DialogTitle sx={{ pb: 0 }}>
            {woReview.work_order_number}
            <Typography variant="caption" display="block" color="text.secondary">
              {woReview.work_order_type_display} · Issued {woReview.issue_date}
              {woReview.status === 'signed' && ' · Signed ✓'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {!woPdfBlob ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <iframe
                src={woPdfBlob}
                title="Work Order PDF"
                style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReview}>Close</Button>
            <Button variant="contained" startIcon={<Download />} onClick={() => downloadWoPdf(woReview)}>
              Download PDF
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── BGE Payment Receipt Confirmation Dialog ─────────────────────── */}
      <Dialog open={bgePaymentConfirmDialog} onClose={() => setBgePaymentConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" /> Confirm Payment Receipt
        </DialogTitle>
        <DialogContent dividers>
          {bgePaymentConfirmTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" icon={false}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {bgePaymentConfirmType === 'work_order'
                    ? `Work Order: ${bgePaymentConfirmTarget.work_order_number}`
                    : bgePaymentConfirmType === 'group_report'
                    ? `Group Report: ${bgePaymentConfirmTarget.group_name}`
                    : `Visit Report: ${bgePaymentConfirmTarget.msme_name || bgePaymentConfirmTarget.msme?.business_name || 'MSME Visit'}`}
                </Typography>
                {bgePaymentConfirmTarget.rate_per_day && (
                  <Typography variant="body2">
                    Net Amount: <strong>UGX {(bgePaymentConfirmTarget.rate_per_day * bgePaymentConfirmTarget.max_days * 0.94).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                  </Typography>
                )}
                {bgePaymentConfirmTarget.payment_reference && (
                  <Typography variant="body2" color="text.secondary">
                    Submission Batch / Ref: <strong>{bgePaymentConfirmTarget.payment_reference}</strong>
                  </Typography>
                )}
              </Alert>

              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={bgePaymentConfirmForm.payment_method}
                  label="Payment Method"
                  onChange={e => setBgePaymentConfirmForm({ ...bgePaymentConfirmForm, payment_method: e.target.value })}
                >
                  <MenuItem value="Airtel Money">Airtel Money</MenuItem>
                  <MenuItem value="MTN Mobile Money">MTN Mobile Money</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer / EFT</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Mobile Money / Transaction ID (Optional)"
                placeholder="e.g. MM-TXN-123456 or Bank deposit reference"
                value={bgePaymentConfirmForm.reference}
                onChange={e => setBgePaymentConfirmForm({ ...bgePaymentConfirmForm, reference: e.target.value })}
                size="small"
                fullWidth
              />

              <TextField
                label="Confirmation Remarks / Notes (Optional)"
                placeholder="e.g. Received in full on registered Airtel number..."
                value={bgePaymentConfirmForm.notes}
                onChange={e => setBgePaymentConfirmForm({ ...bgePaymentConfirmForm, notes: e.target.value })}
                multiline
                rows={2}
                size="small"
                fullWidth
              />

              <Typography variant="caption" color="text.secondary">
                Upon confirming, the Admin Team will receive an automated notification email confirming that funds have safely reached you.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBgePaymentConfirmDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={bgePaymentConfirming}
            onClick={handleBgePaymentConfirm}
            startIcon={bgePaymentConfirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
          >
            {bgePaymentConfirming ? 'Confirming…' : 'Confirm Receipt of Funds'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>

      {/* ── Help dialog ──────────────────────────────────────────────────── */}
      {helpDialog && (
        <Dialog open onClose={() => setHelpDialog(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { height: { xs: '90dvh', md: '80vh' }, display: 'flex', flexDirection: 'column' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
            How to use the BGE Portal
            <IconButton onClick={() => setHelpDialog(false)}><Close /></IconButton>
          </DialogTitle>
          <Box sx={{ px: 3, pb: 0 }}>
            <Tabs value={helpSection} onChange={(_, v) => setHelpSection(v)} variant="scrollable" scrollButtons="auto">
              <Tab label="My MSMEs" />
              <Tab label="My Groups" />
              <Tab label="Work Orders" />
              <Tab label="Training" />
              <Tab label="My Reports" />
            </Tabs>
          </Box>
          <DialogContent sx={{ pt: 2 }}>
            {[
              {
                title: 'My MSMEs',
                steps: [
                  { n: 1, text: 'Your directly assigned MSMEs appear as cards. Each card shows the business name, sector, last support date, and total reports filed.' },
                  { n: 2, text: 'Click any MSME card to open its full profile — including contact details, assignment objectives, group membership, and growth history.' },
                  { n: 3, text: 'Click "New Report" (top-right) to write a visit report. Fill in visit type, date, findings, and recommendations, then save as draft or submit.' },
                  { n: 4, text: 'Submitted reports are reviewed by the programme administrator. Approved reports are final.' },
                ],
              },
              {
                title: 'My Groups',
                steps: [
                  { n: 1, text: 'Groups are clusters of MSMEs supported together in collective sessions.' },
                  { n: 2, text: 'Expand a group card to see all member MSMEs and recent group support reports.' },
                  { n: 3, text: 'Click "New Group Report" to record a group session — add the date, objectives, attending MSMEs, and findings.' },
                  { n: 4, text: 'As team lead you can see contributions from other BGEs in your group.' },
                ],
              },
              {
                title: 'Work Orders',
                steps: [
                  { n: 1, text: 'Work Orders are contracts issued by the programme administrator that define your assignment, rate, and duration.' },
                  { n: 2, text: 'Upload your signature once using the "My Signature" panel — it will be embedded in every signed document.' },
                  { n: 3, text: 'When a work order is issued, use the eye icon to review the full PDF before signing.' },
                  { n: 4, text: 'Click the green "Sign" button to digitally accept the work order. The date is recorded automatically and the signed PDF is stored.' },
                  { n: 5, text: 'You can download or print any work order using the icons on the right of each card.' },
                ],
              },
              {
                title: 'Training',
                steps: [
                  { n: 1, text: 'Sessions you are assigned to as lead facilitator or mentor appear here as cards.' },
                  { n: 2, text: 'Lead sessions show a blue "Lead" badge; mentor sessions show a purple "Mentor" badge.' },
                  { n: 3, text: 'Click "Write Report" to open the report form for that session.' },
                  { n: 4, text: 'Use "Save Draft" to continue later, or "Submit Report" when complete.' },
                ],
              },
              {
                title: 'My Reports',
                steps: [
                  { n: 1, text: 'All your individual and group support reports are listed here with their status (Draft / Submitted / Approved).' },
                  { n: 2, text: 'Use the date and type filters at the top to narrow down the list.' },
                  { n: 3, text: 'Click any report to view its full content and the programme manager\'s feedback.' },
                  { n: 4, text: 'Draft reports can be edited and resubmitted. Approved reports are locked.' },
                ],
              },
            ][helpSection].steps.map(step => (
              <Box key={step.n} sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
                <Box sx={{
                  minWidth: 28, height: 28, borderRadius: '50%', bgcolor: BRAND.primaryMain, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, flexShrink: 0,
                }}>
                  {step.n}
                </Box>
                <Typography variant="body2" sx={{ pt: 0.4 }}>{step.text}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3 }}>
            <Button disabled={helpSection === 0} onClick={() => setHelpSection(s => s - 1)}>← Previous</Button>
            <Box sx={{ flex: 1 }} />
            <Button disabled={helpSection === 4} variant="contained" onClick={() => setHelpSection(s => s + 1)}>Next →</Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
}
